import { AppMonitoringHealthMetricThreshold } from '@/actions/appSettings';
import { RunwayUserAction } from './helpers/aclHelpers';
import { allCases, Nullable, StringKeyedDictionary } from './typescriptHelpers';
import { WorkflowRunStepRunGraph } from '@/types/workflowRunStepRunGraph';
import { StepRun } from '@/types/stepRun';
import { StepConfig } from '@/types/stepConfig';
import { WorkflowStepGraph } from '@/types/workflow';
import { AllNotificationTypes } from '@/helpers/displayHelpers';
import { Destination } from './types/destinations';
import { WorkflowOverview } from './types/workflowOverview';
import { TimelineEventsHistoryPaginatedResponse } from './actions/timelineEventsHistory';
import { TimezonePreferenceEnum } from '@/components/userSettings/EditTimezoneSettingsModal';

export interface DashboardState {
    organizations: Organization[];
    user: User;
}

export interface Auth {
    token: string;
}

export enum ServerType {
    SingleInstance = 'SingleOrg',
    Stateless = 'Stateless',
}

export interface AppConfig {
    version: string;
    isSSOSamlRequired: boolean;
    serverType: ServerType;
}

export enum ThirdPartyAuthError {
    SsoEnabled = 'ssoEnabled',
    Unauthorized = 'unauthorized',
    General = 'general',
}

export enum BuildDistroEntityError {
    BucketNotFound = 'BUCKET_NOT_FOUND',
    NotABucketMember = 'NOT_A_BUCKET_MEMBER',
    BuildNotFound = 'BUILD_NOT_FOUND',
    BuildNotSharedWithYou = 'BUILD_NOT_SHARED_WITH_YOU',
}

export type RunwayResponse<T = any> = {
    success: boolean;
    errorMessage: string;
    data: T;
};

export enum RunwayResponseType {
    Error = 'error',
    Success = 'success',
    Warning = 'warning',
    Info = 'info',
}

export type NonErrorRunwayResponseTypes =
    | RunwayResponseType.Success
    | RunwayResponseType.Warning
    | RunwayResponseType.Info;

export interface RunwayResponseV2<T = unknown> {
    type: RunwayResponseType;
    message?: string;
    data?: T;
}

export enum UserPermissions {
    Admin = 'admin',
    RunwayAdmin = 'runwayAdmin',
    ITAdmin = 'itAdmin',
    RunwayAdminReadOnly = 'runwayAdminReadOnly',
}

interface CommsAvatars {
    image32: string;
    image48: string;
}

export interface VCSUserData {
    username: string;
    name: string;
    avatar: string;
}

export enum BuildDistroFilterByVCSUsersBehaviorType {
    Include = 'include',
    Exclude = 'exclude',
}

export interface CommsMention {
    id?: string;
    displayName?: string;
    email: string;
}

export interface User {
    id: string;
    uuid: string;
    email: string;
    firstName?: string;
    lastName?: string;
    createdAt: string;
    permissions: UserPermissions[];
    needsNewPassword: boolean;
    groups: UserGroupId[];
    commsAvatars?: CommsAvatars;
    commsUserId?: string;
    disallowedActions?: RunwayUserAction[];
    appIds: string[];
    analytics?: UserAnalytics;
    vcsUserDataByProvider?: Record<IntegrationProviderId, VCSUserData>;
    devices?: Device[];
    emailNotificationSettings?: UserEmailNotificationSettings;
    shouldShowSimplifiedBuildDistributionUI?: boolean;
    timezonePreference?: TimezonePreferenceEnum;
}

export interface UserAnalytics {
    isMql: boolean;
    signupIntent?: string;
    userOrderInOrg: number;
    originalSource: string;
    originalSourceDrilldown1: string;
    originalSourceDrilldown2: string;
    latestSource: string;
    latestSourceDrilldown1: string;
    latestSourceDrilldown2: string;
    leadSource: string;
    firstReferringSite: string;
    lastReferringSite: string;
}

export enum ReleaseStatus {
    Active = 'active',
    Completed = 'completed',
    Skipped = 'skipped',
    Deleted = 'deleted',
    Predicted = 'predicted',
}

export enum ReleaseStepType {
    Kickoff = 'kickoff',
    FeatureReadiness = 'featureReadiness',
    ReleaseCandidate = 'releaseCandidate',
    RegressionTesting = 'regressionTesting',
    BetaTesting = 'betaTesting',
    Metadata = 'metadata',
    Screenshots = 'screenshots',
    Approvals = 'approvals',
    Submission = 'submission',
    StoreReview = 'storeReview',
    Takeoff = 'takeoff',
    CIDistribution = 'ciDistribution',
    VCSDistribution = 'vcsDistribution',
    RollbackBuild = 'rollbackBuild',
    Translations = 'translations',
    Rollout = 'rollout',
    ReleaseCycle = 'releaseCycle',
}

export const AllReleaseStepTypes = allCases(ReleaseStepType);

export enum ReleaseNav {
    Overview = 'overview',
    Rollouts = 'rollouts',
    FeatureFlags = 'feature-flags',
    ReleaseCalendar = 'release-calendar',
}

export enum LifecycleStep {
    Kickoff = 'kickoff',
    Submission = 'submission',
    Release = 'release',
}

export const AllLifecycleSteps = allCases(LifecycleStep);

export enum ReleaseStepStatus {
    Ready = 'ready',
    Pending = 'pending',
    PendingChecklistOnly = 'pendingChecklist',
    Blocked = 'blocked',
    Inactive = 'inactive',
    Skipped = 'skipped',
}

export interface BaseBuildArtifact {
    fileName: string;
    size: number; // in bytes
    downloadUrl: string;
}

export interface BuildArtifact extends BaseBuildArtifact {
    version: string;
    workflowRunId: string;
    artifactId: string;
    appStoreInfos: Record<IntegrationInstallationId, AppStoreArtifactUploadInfo>;
    dsymUploadInfo: DsymUploadInfo;
    binaryBuildDetails?: BinaryBuildDetails;
}

export interface AppStoreArtifactUploadInfo {
    uploadStatus: AppStoreUploadArtifactStatus;
    errorMessage?: string;
    uploadRetryCount: number;
    createdAt: string;
    uploadCompletedAt?: string;
    destinationInstallationId: IntegrationInstallationId;
    ascTransactionId?: string;
    trackName?: string;
    createNewTrackReleaseWithPatternedName?: string;
    rateLimitedUntil?: string;
}

export enum AppStoreUploadArtifactStatus {
    AppStoreUploadNotStarted = 'notStarted',
    AppStoreUploadPending = 'pending',
    AppStoreUploadRetryRequested = 'retryRequested',
    AppStoreUploadInProgress = 'inProgress',
    AppStoreUploadRetrying = 'retrying',
    AppStoreUploadSuccess = 'success',
    AppStoreUploadError = 'error',
    AppStoreUploadBlocked = 'blocked',
}

export interface DsymUploadInfo {
    uploadStatus: DsymUploadArtifactStatus;
    errorMessage?: string;
    uploadRetryCount: number;
    createdAt: string;
    uploadCompletedAt?: string;
    projectName: string;
    rateLimitedUntil?: string;
}

export enum DsymUploadArtifactStatus {
    DsymUploadNotStarted = 'notStarted',
    DsymUploadPending = 'pending',
    DsymUploadInProgress = 'inProgress',
    DsymUploadRetrying = 'retrying',
    DsymUploadSuccess = 'success',
    DsymUploadError = 'error',
}

export enum BuildArtifactType {
    IOSDevelopment = 'BuildArtifactIOSDevelopment',
    IOSEnterprise = 'BuildArtifactIOSEnterprise',
    IOSAppStore = 'BuildArtifactIOSAppStore',
    IOSAdHoc = 'BuildArtifactIOSAdHoc',
    TvOSDevelopment = 'BuildArtifactTVOSDevelopment',
    TvOSEnterprise = 'BuildArtifactTVOSEnterprise',
    TvOSAppStore = 'BuildArtifactTVOSAppStore',
    TvOSAdHoc = 'BuildArtifactTVOSAdHoc',
    AndroidDebuggableSigned = 'BuildArtifactAndroidDebuggableSigned',
    AndroidDebuggableUnsigned = 'BuildArtifactAndroidDebuggableUnsigned',
    AndroidReleaseSigned = 'BuildArtifactAndroidReleaseSigned',
    AndroidReleaseUnsigned = 'BuildArtifactAndroidReleaseUnsigned',
    AndroidAAB = 'BuildArtifactAndroidAAB',
    AndroidAABInstallable = 'BuildArtifactAndroidAABInstallable',
}

export interface BinaryBuildDetails {
    artifactType: BuildArtifactType;
    bundleName: string;
    versionString: string;
    appName?: string;
    provisioningProfile?: ProvisioningProfile;
    buildIdentifier: string; // version code or build number
}

export interface BuildInfo {
    integrationId?: IntegrationType;
    buildHash: string;
    buildIdentifier?: string;
    buildUrl?: string;
    buildStartedAt?: string;
    buildFinishedAt?: string;
    buildStatus: BuildStatus;
    providerBuildStatusString: string;
    workflowName?: string;
    commit: string;
    commitMessage: string;
    commitAuthor: string;
    commitUrl: string;
    artifacts?: BuildArtifact[];
    branchName?: string;
}

export enum AppStoreBuildType {
    IosBinary = 'ios-binary',
    AndroidAPK = 'android-apk',
    AndroidAAB = 'android-aab',
}

export interface AppStoreBuildInfo {
    id: string;
    buildNumber: string;
    uploadedAt: string;
    integrationId: IntegrationType;
    type: AppStoreBuildType;
    isProcessed: boolean;
}

export interface AppStoreAvailableBuild extends AppStoreBuild {
    isRecentBuild: boolean;
    isCurrentlySelected: boolean;
    isNewestAppStoreBuild: boolean; // newest w.r.t other builds in the App store
    selectedBuildReason?: AppStoreSelectedBuildReason;
    autoSelectedFromTrack?: string;
    tracks?: string[];
}

// Custom messages

export enum CustomMessageConfigKey {
    BumpVersionCommitMessage = 'bumpVersionCommitMessage',
    BumpVersionPRTitle = 'bumpVersionPRTitle',
    BumpVersionPRBody = 'bumpVersionPRBody',
    PromoteCodePRTitle = 'promoteCodePRTitle',
    PromoteCodePRBody = 'promoteCodePRBody',
    BackMergeBranchPRBody = 'backMergeBranchPRBody',
    BackMergeBranchPRTitle = 'backMergeBranchPRTitle',
    BackMergePRCommitPRBody = 'backMergePRCommitPRBody',
    BackMergePRCommitPRTitle = 'backMergePRCommitPRTitle',
    VCSReleaseTitle = 'vcsReleaseTitle',
    VCSReleaseBody = 'vcsReleaseBody',
    TagMessage = 'tagMessage',
    CherryPickPRTitle = 'cherryPickPRTitle',
    CherryPickPRBody = 'cherryPickPRBody',
    CherryPickCommitMessage = 'cherryPickCommitMessage',
    ReleaseSummaryMessage = 'releaseSummaryMessage',
}

export enum PatternToken {
    Version = '{version}', // three-segment semver
    VersionConcise = '{versionConcise}', // allows one- or two-segment also e.g. 2 or 2.1
    VersionMajorMinor = '{versionMajorMinor}', // forces two-segment only e.g. 2.0 or 2.1 (even if point digit is non-zero; used for dynamic point digit)
    VersionMajor = '{versionMajor}', // forces one segment only e.g. 2 for versions 2.0.0 or 2.1.0 (even if minor or point digits are non-zero)
    VersionSuffix = '{versionSuffix}', // used in conjunction with {version} to add a fourth digit/segment e.g. for OTA
    ReleaseName = '{releaseName}', // human readable release name
    BuildAppStore = '{appStoreBuildNumber}',
    BuildCI = '{ciBuildNumber}',
    ReleasePilot = '{releasePilot}',
    ReleaseBranchName = '{releaseBranchName}',
    WorkingBranchName = '{workingBranchName}',
    Wildcard = '{*}',
    CommitMessage = '{commitMessage}',
    CommitShortHash = '{commitShortHash}',
    TargetBranchName = '{targetBranchName}',
    WorkItems = '{workItems}',
    PRTitle = '{prTitle}', // DEPRECATED
    PRBody = '{prBody}', // DEPRECATED
    PRTitleOrCommitMessage = '{prTitleOrCommitMessage}',
    PRBodyOrCommitHash = '{prBodyOrCommitHash}',
    PRNumberOrCommitHash = '{prNumberOrCommitHash}',
    AppName = '{appName}',
    AppPlatform = '{appPlatform}',
    WorkItemsCount = '{workItemsCount}',
    IssuesCount = '{issuesCount}',
    CommitsCount = '{commitsCount}',
    ContributorsCount = '{contributorsCount}',
    LinesOfCodeCount = '{linesOfCodeCount}',
    ReleaseNotes = '{releaseNotes}',
    ReleaseNotesTerm = `{releaseNotesTerm}`,
    ReleaseDuration = '{releaseDuration}',
    ReleasedAt = '{releasedAt}',
    ReleaseWorkItemsLink = '{releaseWorkItemsLink}',
    ReleaseAppStoreLink = '{releaseAppStoreLink}',
    ReleaseVCSReleaseLink = '{releaseVCSReleaseLink}',
    CIBuildsCount = '{ciBuildsCount}',
}

export interface CustomMessageTemplateData {
    customStringType: CustomMessageConfigKey | '';
    customStringValue?: string;
    maxCharCount: number;
    availableTokens: PatternToken[];
    defaultValue: string;
}

export interface CustomMessagesConfig {
    customMessageTemplatesData: Record<CustomMessageConfigKey, CustomMessageTemplateData>;
}

export const AllPatternTokens = allCases(PatternToken);

export const RequireAtLeastOneOfTokensForTag = [
    PatternToken.Version,
    PatternToken.VersionConcise,
    PatternToken.VersionMajorMinor,
];

export const DisallowedTokensForTag = [
    PatternToken.Wildcard,
    PatternToken.CommitMessage,
    PatternToken.TargetBranchName,
    PatternToken.WorkItems,
    PatternToken.PRTitle,
    PatternToken.PRBody,
    PatternToken.PRTitleOrCommitMessage,
    PatternToken.PRBodyOrCommitHash,
    PatternToken.PRNumberOrCommitHash,
    PatternToken.AppName,
    PatternToken.AppPlatform,
    PatternToken.WorkItemsCount,
    PatternToken.IssuesCount,
    PatternToken.CommitsCount,
    PatternToken.ContributorsCount,
    PatternToken.LinesOfCodeCount,
    PatternToken.ReleaseNotes,
    PatternToken.ReleaseDuration,
    PatternToken.ReleasedAt,
    PatternToken.ReleaseWorkItemsLink,
    PatternToken.ReleaseAppStoreLink,
    PatternToken.ReleaseVCSReleaseLink,
];

export const RequireAtLeastOneOfTokensForFA = [
    PatternToken.Version,
    PatternToken.VersionConcise,
    PatternToken.VersionMajorMinor,
    PatternToken.ReleaseName,
];

export const DisallowedTokensForFA = [
    PatternToken.BuildCI,
    PatternToken.BuildAppStore,
    PatternToken.Wildcard,
    PatternToken.CommitMessage,
    PatternToken.CommitShortHash,
    PatternToken.TargetBranchName,
    PatternToken.WorkItems,
    PatternToken.PRTitle,
    PatternToken.PRBody,
    PatternToken.PRTitleOrCommitMessage,
    PatternToken.PRBodyOrCommitHash,
    PatternToken.PRNumberOrCommitHash,
    PatternToken.AppName,
    PatternToken.AppPlatform,
    PatternToken.WorkItemsCount,
    PatternToken.IssuesCount,
    PatternToken.CommitsCount,
    PatternToken.ContributorsCount,
    PatternToken.LinesOfCodeCount,
    PatternToken.ReleaseNotes,
    PatternToken.ReleaseDuration,
    PatternToken.ReleasedAt,
    PatternToken.ReleaseWorkItemsLink,
    PatternToken.ReleaseAppStoreLink,
    PatternToken.ReleaseVCSReleaseLink,
    PatternToken.CIBuildsCount,
];

export const MetadataAvailableTokens = [
    PatternToken.AppName,
    PatternToken.AppPlatform,
    PatternToken.Version,
    PatternToken.VersionConcise,
    PatternToken.VersionMajorMinor,
    PatternToken.ReleaseName,
    PatternToken.BuildAppStore,
    PatternToken.BuildCI,
    PatternToken.CIBuildsCount,
    PatternToken.ReleasePilot,
    PatternToken.ReleaseBranchName,
    PatternToken.WorkingBranchName,
    PatternToken.WorkItems,
    PatternToken.WorkItemsCount,
    PatternToken.IssuesCount,
    PatternToken.CommitsCount,
    PatternToken.ContributorsCount,
    PatternToken.LinesOfCodeCount,
];

export const MetadataDisallowedTokens = AllPatternTokens.filter((token) => !MetadataAvailableTokens.includes(token));

export const requireAtLeastOneOfTokensForBranch: (isVersioned: boolean) => PatternToken[] = (isVersioned) => {
    return isVersioned ?
            [
                PatternToken.Version,
                PatternToken.VersionConcise,
                PatternToken.VersionMajorMinor,
                PatternToken.VersionMajor,
                PatternToken.ReleaseName,
            ]
        :   [];
};

export const DisallowedTokensForBranch = [
    PatternToken.BuildCI,
    PatternToken.BuildAppStore,
    PatternToken.WorkingBranchName,
    PatternToken.Wildcard,
    PatternToken.CommitMessage,
    PatternToken.CommitShortHash,
    PatternToken.TargetBranchName,
    PatternToken.WorkItems,
    PatternToken.PRTitle,
    PatternToken.PRBody,
    PatternToken.PRTitleOrCommitMessage,
    PatternToken.PRBodyOrCommitHash,
    PatternToken.PRNumberOrCommitHash,
    PatternToken.AppName,
    PatternToken.AppPlatform,
    PatternToken.WorkItemsCount,
    PatternToken.IssuesCount,
    PatternToken.CommitsCount,
    PatternToken.ContributorsCount,
    PatternToken.LinesOfCodeCount,
    PatternToken.ReleaseNotes,
    PatternToken.ReleaseDuration,
    PatternToken.ReleasedAt,
    PatternToken.ReleaseWorkItemsLink,
    PatternToken.ReleaseAppStoreLink,
    PatternToken.ReleaseVCSReleaseLink,
    PatternToken.CIBuildsCount,
];

export const RequireAtLeastOneOfTokensForAndroidTrackReleaseName = [
    PatternToken.Version,
    PatternToken.VersionConcise,
    PatternToken.VersionMajorMinor,
];

export const disallowedTokensForForAndroidTrackReleaseName: () => PatternToken[] = () => {
    return AllPatternTokens.filter((token) => {
        switch (token) {
            case PatternToken.Version:
            case PatternToken.VersionConcise:
            case PatternToken.VersionMajorMinor:
            case PatternToken.BuildAppStore:
            case PatternToken.BuildCI:
                return false;
            default:
                return true;
        }
    });
};

export enum AppStoreBuildProcessingState {
    Processing = 'PROCESSING',
    Failed = 'FAILED',
    Invalid = 'INVALID',
    Valid = 'VALID',
}

export interface StabilityInfo {
    version: string;
    buildIdentifier?: string;
    appStoreAppIdentifier?: string;
    integrationId: IntegrationType;
    sessionStability?: number;
    userStability?: number;
    sessionAdoption?: number;
    userAdoption?: number; // (we don't surface this, in favor of sessionAdoption)
    adoptionPeriodString?: string; // usually nil (defaults to "24H"), overridden for firebase ("24-48H")
    url?: string;
    environmentName?: string;

    topStabilityIssues: StabilityIssue[];
    topNewStabilityIssues: StabilityIssue[];
    topStabilityIssuesNonFatal: StabilityIssue[];
    topNewStabilityIssuesNonFatal: StabilityIssue[];
}

export interface StabilityIssue {
    issueID: string;
    crashShortDescription: string;
    crashLongDescription: string;
    occurrencesCountInRelease: number;
    totalOccurrencesCount: number;
    isNewInRelease: boolean;
    isNonFatal: boolean;
    url: string;
    associatedCommits: CommitItem[];
    blameFile?: string;
    blameLine?: string;
    stackTrace?: StackTraceFrame[];
}

export interface StackTraceFrame {
    filename: string;
    package: string;
    function: string;
}

export enum ReleaseStepBlockingConfig {
    Blocking = 'Blocking',
    NonBlocking = 'NonBlocking',
}

// This will turn to StepConfig on post-workflows, the data structure will also change, see https://github.com/runway-org/runway/pull/6914/files. In the workflows world this is actually contained in the Workflow (currently App) level and thus it should only contain configuration and structure data for steps, data that doesn't depend on a specific StepRun. This means status and description (which is dynamic) should not be contained in StepConfig. However the WorkflowRun (currently Release) should still contain a summary of all the step's statuses and descriptions so we can display them in the release timeline.
export interface ReleaseStepConfig {
    type: ReleaseStepType;
    groupIndex: number;
    orderIndex: number;
    description: string; // how the step is shown in the Timeline
    blockingConfig: ReleaseStepBlockingConfig;
}

export interface ReleaseStep extends ReleaseStepConfig {
    id: string;
    status: ReleaseStepStatus;
    statusReasonString: string;
    checklistItems: ChecklistItem[];
    automationRuns: AutomationRun[];

    data: any;
}

export enum AppStoreReviewSubmissionState {
    ReadyForReview = 'READY_FOR_REVIEW',
    WaitingForReview = 'WAITING_FOR_REVIEW',
    InReview = 'IN_REVIEW',
    UnresolvedIssues = 'UNRESOLVED_ISSUES',
    Canceling = 'CANCELING',
    Completing = 'COMPLETING',
    Complete = 'COMPLETE',
    Blocked = 'BLOCKED',
}

export enum AppStoreReviewSubmissionItemType {
    CustomProductPage = 'appCustomProductPageVersions',
    AppEvent = 'appEvents',
    AppBuildVersion = 'appStoreVersions',
    AppStoreVersionExperiment = 'appStoreVersionExperiments',
}

export enum AppleAppStoreState {
    ReadyForSale = 'READY_FOR_SALE',
    PreorderReadyForSale = 'PREORDER_READY_FOR_SALE',
    ProcessingForAppStore = 'PROCESSING_FOR_APP_STORE',
    PendingDeveloperRelease = 'PENDING_DEVELOPER_RELEASE',
    PendingAppleRelease = 'PENDING_APPLE_RELEASE',
    InReview = 'IN_REVIEW',
    WaitingForReview = 'WAITING_FOR_REVIEW',
    DeveloperRejected = 'DEVELOPER_REJECTED',
    Rejected = 'REJECTED',
    Accepted = 'ACCEPTED',
    PrepareForSubmission = 'PREPARE_FOR_SUBMISSION',
    MetadataRejected = 'METADATA_REJECTED',
    InvalidBinary = 'INVALID_BINARY',
    ReplacedWithNewVersion = 'REPLACED_WITH_NEW_VERSION', // previous versions transition to this state when newer version is released
    WaitingForExportCompliance = 'WAITING_FOR_EXPORT_COMPLIANCE',
    PendingContract = 'PENDING_CONTRACT',
    RemovedFromSale = 'REMOVED_FROM_SALE',
    DeveloperRemovedFromSale = 'DEVELOPER_REMOVED_FROM_SALE',
    ReadyForReview = 'READY_FOR_REVIEW',
}

export enum AppleExternalBetaBuildState {
    Processing = 'PROCESSING',
    ProcessingException = 'PROCESSING_EXCEPTION',
    MissingExportCompliance = 'MISSING_EXPORT_COMPLIANCE',
    ReadyForBetaTesting = 'READY_FOR_BETA_TESTING',
    InBetaTesting = 'IN_BETA_TESTING',
    Expired = 'EXPIRED',
    ReadyForBetaSubmission = 'READY_FOR_BETA_SUBMISSION',
    InExportCompliance = 'IN_EXPORT_COMPLIANCE_REVIEW',
    WaitingForBetaReview = 'WAITING_FOR_BETA_REVIEW',
    InBetaReview = 'IN_BETA_REVIEW',
    BetaRejected = 'BETA_REJECTED',
    BetaApproved = 'BETA_APPROVED',
}

export enum AppleInternalBetaBuildState {
    Processing = 'PROCESSING',
    ProcessingException = 'PROCESSING_EXCEPTION',
    MissingExportCompliance = 'MISSING_EXPORT_COMPLIANCE',
    ReadyForBetaTesting = 'READY_FOR_BETA_TESTING',
    InBetaTesting = 'IN_BETA_TESTING',
    InExportCompliance = 'IN_EXPORT_COMPLIANCE_REVIEW',
    Expired = 'EXPIRED',
}

export enum GooglePlayStoreState {
    StatusUnspecified = 'statusUnspecified', // Unspecified status
    Draft = 'draft', //The release's APKs are not being served to users.
    InProgress = 'inProgress', // The release's APKs are being served to a fraction of users, determined by 'userFraction'.
    Halted = 'halted', // The release's APKs will no longer be served to users. Users who already have these APKs are unaffected.
    Completed = 'completed', //The release will have no further changes. Its APKs are being served to all users, unless they are eligible to APKs of a more recent release.
}

export enum HuaweiPhasedReleaseState {
    Suspended = 'SUSPEND',
    Released = 'RELEASE',
    Canceled = 'CANCEL',
    Draft = 'DRAFT',
}

export enum HuaweiAppStoreState {
    Released = 0,
    ReleaseRejected = 1,
    Removed = 2,
    Releasing = 3,
    Reviewing = 4,
    Updating = 5,
    RemovalRequested = 6,
    Draft = 7,
    UpdateRejected = 8,
    RemovalRejected = 9,
    RemovedByDeveloper = 10,
    ReleaseCanceled = 11,
}

export enum AmazonAppStoreState {
    Completed = 'COMMITTED',
    InProgress = 'IN_PROGRESS',
    Submitted = 'SUBMITTED',
    Review = 'REVIEW',
    Live = 'LIVE',
    ReadyToSubmit = 'READY_TO_SUBMIT',
    NotReadyToSubmit = 'NOT_READY_TO_SUBMIT',
}

export enum SamsungAppStoreState {
    Registering = 'REGISTERING',
    ForSale = 'FOR_SALE',
    ReadyForChange = 'READY_FOR_CHANGE',
    ReadyToPreExamination = 'READY_TO_PREEXAMINATION',
    UnderContentReview = 'UNDER_CONTENT_REVIEW',
    Canceled = 'CANCELED',
}

export enum PullRequestStatus {
    Open = 'open',
    Closed = 'closed',
    Merged = 'merged',
    Unknown = 'unknown',
}

export enum PullRequestType {
    BumpVersion = 'BumpVersion',
    PromoteCode = 'PromoteCode',
    BackmergeReleaseBranch = 'BackmergeReleaseBranch',
    BackmergeReleasePRCommit = 'BackmergeReleasePRCommit',
    CherryPickWorkingBranch = 'CherryPickWorkingBranch',
    HotfixCherryPick = 'HotfixCherryPick',
    ManualConflictResolution = 'ManualConflictResolution',
    TranslationsUpdate = 'TranslationsUpdate',
}

export interface PullRequest {
    id: string;
    url: string;
    number: number;
    title: string;
    status: PullRequestStatus;
    pullRequestType: PullRequestType;
    owner: string;
    baseBranch: string;
    filesChanged?: string[];
}

export enum KickoffActionStatus {
    Detected = 'detected',
    NotDetected = 'notDetected',
    Incomplete = 'incomplete',
}

// Kickoff
export interface ReleaseStepKickoffData {
    releaseBranch: string;
    doesReleaseBranchExist: boolean;
    didRunwayCreateReleaseBranch: boolean;
    isStaticReleaseBranch: boolean;
    resolvedFeatureAffiliations: ResolvedFeatureAffiliation[];
    previousTaggedReleaseTag?: string;
    previousTaggedReleaseTagVersionsBehind: number;
    previousReleaseVersion?: string;
    previousReleaseTag?: string;
    releaseBranchUrl?: string;
    releaseBranchDiffUrl?: string;
    workflowBranchDiffUrl?: string;
    codePromotionStatus?: KickoffActionStatus;
    codePromotionPullRequest?: PullRequest;
    canPerformCodePromotion: boolean;
    hasPerformedAnyCodePromotion: boolean;
    workingBranchCodeVersionData?: CodeVersionData;
    releaseBranchCodeVersionData?: CodeVersionData;
    hotfixBranchingBase?: string; // hotfixes branch of prev release tag (if possible)

    workingBranch: Nullable<string>;
    releasePlanningSummary: Nullable<string>;
    canBeSubmitted: Nullable<boolean>;
}

export interface CodeVersionData {
    branchName: string;
    canPerformBumpVersion: boolean;
    currentCodeVersion?: string;
    targetCodeVersion: string;
    bumpVersionPullRequest?: PullRequest;
    bumpVersionStatus: KickoffActionStatus;
    errorMessage?: string;
}

export interface ReleaseStepKickoff extends ReleaseStep {
    type: ReleaseStepType.Kickoff;
    data: ReleaseStepKickoffData;
}

export enum FeatureItemSortBy {
    CodeUpdated = 'codeUpdatedAt',
    TicketUpdatedAt = 'ticketUpdatedAt',
}

// CI
export enum BuildStatus {
    InProgress = 'inProgress',
    Stopped = 'stopped',
    Success = 'success',
    Failure = 'failure',
}

export enum CIBuildSelectionReason {
    AutoSelection = 'autoRunwaySelection', // latest build selected automatically by Runway
    ManualSelection = 'manualRunwaySelection', // build selected explicitly by user in Runway
}

export interface ReleaseCandidateBuildInfo extends CIBuildInfo {
    isSelectedBuild: boolean;
    selectedReason?: CIBuildSelectionReason;
}

export interface ReleaseStepReleaseCandidateData {
    workflowData?: WorkflowAndBranchData;
    builds?: ReleaseCandidateBuildInfo[];
    ciFailureApproved?: boolean;
    ciOverrideApprovedBy?: string;
}

export interface RollbackBuildInfo {
    status: BuildStatus;
    startedAt: string;
    finishedAt?: string;
    artifacts: BuildArtifact[];
    associatedAppStoreBuild?: AppStoreBuildInfo;
    testerNotes?: LocalizedBetaTestingNotes;
}

export interface RollbackSourceVersionInfo {
    version: string;
    completedReleaseSummary: CompletedReleaseSummary;
}

export enum ResignSequenceProgressState {
    NotStarted = 'NotStarted',
    InProgress = 'InProgress',
    Completed = 'Complete',
    Failed = 'Failed',
}

export interface ReleaseStepRollbackBuildData {
    sourceVersion?: RollbackSourceVersionInfo;
    subtext?: string;
    resignSequenceState: ResignSequenceProgressState;
    betaGroups: BetaTestingGroup[];
    betaTesters: BetaTester[];
    betaBuild?: BetaBuild;
    canBeSubmitted: boolean;
    strategy: RollbackResigningStrategy;

    // For Runway signed rollbacks
    build?: RollbackBuildInfo;

    // For CI signed rollbacks
    ciBuild?: ReleaseCandidateBuildInfo;
    workflowData?: WorkflowAndBranchData;
}

export interface ReleaseStepReleaseCandidate extends ReleaseStep {
    type: ReleaseStepType.ReleaseCandidate;
    data: ReleaseStepReleaseCandidateData;
}

export interface ReleaseStepRollbackBuild extends ReleaseStep {
    type: ReleaseStepType.RollbackBuild;
    data: ReleaseStepRollbackBuildData;
}

// Feature readiness
export enum FeatureItemCodeStatus {
    OpenPR = 'openPR',
    Unmerged = 'unmerged',
    Merged = 'merged',
    CherryPickPending = 'cherryPickPending',
    CherryPickOpen = 'cherryPickOpen',
    CherryPickFailed = 'cherryPickFailed',
    CherryPickMerged = 'cherryPickMerged',
    CherryPickClosed = 'cherryPickClosed',
    CherryPickSkipped = 'cherryPickSkipped',
    CherryPickManuallyResolved = 'cherryPickManuallyResolved',
    CherryPickQueued = 'cherryPickQueued',
}

const enumMapping = {
    openPR: FeatureItemCodeStatus.OpenPR,
    unmerged: FeatureItemCodeStatus.Unmerged,
    merged: FeatureItemCodeStatus.Merged,
    cherryPickOpen: FeatureItemCodeStatus.CherryPickOpen,
    cherryPickOpenAutoMergeOn: FeatureItemCodeStatus.CherryPickOpen,
    cherryPickOpenAutoMergeOff: FeatureItemCodeStatus.CherryPickOpen,
    cherryPickOpenCumulativeStyle: FeatureItemCodeStatus.CherryPickOpen,
    cherryPickPending: FeatureItemCodeStatus.CherryPickPending,
    cherryPickFailed: FeatureItemCodeStatus.CherryPickFailed,
    cherryPickMergeFailed: FeatureItemCodeStatus.CherryPickFailed,
    cherryPickFailedWithOpenPR: FeatureItemCodeStatus.CherryPickFailed,
    cherryPickFailedWithConflict: FeatureItemCodeStatus.CherryPickFailed,
    cherryPickFailedWithConflictWithOpenPR: FeatureItemCodeStatus.CherryPickFailed,
    cherryPickMerged: FeatureItemCodeStatus.CherryPickMerged,
    cherryPickMergedManually: FeatureItemCodeStatus.CherryPickMerged,
    cherryPickMergedByRunway: FeatureItemCodeStatus.CherryPickMerged,
    cherryPickClosed: FeatureItemCodeStatus.CherryPickClosed,
    cherryPickSkipped: FeatureItemCodeStatus.CherryPickSkipped,
    cherryPickManuallyResolved: FeatureItemCodeStatus.CherryPickManuallyResolved,
    cherryPickQueued: FeatureItemCodeStatus.CherryPickQueued,
};

/**
 * @deprecated This method should be redundant as Feature Items are persisted with the updated FeatureItemCodeStatus values
 */
export const mapCodeStatus: (originalStatus: string) => FeatureItemCodeStatus = (originalStatus) => {
    return enumMapping[originalStatus as keyof typeof enumMapping];
};

export enum CodeType {
    PullRequest = 'pullRequest',
    Commit = 'commit',
}

export interface TicketProject {
    identifier: string;
    displayableName: string;
    url: string;
}

export enum FeatureItemDebugVariant {
    CodeMergedTicket = 'codeMergedWithTicket',
    CodeUnmergedTicket = 'codeUnmergedWithTicket',
    CodeMergedNoTicket = 'codeMergedNoTicket',
    OpenPRTicket = 'openPRWithTicket',
    OpenPRNoTicket = 'openPRNoTicket',
    NoCodeTicket = 'noCodeOnlyTicket',

    // Not a real debug variant, used by FE when grouping items by ticket
    GroupedByTicket = 'groupedByTicket',
}

export interface TicketItem {
    identifier: string;
    description: string; //ticket title
    fullDescription?: string; //ticket description
    url: string;
    owner?: string;
    reporter?: string;
    status: string;
    createdAt?: string;
    updatedAt?: string;
    isTicketMissingFeatureAffiliation: boolean;
    isTicketSubtask: boolean;
    isTicketDone: boolean;
    project: TicketProject;
    priority?: string;
    ticketType?: string;
    featureAffiliations?: ResolvedFeatureAffiliation[];
    additionalFields: StringKeyedDictionary<string[]>;
}

export interface CodeItem {
    identifier: string;
    status: string; // Revert from string -> FeatureItemCodeStatus as soon as `mapCodeStatus` can be deleted
    type: CodeType;
    url: string;
    author: string;
    manualCherryPickCommands: string[];
    manualCherryPickContext?: string;
    sourcePR?: SourcePRItem;
    sourceCommit?: SourceCommitItem;
}

export interface SourcePRItem {
    number: number;
    url: string;
    featureAffiliations?: ResolvedFeatureAffiliation[];
}

export interface SourceCommitItem {
    hash: string;
    url: string;
}

export interface CommitItem extends CodeItem {
    message: string;
    branch: string;
    committedAt: string;
    unsquashedChildCommitForPR?: SourcePRItem;
}

export interface ChangesVolume {
    added: number;
    changed: number;
    deleted: number;
    commitsCount?: number;
    changedFiles: number;
    codeDiffUrl: string;
}

export interface PRItem extends CodeItem {
    title: string;
    description?: string;
    baseBranch: string;
    headBranch: string;
    createdAt?: string;
    updatedAt?: string;
    changesVolume?: ChangesVolume;
    pullRequestStatus: PullRequestStatus;
    featureAffiliations: ResolvedFeatureAffiliation[];
}

export interface AssociatedStabilityIssue {
    buildIdentifier: string;
    stabilityIssue: StabilityIssue;
}

export interface FeatureFlag {
    id: string;
    name: string;
    description?: string;
    createdAt: string;
    updatedAt?: string;
    enabledAt?: string;
    isEstimatedEnabledAt: boolean;
    estimatedDisabledAt?: string;
    minimumVersion?: string;
    maximumVersion?: string;
    equalVersions?: string[];
    rolloutPercentage?: number;
    status: FeatureFlagStatus;
    type?: FeatureFlagType;
    owner?: string;
    variations: FeatureFlagVariation[];
    rules: FeatureFlagRule[];
    url?: string;
    isReadOnly: boolean;
    isNewlyEnabledInThisVersion: boolean;
    isNewlyDisabledInThisVersion: boolean;
    isSpecificallyTargetingThisVersion: boolean;
    isSpecificallyNotTargetingThisVersion: boolean;
    didChangeInThisVersion: boolean;
    shouldHighlight: boolean;
    highlightTooltipText: string;
}

export enum FeatureFlagStatus {
    Enabled = 'enabled',
    Disabled = 'disabled',
    Archived = 'archived',
    Paused = 'paused',
}

export enum FeatureFlagType {
    OptimizelyExperiment = 'featureFlagOptimizelyExperiment',
    OptimizelyFeature = 'featureFlagOptimizelyFeature',
    OptimizelyFlag = 'featureFlagOptimizelyFlag',
    LaunchDarklyFlag = 'featureFlagLaunchDarklyFlag',
    StatsigGate = 'featureFlagStatsigGate',
    StatsigDynamicConfig = 'featureFlagStatsigDynamicConfig',
}

export interface FeatureFlagVariationVariable {
    type: string;
    key: string;
    value: string;
}

export interface FeatureFlagVariation {
    key: string;
    name: string;
    rolloutPercentage: number;
    variables: FeatureFlagVariationVariable[];
}

export interface FeatureFlagRule {
    id: string;
    name: string;
    isDefault: boolean;
    audienceConditions?: FeatureFlagAudienceCondition[];
    conditions?: FeatureFlagRuleCondition[];
    variations: FeatureFlagWeightedVariation[];
}

export interface FeatureFlagAudienceCondition {
    audiences: FeatureFlagAudience[];
    negate: boolean;
    predicate: FeatureFlagPredicate;
    weight: number;
}

export interface FeatureFlagAudience {
    id: string;
    name: string;
    url?: string;
}

export interface FeatureFlagRuleCondition {
    attribute: string;
    negate: boolean;
    predicate: FeatureFlagPredicate;
    values: unknown[];
}

export enum FeatureFlagPredicate {
    Matches = 'matches',
    EndsWith = 'endsWith',
    StartsWith = 'startsWith',
    Contains = 'contains',
    LessThan = 'lessThan',
    GreaterThan = 'greaterThan',
    LessThanOrEqual = 'lessThanOrEqual',
    GreaterThanOrEqual = 'greaterThanOrEqual',
    Before = 'before',
    After = 'after',
    Unknown = 'unknown',
}

export interface FeatureFlagWeightedVariation {
    key: string;
    name?: string;
    weight: number;
}

export enum TranslationStringStatus {
    OpenPR = 'openPR',
    UploadNeeded = 'uploadNeeded',
    TranslationsPending = 'translationsPending',
    ExportNeeded = 'exportNeeded',
    Synced = 'synced',
    Inconclusive = 'inconclusive',
    BranchUpdateNeeded = 'branchUpdateNeeded',
    BranchUpdatePending = 'branchUpdatePending',
    BranchUpdatePROpen = 'branchUpdatePROpen',
    BranchUpdateFailed = 'branchUpdateFailed',
    UnsupportedFileType = 'unsupportedFileType',
}

export enum PluralTranslationQuantity {
    Zero = 'zero',
    One = 'one',
    Two = 'two',
    Few = 'few',
    Many = 'many',
    Other = 'other',
}

export interface TranslationValue {
    locale: string;
    status: TranslationStringStatus;

    // Either value or pluralValues will be set
    value?: string;
    pluralValues?: Record<PluralTranslationQuantity | string, string>;
    missingInVCS?: boolean;
}

export interface TranslationString {
    key: string;
    isNew: boolean;
    fileName?: string;
    status: TranslationStringStatus;
    values: TranslationValue[];
    openPRNumber?: string;
    exportPRNumber?: string;
    missingInProvider?: boolean;
}

export interface FeatureItem {
    id: string;
    ticket?: TicketItem;
    code?: CommitItem | PRItem;
    explanationText: string;
    ignored: boolean;
    autoIgnored: boolean;
    isItemDone: boolean;
    isInLatestOrSelectedStagingBuild: boolean;
    firstSeenInStagingBuild?: string; // build identifier of the RC build this commit was first available in
    isInLatestWorkingBranchBuild: boolean;
    debugVariant: FeatureItemDebugVariant;
    associatedStabilityIssues: AssociatedStabilityIssue[];
    automationTooltipMessage?: string;
    fixRequest?: FixRequest;

    // FE only, used for handling grouping by ticket
    groupedCodeItems?: FeatureItem[];
}

export interface ReleaseStepFeatureReadinessData {
    items: FeatureItem[];
    workingBranchItems?: FeatureItem[];
    percentComplete: number;
    workingBranchPercentComplete: number;

    customTicketFields: string[];
    doesBranchExist: boolean;
    workingBranch?: string;
    releaseBranch: string;
    latestStagingBuild?: ReleaseCandidateBuildInfo;
    latestWorkingBranchBuild?: CIBuildInfo;
    selectedStagingBuild?: ReleaseCandidateBuildInfo;
    appStoreSelectedBuilds?: AppStoreAvailableBuild[];
}

export interface CompactFeaturesStepData {
    pendingItemsCount: number;
    completedItemsCount: number;
    totalItemsCount: number;
    percentFeatureComplete: number;
}

export interface ReleaseStepFeatureReadiness extends ReleaseStep {
    type: ReleaseStepType.FeatureReadiness;
    data: ReleaseStepFeatureReadinessData;
}

// Regression
export enum RegressionStatus {
    NotStarted = 'notStarted',
    InProgress = 'inProgress',
    Passed = 'passed',
    Failed = 'failed',
}

export enum BuildMatchingWarningType {
    NewerCIBuild = 'newerCIBuild',
    NewerAppStoreBuild = 'newerAppStoreBuild',
    Mismatch = 'mismatch',
    RegressionMismatch = 'regressionMismatch',
    TrackFiltering = 'trackFiltering',
}

export interface BuildMatchingWarning {
    message: string;
    type: BuildMatchingWarningType;
}

export enum RegressionTestingStrategy {
    ClearStatusOnNewBuild = 'clearStatusOnNewBuild',
    MarkLatestRCAsInProgress = 'markLatestRCAsInProgress',
    ManualUpdatesOnly = 'manualUpdatesOnly',
    StatusBasedOnItems = 'statusBasedOnItems',
    StatusBasedOnItemsWithCLIs = 'statusBasedOnItemsWithCLIs',
    DisableOverallStatus = 'disableOverallStatus',
    DisableOverallStatusWithCLIs = 'disableOverallStatusWithCLIs',
}

export const AllRegressionStatuses = allCases(RegressionStatus);

export interface AvailableBuild {
    hash: string;
    buildIdentifier: string;
    integrationId: IntegrationType;
    appStoreBuildIdentifier?: string;
    appStoreIntegrationId?: IntegrationType;
}

export interface TestRunStatus {
    displayableName: string;
    count: number;
    fraction: number; // fraction out of 1
    color: string;
}

export interface TestRun {
    id: string;
    integrationId: IntegrationType;
    name: string;
    description: string;
    statuses: TestRunStatus[];
    createdAt: Date;
    updatedAt?: Date;
    completedAt?: Date;
    url: string;
    completionFraction: number; // fraction out of 1
    isHistorical: boolean;
}

export interface ReleaseStepRegressionTestingData {
    status: RegressionStatus;
    regressionBuildInfo?: RegressionBuildInfo; // The build being regression tested, null if regression testing is "not started"
    availableBuilds: AvailableBuild[];
    testRuns: TestRun[];
    nonHistoricalTestRunsCompletionFraction?: number; // fraction out of 1
    nonHistoricalTestRunsStatusesTotalCount?: number;
    testItems: ChecklistItem[];
    updatedAt?: Date;

    selectedStagingBuild: Nullable<ReleaseCandidateBuildInfo>;
}

export interface CIBuildInfo {
    ciBuild: BuildInfo;
    associatedAppStoreBuild?: AppStoreBuildInfo;
}

export type RegressionBuildInfo = CIBuildInfo;

export type QAHistoryItem = {
    relatedBuildHash: string;
    status: string;
};

export interface ReleaseStepRegressionTesting extends ReleaseStep {
    type: ReleaseStepType.RegressionTesting;
}

export interface ReleaseStepApprovals extends ReleaseStep {
    type: ReleaseStepType.Approvals;
}

export interface ReleaseStepApprovalsData {
    approvalItems: ChecklistItem[];
}

export enum BetaSoakStatus {
    NotTesting = 'notTesting',
    Testing = 'testing',
    TestingNonBlocking = 'testingNonBlocking',
    TestingComplete = 'testingComplete',
}

// Beta testing
export enum BetaTestingStyle {
    NonBlocking = 'nonBlocking',
    OpenEndedSoak = 'openEndedSoak',
    TimeLimitedSoak = 'timeLimitedSoak',
}

export const AllBetaTestingStyles = allCases(BetaTestingStyle);

export interface BetaTester {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
}

export interface BetaTestingGroup {
    id: string;
    name: string;
    testers: string[];
    isExternalGroup: boolean;
}

export interface GoogleBetaBuildDetail {
    status: GooglePlayStoreState;
    userFraction: number;
    url: string;
}

export interface TestFlightBetaBuildDetail {
    url: string; // TODO! return https://appstoreconnect.apple.com/apps/${appId}/testflight/ios/${buildId}
    autoNotifyEnabled: boolean;
    externalBuildState: AppleExternalBetaBuildState;
    internalBuildState: AppleInternalBetaBuildState;
    processingState: AppStoreBuildProcessingState;
}

export interface AppCenterBetaBuildDetail {
    status: string;
    url: string;
}

export interface LocalizedBetaTestingNotes {
    id: string;
    locale: string;
    displayLocale: string;
    notes?: string;
}

export enum BetaStatus {
    Testing = 'testing',
    NotTesting = 'not-testing',
    NeedsReview = 'needs-review',
    WaitingForReview = 'waiting-for-review',
    InReview = 'in-review',
    Processing = 'processing',
    Error = 'error',
}

export type AppBetaBuildInfo = TestFlightBetaBuildDetail | GoogleBetaBuildDetail | AppCenterBetaBuildDetail;

export interface AppStoreBuild {
    appStoreBuild: AppStoreBuildInfo;
    associatedCIBuild?: BuildInfo;
    stabilityInfo?: StabilityInfo;
}

export interface TestTrack {
    name: string;
}

export interface BetaBuild {
    appStoreBuild: AppStoreBuild;
    betaBuildDetails: AppBetaBuildInfo; // additional beta build info
    betaStatus: BetaStatus;
    betaGroups: string[]; // beta groups assigned to the build
    betaTesters: string[]; // beta testers (individual) assigned to the build
    testTrack?: string; // the track the build is on - play beta only
    betaTestingNotes: LocalizedBetaTestingNotes[]; // the test notes assigned to the build
    isConsideredExternal: boolean;
    isSelectedBuild: boolean;
    isLatestBuild: boolean;
    selectedReason?: CIBuildSelectionReason;
    buildDistroBuild?: BuildDistroBuild;
}

export interface ReleaseStepBetaTestingSummaryData {
    soakStartedAt?: string; // usually == buildAvailableFrom unless beta period was explicitly restarted
    soakFinishedAt?: string;
    soakDidFinishEarly: boolean;
    selectedBetaBuilds: BetaBuild[]; // selected beta builds for the version, if any
    testTracks: TestTrack[]; // all available tracks - play beta only
    testers: BetaTester[]; // all available beta testers
    betaGroups: BetaTestingGroup[]; // all available beta testing groups, and their testers
    activeCIBuild?: BuildInfo;
    betaIntegrationType?: IntegrationType;
    buildSystemIntegrationType?: IntegrationType;
    betaTestingActiveBuildIdentifier?: string;
    consoleUrl?: string;
}

export interface ReleaseStepBetaTestingComplementaryData {
    betaBuilds: BetaBuild[]; // all available beta builds for the version
}

export interface ReleaseStepBetaTestingData
    extends ReleaseStepBetaTestingSummaryData,
        ReleaseStepBetaTestingComplementaryData {}

export interface ReleaseStepBetaTesting extends ReleaseStep {
    type: ReleaseStepType.BetaTesting;
}

export enum LocalizationStatus {
    Ready = 'ready',
    Pending = 'pending',
    Blocked = 'blocked',
    PendingTranslations = 'pending-translations',
}

export interface LocalizedMetadata {
    id: string;
    locale: string; // ex. en
    displayLocale: string; // ex. English
    description?: string; // localized description
    shortDescription?: string; // just for android
    keywords?: string; // just for iOS
    promotionalText?: string; // localized promotional text
    whatsNew?: string; // localized whats new
    status: LocalizationStatus;
    appName: string; // localized app name
    videoUrl?: string; // localized video url
    subtitle?: string;
}

export interface MetadataTranslationLocale {
    id: string;
    locale: string;
    appName: MetadataTranslationStatus;
    description?: MetadataTranslationStatus;
    keywords?: MetadataTranslationStatus;
    promotionalText?: MetadataTranslationStatus;
    shortDescription?: MetadataTranslationStatus;
    whatsNew?: MetadataTranslationStatus;
    subtitle?: MetadataTranslationStatus;
}

export enum MetadataTranslationStatus {
    Synced = 'synced',
    TranslationsPending = 'translationsPending',
    UploadNeeded = 'uploadNeeded',
    ExportNeeded = 'exportNeeded',
    NotFound = 'notFound',
}

export interface MetadataWhatsNew {
    appStoreLink: string;
    localizations: LocalizedMetadata[];
}

// Metadata
export interface ReleaseStepMetadataData {
    storeUrl?: string;
    isApproved: boolean;
    requiresManualApproval: boolean;
    localizations: LocalizedMetadata[];
    translationsSourceLocale?: string;
    translationStatuses?: MetadataTranslationLocale[];
    primaryLocale?: string;
    appStoreVersionExists: boolean;
    selectedBetaBuild: BetaBuild;
    appStoreType: AppStoreType;
    platform: AppPlatform;
}

export interface ReleaseStepMetadata extends ReleaseStep {
    type: ReleaseStepType.Metadata;
    data: ReleaseStepMetadataData;
}

// Screenshots
export interface Screenshot {
    id: string;
    width: number;
    height: number;
    url: string;
    uploadError?: string;
}

export interface ScreenshotSet {
    id: string;
    screenshotDisplayType: string;
    screenshots: Screenshot[];
}

export interface ScreenshotLocalization {
    locale: string; // ex. en
    displayLocale: string; // ex. English
    screenshotSets: ScreenshotSet[];
}

export interface AppPreview {
    id: string;
    url: string;
    previewImageUrl: string;
    uploadError?: string;
}

export interface AppPreviewSet {
    id: string;
    previewType: string;
    appPreviews: AppPreview[];
}

export interface AppPreviewLocalization {
    locale: string; // ex. en
    displayLocale: string; // ex. English
    appPreviewSets: AppPreviewSet[];
}

export interface ReleaseStepScreenshotsData {
    storeUrl?: string;
    isApproved: boolean;
    requiresManualApproval: boolean;
    screenshotLocalizations?: ScreenshotLocalization[];
    appPreviewLocalizations?: AppPreviewLocalization[];
    primaryLocale?: string;
    appStoreType: AppStoreType;
    platform: AppPlatform;
}

export interface ReleaseStepScreenshots extends ReleaseStep {
    type: ReleaseStepType.Screenshots;
    data: ReleaseStepScreenshotsData;
}

export interface TranslationProviderState {
    branchId?: string;
    branchName?: string;
    translatedKeyCount: Record<string, number>;
    keyCount: Record<string, number>;
}

// Translations
export interface ReleaseStepTranslationsData {
    isApproved: boolean;
    translationsData?: TranslationsData;
    workingBranch: Nullable<string>;
    releaseBranch: string;
    doesBranchExist: boolean;
    isKickedOff: boolean;
}

export interface TranslationsData {
    keys: TranslationString[];
    workingBranchKeys: TranslationString[];
    sourceLocale: string;
    latestPullRequest?: PullRequest;
    providerState?: TranslationProviderState;
}

export interface ReleaseStepTranslations extends ReleaseStep {
    type: ReleaseStepType.Translations;
    data: ReleaseStepTranslationsData;
}

// Submission
export enum RolloutAction {
    Halt = 'HaltRollout',
    Resume = 'ResumeRollout',
    UpdatePercentage = 'UpdateRolloutPercentage',
}

export type PhasedReleaseState = AppleAppStorePhasedReleaseState | GooglePlayStoreState | HuaweiPhasedReleaseState;

export interface PhasedRelease {
    state: PhasedReleaseState;
    phasedReleasePercent: number;
    startDate?: string;
    dayNumber: number;
    userFraction: number;
    isRunwayPaused: boolean;
    isRunwayAcceleratedRollout: boolean;
    isManuallyAcceleratedPhasedRollout: boolean;
    isSuperseded: boolean;
    isOnBlackoutPeriod: boolean;
    changedByAutomation?: AutomationType;
    blackoutEndsAt?: string;
    completedAt?: string;
}

export interface DestinationPhasedRelease {
    destinationStepConfigId: string;
    destination: Destination;

    state: PhasedReleaseState;
    phasedReleasePercent: number;
    userFraction: number;
    dayNumber: number;

    daysToPercentSchedule: number[];

    isAutomatedPhasedRollout: boolean;

    isPaused: boolean;
    isRunwayPaused: boolean;
    isOnBlackoutPeriod: boolean;

    isAccelerated: boolean;
    isManuallyAcceleratedPhasedRollout: boolean;
    isRunwayAcceleratedRollout: boolean;

    isSuperseded: boolean;
    isCompleted: boolean;

    startDate?: string;
    blackoutEndsAt?: string;
    completedAt?: string;
}

export type AppStoreData =
    | AppleAppStoreData
    | GooglePlayStoreData
    | AmazonAppStoreData
    | HuaweiAppStoreData
    | SamsungAppStoreData;
export type AppStoreState =
    | AppleAppStoreState
    | GooglePlayStoreState
    | AmazonAppStoreState
    | HuaweiAppStoreState
    | SamsungAppStoreState;

export interface AppleAppReviewData {
    id: string;
    contactFirstName?: string; //required
    contactLastName?: string; // required
    contactEmail?: string; // required
    contactPhone?: string; // required
    notes?: string;
    demoAccountName?: string;
    demoAccountPassword?: string;
    demoAccountRequired: boolean; // required
    reviewAttachments: Nullable<ReviewAttachment[]>;
}

export enum ReviewAttachmentUploadStatus {
    Complete = 'COMPLETE',
    Failed = 'FAILED',
    UploadComplete = 'UPLOAD_COMPLETE',
    AwaitingUpload = 'AWAITING_UPLOAD',
}

export interface ReviewAttachment {
    fileName: string;
    fileSize: number;
    status: ReviewAttachmentUploadStatus;
}

export interface CustomProductPageLocalization {
    id: string;
    locale: string;
    promotionalText: string;
    appPreviewSets: AppPreviewSet[];
    screenshotSets: ScreenshotSet[];
}

export enum ASCCustomProductPageState {
    PrepareForSubmission = 'PREPARE_FOR_SUBMISSION',
    ReadyForReview = 'READY_FOR_REVIEW',
    WaitingForReview = 'WAITING_FOR_REVIEW',
    InReview = 'IN_REVIEW',
    Rejected = 'REJECTED',
    Accepted = 'ACCEPTED',
    Approved = 'APPROVED',
    ReplacedWithNewVersion = 'REPLACED_WITH_NEW_VERSION',
}

export interface CustomProductPageVersion {
    id: string;
    version: string;
    state: ASCCustomProductPageState;
}

export interface CustomProductPage {
    id: string;
    name: string;
    url: string;
    visible: boolean;
    versions: CustomProductPageVersion[];
    firstVersionLocalization?: CustomProductPageLocalization;
    ascLink: string;
}

export enum AppEventAssetType {
    EventCard = 'EVENT_CARD',
    EventDetailsPage = 'EVENT_DETAILS_PAGE',
}

export interface PreviewImage {
    url: string;
    width: number;
    height: number;
    type: AppEventAssetType;
}

export interface TerritorySchedule {
    territories: string[];
    publishStart: string;
    eventStart: string;
    eventEnd: string;
}

export enum InAppEventState {
    Draft = 'DRAFT',
    ReadyForReview = 'READY_FOR_REVIEW',
    WaitingForReview = 'WAITING_FOR_REVIEW',
    InReview = 'IN_REVIEW',
    Rejected = 'REJECTED',
    Accepted = 'ACCEPTED',
    Approved = 'APPROVED',
    Published = 'PUBLISHED',
    Past = 'PAST',
    Archived = 'ARCHIVED',
}

export interface InAppEvent {
    id: string;
    badge: string;
    referenceName: string;
    primaryLocale: string;
    territorySchedules: TerritorySchedule[];
    previewImages: PreviewImage[];
    longDescription: string;
    shortDescription: string;
    eventState: InAppEventState;
    ascLink: string;
}

export interface AppStoreAppVersionExperimentTreatment {
    id: string;
    name: string;
}

export enum AppStoreAppVersionExperimentState {
    PrepareForSubmission = 'PREPARE_FOR_SUBMISSION',
    ReadyForReview = 'READY_FOR_REVIEW',
    WaitingForReview = 'WAITING_FOR_REVIEW',
    InReview = 'IN_REVIEW',
    Rejected = 'REJECTED',
    Accepted = 'ACCEPTED',
    Approved = 'APPROVED',
    Published = 'PUBLISHED',
    Past = 'PAST',
    Archived = 'ARCHIVED',
}

export interface AppVersionExperiment {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    reviewRequired: boolean;
    appVersionExperimentTreatments: AppStoreAppVersionExperimentTreatment[];
    controlVersionString?: string;
    state: AppStoreAppVersionExperimentState;
    ascLink: string;
}

export enum AppStoreReviewSubmissionItemState {
    InReview = 'IN_REVIEW', // Not coming from ASC, local to Runway to show IN REVIEW
    ReadyForReview = 'READY_FOR_REVIEW',
    Accepted = 'ACCEPTED',
    Approved = 'APPROVED',
    Rejected = 'REJECTED',
    Removed = 'REMOVED',
}

export enum UnblockAppStoreReviewSubmissionState {
    Unblocking = 'UNBLOCKING',
    Error = 'ERROR',
}

export enum AppStoreBuildSubmissionStatus {
    Unblocked = 'UNBLOCKED',
    Blocked = 'BLOCKED',
    UnresolvedIssues = 'UNRESOLVED_ISSUES',
}

export type ItemByType<T> =
    T extends AppStoreReviewSubmissionItemType.CustomProductPage ? CustomProductPage
    : T extends AppStoreReviewSubmissionItemType.AppEvent ? InAppEvent
    : T extends AppStoreReviewSubmissionItemType.AppStoreVersionExperiment ? AppVersionExperiment
    : null;

export interface ReviewSubmissionItem<T = AppStoreReviewSubmissionItemType> {
    id: string;
    type: T;
    state: AppStoreReviewSubmissionItemState;
    itemId: string; // the id of the specific item (in-app event, custom product page, etc)
    item: ItemByType<T>;
}

export interface ReviewSubmission {
    id: string;
    state: AppStoreReviewSubmissionState;
    appStoreVersionForReviewId?: string;
    reviewSubmissionItems: ReviewSubmissionItem[];
    unblockState?: UnblockAppStoreReviewSubmissionState;
    buildSubmissionStatus: AppStoreBuildSubmissionStatus;
    reviewSubmissionASCLink: string;
}

export interface AppStoreVersion {
    id: string;
    state: AppleAppStoreState;
    releaseType?: AppleAppStoreReleaseType;
    earliestReleaseDate?: string;
}

export interface AppleAppStoreData {
    versionReviewSubmissionItem?: AppStoreVersion;
    phasedRelease?: PhasedRelease;
    reviewDetail?: AppleAppReviewData;
    customProductPages: CustomProductPage[];
    inAppEvents: InAppEvent[];
    relevantReviewSubmission?: ReviewSubmission;
}

export interface AmazonAppStoreData {
    state: AmazonAppStoreState;
}

export interface HuaweiAppStoreData {
    state: HuaweiAppStoreState;
    phasedRelease?: PhasedRelease;
}

export interface SamsungAppStoreData {
    state: SamsungAppStoreState;
}

export interface GooglePlayStoreData {
    state: GooglePlayStoreState;
    phasedRelease?: PhasedRelease;
}

export enum WorkflowType {
    Staging = 'staging',
    Production = 'prod',
    Working = 'working',
}

export interface WorkflowAndBranchData {
    workflowName: string;
    workflowUrl: string;
    workflowType: WorkflowType;
    integrationType: IntegrationType;
    branch: string;
    headCommit?: string;
    headCommitMessage?: string;
    headCommitAuthor?: string;
}

export interface WorkflowData {
    workflowId: string;
    workflowName: string;
    workflowUrl: string;
    subWorkflowId?: string;
    appSlug?: string;
    variables?: Record<string, string>;
}

export type AppStoreReleaseSettings = GoogleAppStoreSettings | AppleAppStoreDefaultSettings;

export interface ReleaseStepSubmissionData {
    appStoreData?: AppStoreData;
    storeUrl?: string;
    workflowData?: WorkflowAndBranchData;
    submittedAt?: string;
    lastCompletedRelease?: ReleaseEntity;
    appStoreReleaseSettings?: AppStoreDefaultSettings;
    lastSuccessfullyUploadedBuild?: AppStoreBuild;
    regressionBuildInfo?: RegressionBuildInfo;
    regressionStatus?: RegressionStatus;
    buildMatchingWarnings?: BuildMatchingWarning[];

    prodBuild: Nullable<CIBuildInfo>;
    appStoreAvailableBuilds: Nullable<AppStoreAvailableBuild[]>;
    appStoreSelectedBuilds: Nullable<AppStoreAvailableBuild[]>;
    additionalAppStoreSelectedBuilds: Nullable<string[]>;
    latestSuccessfulProdBuild: Nullable<CIBuildInfo>;
    latestSuccessfulBuild: Nullable<ReleaseCandidateBuildInfo>;
    selectedStagingBuild: Nullable<ReleaseCandidateBuildInfo>;
    selectedBetaBuild: BetaBuild;
    reviewSubmissionWithoutAppVersion: boolean;
    appStoreType: AppStoreType;
    platform: AppPlatform;
    autoStagedRolloutAutomationId: Nullable<string>;
    latestReleasePhasedRelease: Nullable<PhasedRelease>;
    latestReleaseVersion: Nullable<string>;
    latestPassingRegressionTestBuild: Nullable<QAHistoryItem>;
    isAppStoreBuildListFilteredByConfiguredTrack?: boolean;
}

export interface ReleaseStepSubmission extends ReleaseStep {
    type: ReleaseStepType.Submission;
    data: ReleaseStepSubmissionData;
}

// Store Review

export interface ReleaseStepStoreReviewData {
    appStoreData?: AppStoreData;
    storeUrl?: string;
    markedRejectedAt?: string;
    appStoreReleaseSettings?: AppStoreDefaultSettings;
    lastSuccessfullyUploadedBuild?: AppStoreBuild;

    appStoreAvailableBuilds: Nullable<AppStoreAvailableBuild[]>;
    appStoreSelectedBuilds: Nullable<AppStoreAvailableBuild[]>;
    additionalAppStoreSelectedBuilds: Nullable<string[]>;
    latestSuccessfulProdBuild: Nullable<CIBuildInfo>;
    latestSuccessfulBuild: Nullable<ReleaseCandidateBuildInfo>;
    selectedStagingBuild: Nullable<ReleaseCandidateBuildInfo>;
    latestPassingRegressionTestBuild: Nullable<QAHistoryItem>;
    appStoreType: AppStoreType;
    platform: AppPlatform;
    timeWaitingForReview: Nullable<number>;
    timeInReview: Nullable<number>;
}

export interface ReleaseStepStoreReview extends ReleaseStep {
    type: ReleaseStepType.StoreReview;
    data: ReleaseStepStoreReviewData;
}

// Takeoff
export enum AppleAppStoreReleaseType {
    Manual = 'MANUAL',
    AfterApproval = 'AFTER_APPROVAL', // aka automatic
    Scheduled = 'SCHEDULED',
}

export enum AppleAppStorePhasedReleaseState {
    Inactive = 'INACTIVE',
    Active = 'ACTIVE',
    Paused = 'PAUSED',
    Complete = 'COMPLETE',
}

export enum ReleaseTagState {
    NotTaggable = 'notTaggable', // Currently due to release not being in the App store
    TagNeeded = 'tagNeeded', // Currently manual tagging
    TagNeededNameUnresolved = 'tagNeededNameUnresolved',
    Tagged = 'tagged', // Either by runway or manually
}

export interface TagData {
    expectedReleaseTagName?: string;
    releaseTagState: ReleaseTagState;
    releaseTag?: ReleaseTag;
    codeDiffUrl?: string;
}

export interface LastCompletedRelease {
    version: string;
    isRollback: boolean;
    phasedReleasePercent: number;
    phasedReleaseState: PhasedReleaseState;
}

export interface ReleaseStepTakeoffData {
    appStoreData?: AppStoreData;
    storeUrl?: string;
    appStoreUrl?: string;
    tagData: TagData;
    appStoreReleaseSettings?: AppStoreDefaultSettings;
    lastSuccessfullyUploadedBuild?: AppStoreBuild;
    lastCompletedRelease: Nullable<LastCompletedRelease>;
    isReleaseTagged: boolean;
    phasedReleaseHasBeenSupersededByNewRelease: boolean;
    appStoreAvailableBuilds: Nullable<AppStoreAvailableBuild[]>;
    appStoreSelectedBuilds: Nullable<AppStoreAvailableBuild[]>;
    additionalAppStoreSelectedBuilds: Nullable<string[]>;
    latestSuccessfulProdBuild: Nullable<CIBuildInfo>;
    latestSuccessfulBuild: Nullable<ReleaseCandidateBuildInfo>;
    selectedStagingBuild: Nullable<ReleaseCandidateBuildInfo>;
    appStoreType: AppStoreType;
    platform: AppPlatform;
    latestPassingRegressionTestBuild: Nullable<QAHistoryItem>;
    autoStagedRolloutAutomationId: Nullable<string>;
    buildMatchingWarnings?: BuildMatchingWarning[];
}

export interface ReleaseTag {
    name: string;
    url: string;
    authorEmail?: string;
    authorName?: string;
    isAuthorRunway: boolean;
    commitHash: string;
    commitMessage: string;
    commitURL: string;
    version?: string;
}

export interface PilotInfo {
    userId: string;
    version?: string;
}

export interface ReleasePilotRotationInfo {
    currentPilot?: PilotInfo;
    nextUp?: PilotInfo;
    onDeck?: PilotInfo;
}

export interface SelectableCommit {
    hash: string;
    message: string;
}

export interface ReleaseStepTakeoff extends ReleaseStep {
    type: ReleaseStepType.Takeoff;
    data: ReleaseStepTakeoffData;
}

// VCS Distribution

export interface ReleaseStepVCSDistributionData {
    externalReleaseUrl?: string; // the link to the public github release
    externalPublicRepoName?: string; // the name of the public repo
    externalPublicReleaseNotes?: string; // the release notes published on the public repo
    publishedOn?: string;
    tagData: TagData;
    workflowData?: WorkflowAndBranchData;

    prodBuild: Nullable<CIBuildInfo>;
}

export interface ReleaseStepVCSDistribution extends ReleaseStep {
    type: ReleaseStepType.VCSDistribution;
    data: ReleaseStepVCSDistributionData;
}

// CI Distribution

export interface ReleaseStepCIDistributionData {
    workflowData?: WorkflowAndBranchData;
    isReleased: boolean;
    sdkVersion?: string;
    tagData: TagData;

    prodBuild: Nullable<CIBuildInfo>;
    platform: AppPlatform;
}

export interface ReleaseStepCIDistribution extends ReleaseStep {
    type: ReleaseStepType.CIDistribution;
    data: ReleaseStepCIDistributionData;
}

export enum ReleaseType {
    Major = 'major',
    Minor = 'minor',
    Point = 'point',
    Hotfix = 'hotfix',
    Rollback = 'rollback',
}

export enum OrgOverviewReleaseTypesFilter {
    AllReleaseTypes = 'allReleaseTypes',
    AllExceptHotfix = 'allExceptHotfix',
    HotfixOnly = 'hotfixOnly',
}

export enum ReleaseTypesForNonBlockingSteps {
    Standard = 'standard',
    Hotfix = 'hotfix',
    Rollback = 'rollback',
}

export const AllReleaseTypes = allCases(ReleaseType);

// Release types that can be selected as default
export const PossibleDefaultReleaseTypes = [ReleaseType.Major, ReleaseType.Minor, ReleaseType.Point];

export enum ReleaseTimelinePhase {
    Upcoming = 'upcoming',
    Current = 'current',
    Rollback = 'rollback',
    Completed = 'completed',
}

export enum ReleaseCreatedOrigin {
    Draft = 'draft',
    BranchDetection = 'branch',
    FeatureAffiliationDetection = 'featureAffiliation',
    TaggedLatestReleaseOnStaticReleaseBranch = 'taggedLatestReleaseOnStaticReleaseBranch',
    AppStoreScraper = 'appStoreScraper',
    AutoCreatedByRunway = 'runwayDraft',
}

export enum AppStoreSelectedBuildReason {
    ManualRunwaySelection = 'manualRunwaySelection',
    ManualStoreSelection = 'manualStoreSelection',
    AutoSelection = 'autoRunwaySelection',
    ManualRCSelection = 'manualRCSelection', // user manually chose the selected RC, Runway matched to app store build
    Rollback = 'rollback',
}

// ------------------------------------------------- Timeline Events -------------------------------------------------

export interface TimelineEventReleaseVersionBumpedData {
    branch: string;
    bumpedVersion: string;
}

export interface TimelineEventReleaseBranchCreatedData {
    releaseBranch: string;
}

export interface TimelineEventReleaseCodePromotedData {
    workingBranch: string;
    releaseBranch: string;
}

export interface BuildIdentifiers {
    ciBuildNumber?: string;
    appStoreBuildNumber?: string;
}

export interface CommonBuildData extends BuildIdentifiers {
    ciBuildIntegrationType?: IntegrationType;
    appStoreBuildIntegrationType?: IntegrationType;
    betaServiceName?: string;
    multipleBuildData: BuildIdentifiers[];
}

export interface ReviewSubmissionItemEventData {
    id: string;
    type: AppStoreReviewSubmissionItemType;
    state: AppStoreReviewSubmissionItemState;
    name: string;
}

export interface TimelineEventReleaseSubmittedData extends CommonBuildData {
    reviewSubmissionItems: ReviewSubmissionItemEventData[];
}

export interface TimelineEventReleaseReleasedData extends CommonBuildData {
    isPhasedRelease: boolean;
    reviewSubmissionItems: ReviewSubmissionItemEventData[];
}

export interface TimelineEventReleaseSubmissionItemRemovedData {
    item: ReviewSubmissionItemEventData;
    submissionVersion: string;
}

export interface TimelineEventReleaseSubmissionItemAddedData {
    item: ReviewSubmissionItemEventData;
    submissionVersion: string;
}

export interface TimelineEventReleaseSubmissionItemStatusUpdatedData {
    item: ReviewSubmissionItemEventData;
}

export interface TimelineEventReleaseTaggedData {
    vcsIntegration: IntegrationType;
}

export interface TimelineEventReleaseStepMetadataApprovalChangedData {
    approved: boolean;
}

export interface TimelineEventReleaseStepTranslationsApprovalChangedData {
    approved: boolean;
}

export interface TimelineEventReleaseStepScreenshotsApprovalChangedData {
    approved: boolean;
}

export interface TimelineEventReleaseTargetKickoffDateUpdatedData {
    targetKickoffDate: string;
}

export interface TimelineEventReleaseTargetSubmissionDateUpdatedData {
    targetSubmissionDate: string;
}

export interface TimelineEventReleaseTargetReleaseDateUpdatedData {
    targetReleaseDate: string;
}

export interface TimelineEventReleasePilotChangedData {
    releasePilotId: string;
}

export interface TimelineEventReleaseVersionChangedData {
    oldVersion: string;
    newVersion: string;
}

export interface TimelineEventReleaseStepRCBuildStatusChangedData {
    buildStatus: BuildStatus;
    ciBuildNumber: string;
    ciBuildIntegrationType?: IntegrationType;
}

export interface TimelineEventReleaseStepCIBuildFailureOverriddenData {
    overridden: boolean;
}

export interface TimelineEventReleaseStepRegressionTestingStatusChangedData extends CommonBuildData {
    regressionTestingStatus: RegressionStatus;
}

export type TimelineEventReleaseStepBetaSoakStartedData = CommonBuildData;
export type TimelineEventReleaseStepBetaSoakFinishedData = CommonBuildData;

export interface TimelineEventReleaseStepStatusChangedData {
    releaseStepType: ReleaseStepType;
    releaseStepStatus: ReleaseStepStatus;
}

export interface TimelineEventReleaseStepChecklistCompletedData {
    releaseStepType: ReleaseStepType;
}

export interface TimelineEventMarkReleaseActiveForAppStoreData {
    displayableVersion?: string;
    version?: string;
    storePortalName: string;
}

export interface TimelineEventAppStoreVersionStatusUpdatedData extends CommonBuildData {
    appStoreStatus: AppStoreState;
}

export type TimelineEventAppStoreVersionBetaSubmittedForReviewData = CommonBuildData;

export interface TimelineEventAppStoreVersionPhasedReleaseUpdatedData extends CommonBuildData {
    phaseState?: PhasedReleaseState;
    userFraction?: number;
    dayNumber?: number;
}

export type TimelineEventAppStoreVersionAppStoreBuildChangedData = CommonBuildData;

export interface TimelineEventChecklistItemStatusUpdatedData {
    checklistItemTitle: string;
    releaseStepType?: ReleaseStepType;
    checklistItemStatus: ChecklistItemStatusType;
    checklistItemPlacement: ChecklistItemPlacement;
}

export interface TimelineEventFeatureFlagToggledData {
    featureFlagName: string;
    enabled: boolean;
}

export interface TimelineEventReleaseMarkedRejectedData {
    appStoreType: AppStoreType;
    platform: AppPlatform;
}

export interface TimelineEventBinaryArtifactAppStoreUploadData {
    artifactName: string;
    destination: string;
}

export interface TimelineEventDsymArtifactStabilityMonitoringUploadData {
    buildSystemName: string;
    buildNumber: string;
    destination: string;
}

export interface TimelineEventRollbackResigningStrategyData {
    strategy: RollbackResigningStrategy;
}

export interface TimelineEventChecklistItemCommentUpdatedData {
    checklistItemTitle: string;
    releaseStepType: ReleaseStepType;
    commentDescription: string;
    checklistItemPlacement: ChecklistItemPlacement;
}

export interface TimelineEventTranslationsUploadedData {
    branch: string;
}

export interface TimelineEventTranslationsExportedData {
    translationsIntegrationId: IntegrationType;
    prNumber?: number;
}

export interface TimelineEventMetadataTranslationsExportedData {
    appStoreIntegrationId: IntegrationType;
}

export interface TimelineEventMetadataTranslationsUploadedData {
    translationsIntegrationId: IntegrationType;
}

export interface TimelineEventReleaseCreatedData {
    releaseOrigin: ReleaseCreatedOrigin;
    createdByUser?: string;
}

export enum FileChangesResult {
    ChangedAndDirectlyPushedAndCommitted = 'changedAndPushedAndCommitted',
    ChangedAndDirectlyPushedToBranchWaitingForChecksToPass = 'changedAndPushedToBranchWaitingForChecksToPass',
    ChangedAndMerged = 'changedAndMerged',
    ChangedWithPendingPR = 'changedPendingPR',
    ChangeError = 'errorChanging',
}

export interface TimelineEventTranslationsUpdatedData {
    prNumber?: number;
    changeResult: FileChangesResult;
}

export interface TimelineEventAutomationData {
    automationId: string;
    automationStatus: AutomationStatus;
    automationType: AutomationType;
    stepRunDisplayName: string;
    failureReason?: string;
}

export interface TimelineEventRolloutSupersededData {
    appStoreIntegrationType: IntegrationType;
}

export interface TimelineEventIntegrationUpdatedData {
    title: string;
    stepName?: string;
    integrationCapability: IntegrationCapability;
    previousSettingValue?: string;
    updatedSettingValue?: string;
    changedByUser: string;
}

export interface TimelineEventBatchActionRequestedData {
    transactionType: TransactionType;
    batchTransactionId: string;

    stepGroupName: string;

    targetStepRunIds: string[];
    targetDestStepConfigIds: string[];
    targetStepDisplayNames: string[];

    maxAttempts: number;

    requestedAt: string;
    lastAttemptedAt: Nullable<string>;

    succeededForStepRunIds: Nullable<string[]>;
    successesDestStepConfigIds: Nullable<string[]>;
    successesStepDisplayNames: Nullable<string[]>;

    pendingRetryForStepRunIds: Nullable<string[]>;
    pendingRetryDestStepConfigIds: Nullable<string[]>;
    pendingRetryStepDisplayNames: Nullable<string[]>;

    failedForStepRunIds: Nullable<string[]>;
    failuresDestStepConfigIds: Nullable<string[]>;
    failuresStepDisplayNames: Nullable<string[]>;
}

export interface TimelineEventVCSCherryPickConflictData {
    commitHash: string;
}

export enum TransactionType {
    SubmitVersion = 'submitVersion',
    ReleaseVersion = 'releaseVersion',
    RejectAppStoreRelease = 'rejectAppStoreRelease',
    RunWorkflow = 'runWorkflow',
    StartHotfix = 'startHotfix',
    HaltRollout = 'haltRollout',
    ResumeRollout = 'resumeRollout',
    AccelerateRollout = 'accelerateRollout',
    UpdateRegressionStatus = 'updateRegressionStatus',
    PingPendingRegressionItems = 'pingPendingRegressionItems',
    PingPendingApprovalItems = 'pingPendingApprovalItems',
    PopulateReleaseNotes = 'populateReleaseNotes',
    ApproveMetadata = 'approveMetadata',
}

export enum TransactionGroupType {
    UpdateRollout = 'updateRollout',
}

export enum TimelineEventType {
    ReleaseCreated = 'release.created',
    ReleaseCreatedUpcomingRelease = 'release.createdUpcomingRelease',
    ReleaseVersionBumped = 'release.versionBumped',
    ReleaseBranchCreated = 'release.branchCreated',
    ReleaseCodePromoted = 'release.codePromoted',
    ReleaseSubmitted = 'release.submitted',
    ReleaseReleased = 'release.released',
    ReleaseTagged = 'release.tagged',
    ReleaseStepsReadyToSubmit = 'release.stepsReadyToSubmit',
    ReleaseStepsReadyToRelease = 'release.stepsReadyToRelease',
    ReleaseTargetKickoffDateUpdated = 'release.targetKickoffDateUpdated',
    ReleaseTargetSubmissionDateUpdated = 'release.targetSubmissionDateUpdated',
    ReleaseTargetReleaseDateUpdated = 'release.targetReleaseDateUpdated',
    ReleasePilotChanged = 'release.pilotChanged',
    ReleaseVersionUpdated = 'release.versionUpdated',
    ReleaseStepRCBuildStatusChanged = 'releaseStep.rcBuildStatusChanged',
    ReleaseStepCIBuildFailureOverridden = 'releaseStep.ciBuildFailureOverridden',
    ReleaseStepRegressionTestingStatusChanged = 'releaseStep.regressionTestingStatusChanged',
    ReleaseStepBetaSoakStarted = 'releaseStep.betaSoakStarted',
    ReleaseStepBetaSoakFinished = 'releaseStep.betaSoakFinished',
    ReleaseStepMetadataApprovalChanged = 'releaseStep.metadataApprovalChanged',
    ReleaseStepScreenshotsApprovalChanged = 'releaseStep.screenshotsApprovalChanged',
    ReleaseStepScreenshotAdded = 'releaseStep.screenshotAdded',
    ReleaseStepScreenshotDeleted = 'releaseStep.screenshotDeleted',
    RunwayEventReleaseStepAppPreviewAdded = 'releaseStep.appPreviewAdded',
    RunwayEventReleaseStepAppPreviewDeleted = 'releaseStep.appPreviewDeleted',
    ReleaseStepStatusChanged = 'releaseStep.statusChanged',
    ReleaseStepChecklistCompleted = 'releaseStep.checklistCompleted',
    AppStoreVersionStatusUpdated = 'appStoreVersion.statusUpdated',
    AppStoreVersionBetaSubmittedForReview = 'appStoreVersion.betaSubmittedForReview',
    AppStoreVersionPhasedReleaseUpdated = 'appStoreVersion.phasedReleaseUpdated',
    AppStoreVersionReleaseNotesUpdated = 'appStoreVersion.releaseNotesUpdated',
    AppStoreVersionAppStoreBuildChanged = 'appStoreVersion.appStoreBuildChanged',
    ChecklistItemStatusUpdated = 'checklistItem.statusUpdated',
    ChecklistItemCommentUpdated = 'checklistItem.commentUpdated',
    ReleaseSchedulePaused = 'release.schedulePaused',
    ReleaseScheduleResumed = 'release.scheduleResumed',
    FeatureFlagToggled = 'featureFlag.toggled',
    ReleaseMarkedRejected = 'release.markedRejected',
    AndroidRolloutStartedManually = 'release.rolloutManuallyStarted',
    AndroidRolloutStartedFromForwardedEmail = 'release.rolloutStartedFromForwardedEmail',
    BinaryArtifactAppStoreUploadSuccess = 'binaryArtifact.uploadSuccess',
    BinaryArtifactAppStoreUploadFailed = 'binaryArtifact.uploadFailed',
    DsymArtifactStabilityMonitoringUploadSuccess = 'dsymArtifact.uploadSuccess',
    DsymArtifactStabilityMonitoringUploadFailed = 'dsymArtifact.uploadFailed',
    ReleaseStepTranslationsApprovalChanged = 'releaseStep.translationsApprovalChanged',
    SyncedVCSMetadata = 'release.syncedVCSMetadata',
    AutomationSettingsUpdated = 'automation.settingsUpdated',
    AutomationEnabled = 'automation.enabled',
    AutomationDisabled = 'automation.disabled',
    NotificationSettingsUpdated = 'notification.settingsUpdated',
    NotificationEnabled = 'notification.enabled',
    NotificationDisabled = 'notification.disabled',
    ReleaseDeleted = 'release.deleted',
    ReleaseSkipped = 'release.skipped',
    ReleaseCompleted = 'release.completed',
    ReleaseUncompleted = 'release.uncompleted',
    ReleaseUnskipped = 'release.unskipped',
    ReleaseMarkedActive = 'release.markedActive',
    ReleaseBranchDeleted = 'release.releaseBranchDeleted',
    FeatureWorkItemIgnored = 'releaseStep.featureWorkItemIgnored',
    FeatureWorkItemUnIgnored = 'releaseStep.featureWorkItemUnIgnored',
    FixRequestAdded = 'releaseStep.fixRequestAdded',
    FixRequestDeleted = 'releaseStep.fixRequestDeleted',
    FixRequestApprovalStatusUpdated = 'releaseStep.fixRequestApprovalStatusUpdated',
    StagedRolloutUpdated = 'releaseStep.stagedRolloutUpdated', // Deprecated: Use AppStoreVersionPhasedReleaseUpdated
    ReleaseSettingsUpdated = 'releaseStep.releaseSettingsUpdated',
    IssueTrackingReleaseCompleted = 'releaseStep.issueTrackingReleaseCompleted',
    IssueTrackingTicketsTransitionedToStatus = 'releaseStep.issueTrackingTicketsTransitionedToStatus',
    IntegrationAdded = 'app.integrationAdded',
    IntegrationUpdated = 'app.integrationUpdated',
    IntegrationRemoved = 'app.integrationRemoved',
    ScheduleCadenceEnabled = 'app.releaseScheduleCadenceEnabled',
    ScheduleCadenceDisabled = 'app.releaseScheduleCadenceDisabled',
    AppStoreVersionCreated = 'appStoreVersion.versionCreated',
    BuildEncryptionDefaultsApplied = 'appStoreVersion.buildEncryptionDefaultsApplied',
    BetaBuildTestingGroupsApplied = 'releaseStep.betaBuildTestingGroupsApplied',
    BetaBuildPromotedToTestTrack = 'releaseStep.betaBuildPromotedToTestTrack',
    BetaBuildTestingNotesAutoApplied = 'releaseStep.betaBuildTestingNotesUpdated',
    AutoDownloadArtifacts = 'releaseStep.rcBuildArtifactsDownloaded',
    PRMerged = 'releaseStep.prMerged',
    FeatureAffiliationsApplied = 'releaseStep.featureAffiliationsApplied',
    BuildWorkflowTriggered = 'releaseStep.buildWorkflowTriggered',
    CommsChannelCreated = 'release.commsChannelCreated',
    DefaultReviewAttachmentUpdated = 'releaseStep.defaultReviewAttachmentUpdated',
    RolloutSuperseded = 'release.rolloutSuperseded',
    ReleaseBranchBackmergePerformed = 'releaseStep.releaseBranchBackmergePerformed',
    AppStoreUsersSynced = 'app.appStoreUsersSynced',
    ProvisioningProfileDevicesSynced = 'app.provisioningProfileDevicesSynced',
    DevicesSynced = 'app.devicesSynced',
    ReleaseSummarySent = 'releaseStep.releaseSummarySent',
    CommsGroupsSynced = 'app.commsGroupsSynced',
    CommsPilotUserGroupSynced = 'app.commsPilotUserGroupSynced',
    TestRunCreated = 'releaseStep.testRunCreated',
    LocalizableStringsCherryPicked = 'releaseStep.localizableStringsCherryPicked',
    BuildDetailsPostedToTicket = 'feautureWorkItem.buildDetailsPostedToTicket',
    ReleaseSubmissionUnblocked = 'release.submissionUnblocked',
    ReleaseStepIntegrationSettingsUpdated = 'releaseStep.integrationSettingsUpdated',

    // rollbacks
    ResignSequenceStarted = 'resignSequence.started',
    ResignSequenceCompleted = 'resignSequence.completed',
    BuildResignStarted = 'buildResign.started',
    BuildResignCompleted = 'buildResign.completed',

    // Future events

    TargetKickoffDateUpcoming = 'release.targetKickoffDateUpcoming',
    TargetSubmissionDateUpcoming = 'release.targetSubmissionDateUpcoming',
    TargetReleaseDateUpcoming = 'release.targetReleaseDateUpcoming',

    AutoBumpVersionUpcoming = 'release.autoBumpVersionUpcoming',
    AutoCreateRunwayReleasesUpcoming = 'release.autoCreateRunwayReleasesUpcoming',
    AutoTagReleasesUpcoming = 'release.autoTagReleasesUpcoming',

    TranslationsUploaded = 'translations.uploaded',
    TranslationsExported = 'translations.exported',
    TranslationsUpdated = 'translations.updated',
    MetadataTranslationsUploaded = 'translations.metadata.uploaded',
    MetadataTranslationsExported = 'translations.metadata.exported',

    // review submissions

    ReviewSubmissionItemRemoved = 'release.submissionItem.removed',
    ReviewSubmissionItemAdded = 'release.submissionItem.added',
    ReviewSubmissionItemStatusUpdated = 'release.submissionItem.statusUpdated',

    // Automations
    AutoSelectLatestBuildsAutomation = 'automation.autoSelectLatestBuilds',

    // JSM scheduling
    JiraIssueTrackingIsMissing = 'scheduling.jiraIssueTrackingIsMissing',

    // Batch actions
    BatchActionRequested = 'batchAction.requested',

    // VCS events
    VCSCherryPickConflict = 'vcs.cherryPick.conflict',
}

export enum TimelineEventInitiator {
    User = 'user',
    Runway = 'runway',
    RunwayAdmin = 'runway-admin',
    Unknown = 'unknown',
}

export enum TimelineEventStatus {
    InProgress = 'in-progress',
    Completed = 'completed',
    Failed = 'failed',
    Upcoming = 'upcoming',
    Target = 'target',
    Cancelled = 'cancelled',
    Recurring = 'recurring',
}

export interface ReleaseStatisticsHistory {
    statistics: ReleaseStatistics[];
}

export interface RunwayEventReleaseData {
    version: string;
    displayableVersion: string;
}

export interface RunwayEventAppData {
    appName: string;
    platform: AppPlatform;
}

export interface TimelineUpcomingEventData {
    automationRun: AutomationRun;
}

export interface TimelineEventPreviousErrorData {
    previousError: string;
    triggeredAt: string;
}

export interface TimelineEvent {
    type: TimelineEventType;
    triggeredAt: string;
    triggeredBy: TimelineEventInitiator;
    userId?: string;
    status: TimelineEventStatus;
    orgId: string;
    appId: string;
    releaseStepArchetypes: string[];
    releaseId?: string;
    stepRunId?: string;
    workflowRunId?: string;
    destinationStepConfigIds: Nullable<string[]>;
    releaseData?: RunwayEventReleaseData;
    title?: string;
    additionalData?: Record<string, any>;
    lastAutomationRun?: Record<string, any>;
    automationData?: TimelineEventAutomationData;
    previouslySeenWithErrorAt: TimelineEventPreviousErrorData[];
    previouslySeenCount: number;
    isRetryable?: boolean;
    nextRetryAt?: string;
    eventData?:
        | TimelineEventReleaseMarkedRejectedData
        | TimelineEventReleaseVersionBumpedData
        | TimelineEventReleaseBranchCreatedData
        | TimelineEventReleaseCodePromotedData
        | TimelineEventReleaseSubmittedData
        | TimelineEventReleaseReleasedData
        | TimelineEventReleaseTaggedData
        | TimelineEventReleaseStepMetadataApprovalChangedData
        | TimelineEventReleaseStepScreenshotsApprovalChangedData
        | TimelineEventReleaseTargetKickoffDateUpdatedData
        | TimelineEventReleaseTargetSubmissionDateUpdatedData
        | TimelineEventReleaseTargetReleaseDateUpdatedData
        | TimelineEventReleasePilotChangedData
        | TimelineEventReleaseStepRCBuildStatusChangedData
        | TimelineEventReleaseStepCIBuildFailureOverriddenData
        | TimelineEventReleaseStepRegressionTestingStatusChangedData
        | TimelineEventReleaseStepBetaSoakStartedData
        | TimelineEventReleaseStepBetaSoakFinishedData
        | TimelineEventReleaseStepStatusChangedData
        | TimelineEventReleaseStepChecklistCompletedData
        | TimelineEventMarkReleaseActiveForAppStoreData
        | TimelineEventAppStoreVersionStatusUpdatedData
        | TimelineEventAppStoreVersionBetaSubmittedForReviewData
        | TimelineEventAppStoreVersionPhasedReleaseUpdatedData
        | TimelineEventAppStoreVersionAppStoreBuildChangedData
        | TimelineEventChecklistItemStatusUpdatedData
        | TimelineEventChecklistItemCommentUpdatedData
        | TimelineEventReleaseVersionChangedData
        | TimelineEventFeatureFlagToggledData
        | TimelineEventBinaryArtifactAppStoreUploadData
        | TimelineEventDsymArtifactStabilityMonitoringUploadData
        | TimelineEventRollbackResigningStrategyData
        | TimelineUpcomingEventData
        | TimelineEventTranslationsUploadedData
        | TimelineEventTranslationsUpdatedData
        | TimelineEventMetadataTranslationsUploadedData
        | TimelineEventMetadataTranslationsExportedData
        | TimelineEventTranslationsExportedData
        | TimelineEventReleaseCreatedData
        | TimelineEventReleaseSubmissionItemRemovedData
        | TimelineEventReleaseSubmissionItemAddedData
        | TimelineEventReleaseSubmissionItemStatusUpdatedData
        | TimelineEventRolloutSupersededData
        | TimelineEventIntegrationUpdatedData
        | TimelineEventBatchActionRequestedData
        | TimelineEventVCSCherryPickConflictData;
}

export enum AutomationConditionResult {
    Passed = 'passed',
    Failed = 'failed',
    DoesNotApply = 'does_not_apply',
}

export enum AutomationConditionTooltipType {
    Conditions = 'conditions',
    Automations = 'automations',
}

export interface TimelineEventTooltipLine {
    name: string;
    isAutomationEnabled: boolean;
    description: string;
    result: AutomationConditionResult;
}

export interface TimelineEventAutomationConditionsData {
    text: string;
    tooltip: TimelineEventTooltipLine[];
    tooltipType: AutomationConditionTooltipType;
}

export interface TimelineEventLastAutomationRunData {
    status: AutomationStatus;
    executedAt: string;
}

//-----------------------------------------------------------------------------------------------------------------

export interface RollbackInfo {
    releaseId: string; // the release id of the rollback
    version: string;
    status: ReleaseStatus;
    isReadyToRelease: boolean; // true on iOS if the rollback has been approved by apple and all steps are green, true for Android if the rollback is ready to submit (all steps green)
}

export interface TargetVersionInfo {
    releaseId: string;
    version: string;
    status: ReleaseStatus;
}

export interface ReleaseRecord {
    id: string;
    releaseId: string;
}

export interface ReleaseOverview {
    storeRejectedOn: Nullable<string>;
    devRejectedOn: Nullable<string>;
    isKickedOff: boolean;
    isSubmitted: boolean;
    isReleased: boolean;
    kickedOffAt: Nullable<string>;
    submittedAt: Nullable<string>;
    targetKickoffDate: Nullable<string>;
    targetSubmitDate: Nullable<string>;
    targetReleaseDate: Nullable<string>;
}

/*

Anytime you add a property to ReleaseEntity, be sure to include it in the list of ReleaseEntity properties to be updated in updateReleaseEntity (releases.ts).
Otherwise they'll get dropped on ws message updates.

*/
export interface ReleaseEntity extends ReleaseRecord {
    type: ReleaseType;
    version: string;
    displayableVersion: string;
    status: ReleaseStatus;
    timelinePhase: ReleaseTimelinePhase;
    releasedAt?: string;
    skippedAt?: string;
    phasedRelease?: PhasedRelease;
    /** @deprecated - Will be different per step in Workflows */
    isReleaseTagged: boolean;
    timelineEventsHistory?: TimelineEvent[];
    releaseStatsHistory?: ReleaseStatistics[];
    releaseOverview?: ReleaseOverview;
    rolloutMonitoring?: RolloutMonitoring;
    featureFlags?: FeatureFlag[];
    createdOrigin: ReleaseCreatedOrigin;
    rollbackTargetVersionInfo?: TargetVersionInfo; // if this release is a rollback, info about the target version release
    rollbackInfo?: RollbackInfo; // info on this release's rollback release, if it exists
    shouldWarnAboutNewerCommits: boolean;
}

// DestinationSummary contains summary info about a destination step run
export interface DestinationSummary {
    id: string;
    displayName: string;
    archetype: ReleaseStepType;
    destinationStatus: ReleaseStatus;
    stepConfigId: string;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    iconURL: string;
}

export interface Release extends ReleaseEntity {
    nextVersion: string;
    displayableNextVersion: string;
    createdAt: string;
    updatedAt: string;
    releaseSummaryMessage?: ReleaseSummaryMessage;
    releasePilotId?: string;
    targetKickoffDate?: string;
    targetSubmitDate?: string;
    targetReleaseDate?: string;
    doesBranchExist: boolean;
    isStaticReleaseBranch: boolean;
    scheduledAutomationsPaused: boolean;
    sourceVersion?: string; // rollbacks only
    overview: ReleaseOverviewStats;
    releaseName?: string;
    lifecycleAutomations: AutomationRun[];
    canBeSubmitted: boolean;
    canRevertCompletedStatus?: boolean;
    phasedReleaseHasBeenSupersededByNewRelease: boolean; // required for the release sidebar
    shouldMatchPreviousPhasedRelease: boolean;
    isKickedOff: boolean;
    isSubmitted: boolean;
    isReleased: boolean;
    kickedOffAt?: string;
    submittedAt?: string;

    // workflows
    stepRunGraph: WorkflowRunStepRunGraph;
    workflowRunId: Nullable<string>;

    // destinations
    destinationSummaries: DestinationSummary[];

    RenderedAt?: string;
}

export interface ReleaseSummaryMessage {
    message: string;
}

export enum IntegrationProviderType {
    GitHub = 'github',
    GitLab = 'gitlab',
    Jira = 'jira',
    Codemagic = 'codemagic',
    Bitrise = 'bitrise',
    Bitbucket = 'bitbucket',
    Buildkite = 'buildkite',
    Apple = 'apple',
    Slack = 'slack',
    Jenkins = 'jenkins',
    CircleCI = 'circleci',
    Google = 'google',
    GoogleOAuth = 'google-oauth',
    Amazon = 'amazon',
    Linear = 'linear',
    Pivotal = 'pivotal',
    Bugsnag = 'bugsnag',
    Sentry = 'sentry',
    TestRail = 'testrail',
    BrowserStack = 'browserstack',
    TravisCI = 'travis',
    Xray = 'xray',
    Qase = 'qase',
    AppCenter = 'appcenter',
    Asana = 'asana',
    Shortcut = 'shortcut',
    Amplitude = 'amplitude',
    Azure = 'azure',
    Optimizely = 'optimizely',
    LaunchDarkly = 'launchdarkly',
    Statsig = 'statsig',
    Datadog = 'datadog',
    NewRelic = 'new-relic',
    Generic = 'generic',
    Mixpanel = 'mixpanel',
    Monday = 'monday',
    RunwayBuildDistro = 'runway-build-distro',
    Embrace = 'embrace',
    AzureActiveDirectory = 'azure-active-directory',
    Crowdin = 'crowdin',
    PagerDuty = 'pagerduty',
    Huawei = 'huawei',
    Lokalise = 'lokalise',
    Samsung = 'samsung',
    Opsgenie = 'opsgenie',
    Dynatrace = 'dynatrace',
    CustomAnalytics = 'custom-analytics',
    CustomCI = 'custom-ci',
    JiraServiceManagement = 'jira-service-management',
}

export enum IntegrationType {
    Bitbucket = 'bitbucket',
    BitbucketCI = 'bitbucket-ci',
    GitHubVCS = 'github',
    GitHubCI = 'github-ci',
    GitHubIT = 'github-it',
    GitHubCIDistribution = 'github-ci-dist', // not in use
    GitHubVCSDistribution = 'github-vcs-dist', // distribution via a public gh repo
    GitLabVCS = 'gitlab-vcs',
    GitLabCI = 'gitlab-ci',
    Jira = 'jira',
    Codemagic = 'codemagic',
    Bitrise = 'bitrise',
    Buildkite = 'buildkite',
    Apple = 'apple',
    TestFlight = 'apple-beta',
    XcodeCloud = 'apple-ci',
    Slack = 'slack',
    Jenkins = 'jenkins',
    CircleCI = 'circleci',
    Google = 'google',
    Amazon = 'amazon',
    GoogleBeta = 'google-beta',
    Linear = 'linear',
    Pivotal = 'pivotal',
    Bugsnag = 'bugsnag',
    Sentry = 'sentry',
    TestRail = 'testrail',
    BrowserStack = 'browserstack',
    TravisCI = 'travis',
    Firebase = 'firebase',
    Xray = 'xray',
    Qase = 'qase',
    AppCenterCI = 'appcenter-ci',
    AppCenterBeta = 'appcenter-beta',
    Asana = 'asana',
    Shortcut = 'shortcut',
    Amplitude = 'amplitude',
    AzureCI = 'azure-ci',
    AzureIT = 'azure-it',
    DatadogAnalytics = 'datadog-analytics',
    MixpanelAnalytics = 'mixpanel-analytics',
    GoogleAnalytics = 'google-analytics',
    AndroidVitals = 'android-vitals-analytics',
    ApplePowerPerformanceMetrics = 'apple-power-performance-metrics',
    Optimizely = 'optimizely',
    LaunchDarkly = 'launchdarkly',
    Statsig = 'statsig',
    GenericCI = 'generic-ci',
    Monday = 'monday',
    RunwayBuildDistroBeta = 'runway-build-distro-beta',
    FirebaseBeta = 'firebase-beta',
    EmbraceStabilityMonitoring = 'embrace-stability-monitoring',
    AzureVCS = 'azure-vcs',
    MicrosoftTeams = 'microsoft-teams',
    DatadogStabilityMonitoring = 'datadog-stability-monitoring',
    NewRelicStabilityMonitoring = 'new-relic-stability-monitoring',
    NewRelicAnalytics = 'new-relic-analytics',
    Crowdin = 'crowdin',
    PagerDutyScheduling = 'pagerduty-scheduling',
    OpsgenieScheduling = 'opsgenie-scheduling',
    JiraServiceManagementScheduling = 'jira-service-management-scheduling',
    HuaweiAppGallery = 'huawei',
    Lokalise = 'lokalise',
    Smartling = 'smartling',
    Samsung = 'samsung',
    DynatraceStabilityMonitoring = 'dynatrace-stability-monitoring',
    CustomAnalytics = 'custom-analytics',
    GoogleCalendar = 'google-calendar',
    GooglePersonalCalendar = 'google-personal-calendar',
    CustomCI = 'custom-ci',
}

export const AllIntegrationTypes = allCases(IntegrationType);

export enum FeatureAffiliationType {
    GitHubMilestone = 'github-milestone',
    GitHubLabel = 'github-label',
    JiraLabel = 'jira-label',
    JiraFixVersion = 'jira-fix-version',
    LinearLabel = 'linear-label',
    PivotalLabel = 'pivotal-label',
    AsanaTag = 'asana-tag',
    ShortcutLabel = 'shortcut-label',
    AzureBoardsTag = 'azure-tag',
    MondayColumn = 'monday-column',
}

export interface FeatureAffiliation {
    id: string;
    integrationId: IntegrationType;
    affiliationType: FeatureAffiliationType;
    pattern: string;
}

export interface ResolvedFeatureAffiliation {
    resolvedString: string;
    integrationId: IntegrationType;
    affiliationType: FeatureAffiliationType;
}

export enum IntegrationCapability {
    VersionControl = 'versionControl',
    FeatureManagement = 'featureManagement',
    BuildSystem = 'buildSystem',
    BetaTesting = 'betaTesting',
    AppStore = 'appStore',
    SDKDistribution = 'vcsDistribution',
    Notifications = 'notifications',
    StabilityMonitoring = 'stabilityMonitoring',
    RegressionTesting = 'regressionTesting',
    ObservabilityAndAnalytics = 'productAnalytics',
    FeatureFlagging = 'featureFlagging',
    Translations = 'translations',
    Scheduling = 'scheduling',
    Calendar = 'calendar',
}

// Services that we are currently supporting for artifact uploads. These should only be integrations with the `BetaTesting` or `AppStore` IntegrationCapability
// Update with AppDistributionServiceIntegrationTypes below
export type AppDistributionService =
    | IntegrationAmazon
    | IntegrationApple
    | IntegrationGoogle
    | IntegrationSamsung
    | IntegrationTestFlight
    | IntegrationGoogleBeta
    | IntegrationFirebaseBeta;

export const AppDistributionServiceIntegrationTypes = [
    IntegrationType.Amazon,
    IntegrationType.Apple,
    IntegrationType.Google,
    IntegrationType.Samsung,
    IntegrationType.TestFlight,
    IntegrationType.GoogleBeta,
    IntegrationType.FirebaseBeta,
];

export type StabilityMonitoringService =
    | IntegrationSentry
    | IntegrationBugsnag
    | IntegrationFirebase
    | IntegrationDynatraceStabilityMonitoring;

export const AllIntegrationCapabilities = allCases(IntegrationCapability);

export enum AuthProviderType {
    Google = 'google',
    GitHub = 'github',
}

export enum IntegrationRefreshStatus {
    Success,
    Warning,
    Error,
}

export enum IntegrationErrorType {
    AuthInvalid = 'authInvalid',
    IncompleteData = 'incompleteData',
    IncorrectData = 'incorrectData',
    Unreachable = 'unreachable',
    InternalError = 'internalError',
}

export interface IntegrationError {
    status: IntegrationErrorType;
    message?: string;
    url?: string;
    urlOpensInNewTab?: boolean;
    urlText?: string;
}

export type IntegrationProviderId = string;
export type IntegrationInstallationId = string;

export interface IntegrationEntity {
    id: IntegrationType; // TODO: We should really rename this to `type` or `integrationType`
    installationId: IntegrationInstallationId;
    alias?: string;
    name: string;
    providerId: IntegrationProviderId;
    capabilities: IntegrationCapability[];
    data?: unknown;
    integrationError?: IntegrationError;
    refreshData: IntegrationRefreshData;
}

export interface IntegrationProviderEntity {
    id: string;
    type: IntegrationProviderType;
    name: string;
    appIds: string[];
    installedIntegrationTypesByAppId: Record<string, IntegrationType[]>;

    data?: any;
}

export interface FeatureAffiliationTypeData {
    affiliationType: FeatureAffiliationType;
    name: string; // user-displayable string for the affiliation type
}

export enum GitHubPermissionType {
    Contents = 'contents',
    PullRequests = 'pullRequests',
    Actions = 'actions',
    Webhooks = 'webhooks',
    Metadata = 'metadata',
}

export enum GitHubPermissionValue {
    Read = 'read',
    Write = 'write',
}

export interface GitHubPermission {
    type: GitHubPermissionType;
    value: GitHubPermissionValue;
}

export enum BitbucketPermissionType {
    Repository = 'repository',
    PullRequest = 'pullrequest',
    Account = 'account',
    Pipelines = 'pipeline',
}

export enum BitbucketPermissionValue {
    Read = 'read',
    Write = 'write',
}

export interface BitbucketPermission {
    type: BitbucketPermissionType;
    value: BitbucketPermissionValue;
}

export interface BitbucketWorkspaceAndRepos {
    workspaceId: string;
    workspaceSlug: string;
    repos: BitbucketRepository[];
}

export interface BitbucketRepository {
    id: string;
    name: string;
    workspaceId: string;
}

export interface BitbucketProviderData extends BaseVCSProviderData {
    availableWorkspacesAndRepos: BitbucketWorkspaceAndRepos[];
    bitbucketPermissions: BitbucketPermission[];
}

export interface BitbucketIntegrationData {
    selectedRepo?: BitbucketRepository;
}

export interface IntegrationProviderBitbucket extends IntegrationProviderEntity {
    type: IntegrationProviderType.Bitbucket;
    data: BitbucketProviderData;
}

export interface IntegrationBitbucket extends IntegrationEntity {
    id: IntegrationType.Bitbucket;
    data: BitbucketIntegrationData;
}

export interface BitbucketCIIntegrationData extends BaseCIIntegrationData {
    selectedRepo?: BitbucketRepository;
}

export interface IntegrationBitbucketCI extends IntegrationEntity {
    id: IntegrationType.BitbucketCI;
    data: BitbucketCIIntegrationData;
}

export interface BaseITProviderData {
    featureAffiliationTypes: FeatureAffiliationTypeData[];
}

export interface BaseCIIntegrationData {
    workflowName: string;
    workflowId: string;
    devWorkflowName?: string;
    devWorkflowId?: string;
    prodWorkflowName?: string;
    prodWorkflowId?: string;
    allowMatchingMultipleAppStoreBuilds?: boolean;
    buildVariables?: Record<WorkflowType, Record<string, string>>;
}

export interface WorkflowArgument {
    argumentKey: string;
    argumentValue: string;
}

export interface GitHubActionsWorkflow {
    id: string;
    name: string;
    repo: string;
    url: string;
}

export interface BaseVCSProviderData {
    supportsStatusChecks: boolean;
}

export interface GitHubProviderData extends BaseVCSProviderData, BaseITProviderData {
    availableRepos: string[];
    availableWorkflows: StringKeyedDictionary<GitHubActionsWorkflow[]>;
    gitHubPermissions: GitHubPermission[];
    onPremUrl: string;
    gitHubAppId: string;
    issuesDoneStates: string[];
    issuesAvailableAdditionalDoneStates: string[];
}

export interface GitHubVCSIntegrationData {
    repoName?: string;
    repoOwner?: string;
}

export interface GitHubCIIntegrationData extends BaseCIIntegrationData {
    repoName?: string;
    repoOwner?: string;
}

export interface GitHubITIntegrationData {
    repoName?: string;
    repoOwner?: string;
    additionalDoneStates: string[];
}

export interface IntegrationProviderGitHub extends IntegrationProviderEntity {
    type: IntegrationProviderType.GitHub;
    data: GitHubProviderData;
}

export interface IntegrationGitHubVCS extends IntegrationEntity {
    id: IntegrationType.GitHubVCS;
    data: GitHubVCSIntegrationData;
}

export interface IntegrationGitHubCI extends IntegrationEntity {
    id: IntegrationType.GitHubCI;
    data: GitHubCIIntegrationData;
}

export interface IntegrationGitHubIT extends IntegrationEntity {
    id: IntegrationType.GitHubIT;
    data: GitHubITIntegrationData;
}

export interface IntegrationGitHubVCSDistribution extends IntegrationEntity {
    id: IntegrationType.GitHubVCSDistribution;
    data: GitHubVCSIntegrationData;
}

export interface JiraProject {
    id: string;
    key: string;
    name: string;
}

export enum JiraPermissionType {
    Work = 'jira-work',
    Users = 'jira-users',
    Project = 'jira-project',
}

export enum JiraPermissionValue {
    Read = 'read',
    Write = 'write',
    Manage = 'manage',
}

export interface JiraPermission {
    type: JiraPermissionType;
    value: JiraPermissionValue;
}

export interface JiraProviderData extends BaseITProviderData {
    doneStatuses: string[];
    availableAdditionalDoneStatuses: string[];
    availableProjects: JiraProject[];
    permissions: JiraPermission[];
    cloudUrl: string;
    onPremUrl: string;
    canOfferAutoSelectAllProjects: boolean;
}

export interface JiraIntegrationData {
    additionalDoneStatuses: string[];
    selectedProjects: JiraProject[];
    customFields: string[];
    autoSelectAllProjects: boolean;
}

export interface IntegrationProviderJira extends IntegrationProviderEntity {
    type: IntegrationProviderType.Jira;
    data: JiraProviderData;
}

export interface IntegrationJira extends IntegrationEntity {
    id: IntegrationType.Jira;
    data: JiraIntegrationData;
}

export interface PivotalProject {
    id: string;
    name: string;
}

export interface PivotalProviderData extends BaseITProviderData {
    availableProjects: PivotalProject[];
}

export interface PivotalIntegrationData {
    selectedProjects: PivotalProject[];
}

export interface IntegrationProviderPivotal extends IntegrationProviderEntity {
    type: IntegrationProviderType.Pivotal;
    data: PivotalProviderData;
}

export interface IntegrationPivotal extends IntegrationEntity {
    id: IntegrationType.Pivotal;
    data: PivotalIntegrationData;
}

export interface ShortcutGroup {
    id: string;
    name: string;
}

export interface ShortcutMember {
    id: string;
    profile: ShortcutMemberProfile;
}

export interface ShortcutMemberProfile {
    id: string;
    name: string;
}

export interface ShortcutWorkflowState {
    id: number;
    name: string;
}

export interface ShortcutProviderData extends BaseITProviderData {
    doneStatuses: ShortcutWorkflowState[];
    availableAdditionalDoneStatuses: ShortcutWorkflowState[];
    availableGroups: ShortcutGroup[];
    availableMembers: ShortcutMember[];
}

export interface ShortcutIntegrationData {
    additionalDoneStatuses: ShortcutWorkflowState[];
    selectedGroups: ShortcutGroup[];
}

export interface IntegrationProviderShortcut extends IntegrationProviderEntity {
    type: IntegrationProviderType.Shortcut;
    data: ShortcutProviderData;
}

export interface IntegrationShortcut extends IntegrationEntity {
    id: IntegrationType.Shortcut;
    data: ShortcutIntegrationData;
}

export interface AsanaProject {
    gid: string;
    name: string;
}

export interface AsanaProviderData extends BaseITProviderData {
    availableSections: string[];
    availableProjects: AsanaProject[];
    projectOverflow: boolean;
}

export interface AsanaIntegrationData {
    selectedProjects: AsanaProject[];
    additionalDoneSections: string[];
}

export interface IntegrationProviderAsana extends IntegrationProviderEntity {
    type: IntegrationProviderType.Asana;
    data: AsanaProviderData;
}

export interface IntegrationAsana extends IntegrationEntity {
    id: IntegrationType.Asana;
    data: AsanaIntegrationData;
}

export interface LinearProviderData extends BaseITProviderData {
    doneStatuses: string[];
    availableAdditionalDoneStatuses: string[];
    availableTeams: LinearTeam[];
}

export interface LinearTeam {
    id: string;
    name: string;
}

export interface LinearIntegrationData {
    additionalDoneStatuses: string[];
    selectedTeams: LinearTeam[];
}

export interface IntegrationProviderLinear extends IntegrationProviderEntity {
    type: IntegrationProviderType.Linear;
    data: LinearProviderData;
}

export interface IntegrationLinear extends IntegrationEntity {
    id: IntegrationType.Linear;
    data: LinearIntegrationData;
}

export interface JenkinsProviderData {
    url: string;
    username: string;
    availableJobsOrWorkflows: string[];
}

export interface JenkinsIntegrationData extends BaseCIIntegrationData {
    subJobName?: string;
    prodSubJobName?: string;
    devSubJobName?: string;
}

export interface IntegrationProviderJenkins extends IntegrationProviderEntity {
    type: IntegrationProviderType.Jenkins;
    data: JenkinsProviderData;
}

export interface IntegrationJenkins extends IntegrationEntity {
    id: IntegrationType.Jenkins;
    data: JenkinsIntegrationData;
}

export interface RunwayBuildDistroBucketInfo {
    id: string;
    name: string;
    type: BucketType;
}

export interface RunwayBuildDistroProviderData {
    availableAppsAndBuckets: Record<string, RunwayBuildDistroBucketInfo[]>;
}

export interface RunwayBuildDistroBetaIntegrationData {
    selectedBucket?: RunwayBuildDistroBucketInfo;
}

export interface IntegrationProviderRunwayBuildDistro extends IntegrationProviderEntity {
    type: IntegrationProviderType.RunwayBuildDistro;
    data: RunwayBuildDistroProviderData;
}

export interface IntegrationRunwayBuildDistroBeta extends IntegrationEntity {
    id: IntegrationType.RunwayBuildDistroBeta;
    data: RunwayBuildDistroBetaIntegrationData;
}

export type MondayBoard = {
    id: string;
    name: string;
    columns: MondayColumn[];
};

export type MondayColumn = {
    id: string;
    name: string;
};

export interface MondayProviderData extends BaseITProviderData {
    availableBoards: MondayBoard[];
}

export interface MondayIntegrationData {
    selectedBoards?: MondayBoard[];
    doneColumns?: MondayBoardColumnPair[];
    faColumns?: MondayBoardColumnPair[];
    additionalDoneStatuses?: string[];
}

export interface MondayBoardColumnPair {
    boardId: string;
    columnId: string;
}

export interface IntegrationProviderMonday extends IntegrationProviderEntity {
    type: IntegrationProviderType.Monday;
    data: MondayProviderData;
}

export interface IntegrationMonday extends IntegrationEntity {
    id: IntegrationType.Monday;
    data: MondayIntegrationData;
}

export interface GenericProviderData {}

export interface GenericCIIntegrationData extends BaseCIIntegrationData {}

export interface IntegrationProviderGeneric extends IntegrationProviderEntity {
    type: IntegrationProviderType.Generic;
    data: GenericProviderData;
}

export interface IntegrationGenericCI extends IntegrationEntity {
    id: IntegrationType.GenericCI;
    data: GenericCIIntegrationData;
}

export interface TravisCIProviderData {
    hostURL: string;
}

export interface TravisCIIntegrationData extends BaseCIIntegrationData {
    repoSlug?: string;
}

export interface IntegrationProviderTravisCI extends IntegrationProviderEntity {
    type: IntegrationProviderType.TravisCI;
    data: TravisCIProviderData;
}

export interface IntegrationTravisCI extends IntegrationEntity {
    id: IntegrationType.TravisCI;
    data: TravisCIIntegrationData;
}

export enum GitLabPermissionType {} // TODO?

export enum GitLabPermissionValue {
    Read = 'read',
    Write = 'write',
}

export interface GitLabPermission {
    type: GitLabPermissionType;
    value: GitLabPermissionValue;
}

export interface GitLabProject {
    projectId: string;
    displayableName: string;
}

export interface GitLabProviderData extends BaseVCSProviderData {
    baseUrl: string;
    possibleCiDeployJobNames: string[];
    availableProjects: GitLabProject[];
    gitLabPermissions: GitLabPermission[];
}

export interface GitLabCIIntegrationData extends BaseCIIntegrationData {
    project?: GitLabProject;
}

export interface GitLabVCSIntegrationData {
    project?: GitLabProject;
}

export interface IntegrationProviderGitLab extends IntegrationProviderEntity {
    type: IntegrationProviderType.GitLab;
    data: GitLabProviderData;
}

export interface IntegrationGitLabVCS extends IntegrationEntity {
    id: IntegrationType.GitLabVCS;
    data?: GitLabVCSIntegrationData;
}

export interface IntegrationGitLabCI extends IntegrationEntity {
    id: IntegrationType.GitLabCI;
    data?: GitLabCIIntegrationData;
}

export interface BetaGroup {
    id: string;
    name: string;
    isExternal: boolean;
    isEligibleAsDefaultBetaGroup: boolean;
}

export interface ASCAppInfo {
    bundleId: string;
    appId: number;
    betaGroups?: BetaGroup[];
}

export interface ASCProduct {
    id: string;
    name: string;
}

export interface ASCWorkflow {
    id: string;
    name: string;
    isEnabled: boolean;
}

export interface ASCProductAndWorkflows {
    product: ASCProduct;
    workflows: ASCWorkflow[];
}

export interface AppleProviderData {
    availableApps: ASCAppInfo[];
    availableProductsAndWorkflows: ASCProductAndWorkflows[];
    availablePowerAndPerformanceMetrics: string[];
    keyId: string;
    issuerId: string;
}

export interface AppleIntegrationData {
    bundleId?: string;
    appId: number;
    lastReceivedForwardedEmailAt?: string;
}

export interface IntegrationProviderApple extends IntegrationProviderEntity {
    type: IntegrationProviderType.Apple;
    data: AppleProviderData;
}

export interface IntegrationApple extends IntegrationEntity {
    id: IntegrationType.Apple;
    data: AppleIntegrationData;
}

export interface TestFlightIntegrationData {
    bundleId?: string;
    appId?: number;
    consoleUrl: string;
}

export interface IntegrationTestFlight extends IntegrationEntity {
    id: IntegrationType.TestFlight;
    data: TestFlightIntegrationData;
}

export interface XcodeCloudIntegrationData extends BaseCIIntegrationData {
    productId: string;
    productName: string;
    prodProductId?: string;
    prodProductName?: string;
    devProductId?: string;
    devProductName?: string;
}

export interface IntegrationXcodeCloud extends IntegrationEntity {
    id: IntegrationType.XcodeCloud;
    data: XcodeCloudIntegrationData;
}

export interface AmazonProviderData {
    appId: string;
    clientId: string;
}

export interface AmazonIntegrationData {
    selectedAppId: string;
}

export interface IntegrationProviderAmazon extends IntegrationProviderEntity {
    type: IntegrationProviderType.Amazon;
    data: AmazonProviderData;
}

export interface IntegrationAmazon extends IntegrationEntity {
    id: IntegrationType.Amazon;
    data: AmazonIntegrationData;
}

export enum FirebaseProjectState {
    Active = 'ACTIVE',
    Deleted = 'DELETED',
    Unspecified = 'STATE_UNSPECIFIED',
}

export interface FirebaseProject {
    displayName: string;
    name: string;
    projectId: string;
    projectNumber: number;
    state: FirebaseProjectState;
}

export interface FirebaseAndroidApp {
    appId: string;
    displayName: string;
    packageName: string;
    projectId: string;
}

export interface FirebaseIosApp {
    appId: string;
    bundleId: string;
    displayName: string;
    projectId: string;
}

export interface FirebaseBetaGroup {
    id: string;
    alias: string;
    name: string;
    groupTesters: FirebaseBetaTester[];
}

export interface FirebaseBetaTester {
    id: string;
    email: string;
    displayName: string;
    groups: string[];
}

export interface FirebaseProjectResources {
    project: FirebaseProject;
    availableAndroidApps: FirebaseAndroidApp[];
    availableIosApps: FirebaseIosApp[];
    availableBetaGroups: FirebaseBetaGroup[];
}

export interface GoogleAnalyticsDataStream {
    id: string;
    name?: string;
    platform?: AppPlatform;
}

export interface AnalyticsProperty {
    name: string;
    id: string;
}

export interface GoogleAnalyticsMetric {
    name: string;
    apiName: string;
}

export interface AndroidVitalsMetric {
    name: string;
}

export interface AndroidVitalsApp {
    name: string;
    packageName: string;
    displayName: string;
}

export interface GoogleAnalyticsResources {
    dataStreams: GoogleAnalyticsDataStream[];
    allEventNames: string[];
    allMetrics: GoogleAnalyticsMetric[];
}

export interface AndroidVitalsResources {
    allMetrics: AndroidVitalsMetric[];
    allApps: AndroidVitalsApp[];
}

export interface GoogleProviderData {
    // For Firebase App Distribution
    projectsResources: FirebaseProjectResources[];

    // For Google Analytics
    analyticsResources: GoogleAnalyticsResources;

    androidVitalsResources: AndroidVitalsResources;
}

export interface GoogleIntegrationData {
    packageName: string;
    developerId?: string;
    availableTracks: string[];
    lastReceivedForwardedEmailAt?: string;
}

export interface GoogleBetaIntegrationData extends GoogleIntegrationData {
    testingTrack?: string;
    consoleUrl: string;
}

export interface HuaweiProviderData {
    // TODO implement
}

export interface HuaweiIntegrationData {
    appId: string;
}

export interface IntegrationProviderHuawei extends IntegrationProviderEntity {
    type: IntegrationProviderType.Huawei;
    data: HuaweiProviderData;
}

export interface IntegrationProviderSamsung extends IntegrationProviderEntity {
    type: IntegrationProviderType.Samsung;
    data: SamsungProviderData;
}

export interface SamsungProviderData {
    availableApps: SamsungApp[];
}

export interface SamsungApp {
    name: string;
    contentId: string;
}

export interface IntegrationHuawei extends IntegrationEntity {
    id: IntegrationType.HuaweiAppGallery;
    data: HuaweiIntegrationData;
}

export interface IntegrationSamsung extends IntegrationEntity {
    id: IntegrationType.Samsung;
    data: SamsungIntegrationData;
}

export interface SamsungIntegrationData {
    appId: string;
}

export interface IntegrationProviderGoogle extends IntegrationProviderEntity {
    type: IntegrationProviderType.Google;
    data: GoogleProviderData;
}

export interface IntegrationGoogle extends IntegrationEntity {
    id: IntegrationType.Google;
    data: GoogleIntegrationData;
}

export interface IntegrationGoogleBeta extends IntegrationEntity {
    id: IntegrationType.GoogleBeta;
    data: GoogleBetaIntegrationData;
}

export interface FirebaseIntegrationData {
    projectId?: string;
    gaPropertyId?: string;
    estimatedMonthlyCost?: number;
}

export interface IntegrationFirebase extends IntegrationEntity {
    id: IntegrationType.Firebase;
    data: FirebaseIntegrationData;
}

export interface CodemagicWorkflowName {
    name: string;
}

export interface CodemagicAppData {
    id: string;
    appName: string;
    workflows: Record<string, CodemagicWorkflowName>;
}

export interface CodemagicProviderData {
    availableApps: CodemagicAppData[];
}

export interface CodemagicIntegrationData extends BaseCIIntegrationData {
    appId?: string;
    appName?: string;
    prodAppId?: string;
    prodAppName?: string;
    devAppId?: string;
    devAppName?: string;
}

export interface IntegrationProviderCodemagic extends IntegrationProviderEntity {
    type: IntegrationProviderType.Codemagic;
    data: CodemagicProviderData;
}

export interface IntegrationCodemagic extends IntegrationEntity {
    id: IntegrationType.Codemagic;
    data: CodemagicIntegrationData;
}

export interface BitriseAppData {
    slug: string;
    title: string;
    workflows: string[];
}

export interface BitriseProviderData {
    availableApps: BitriseAppData[];
}

export interface BitriseIntegrationData extends BaseCIIntegrationData {
    appSlug?: string;
    appTitle?: string;
    prodAppSlug?: string;
    prodAppTitle?: string;
    devAppSlug?: string;
    devAppTitle?: string;
    isPipeline?: Record<WorkflowType, boolean>;
}

export interface IntegrationProviderBitrise extends IntegrationProviderEntity {
    type: IntegrationProviderType.Bitrise;
    data: BitriseProviderData;
}

export interface IntegrationBitrise extends IntegrationEntity {
    id: IntegrationType.Bitrise;
    data: BitriseIntegrationData;
}

export interface BuildkiteOrg {
    id: string;
    slug: string;
    name: string;
}

export interface BuildkiteOrgAndPipelines {
    org: BuildkiteOrg;
    pipelines: BuildkitePipeline[];
}

export interface BuildkitePipeline {
    id: string;
    slug: string;
    name: string;
}

export interface BuildkiteProviderData {
    availableOrgsAndPipelines: BuildkiteOrgAndPipelines[];
    tokenScopes: string[];
}

export interface IntegrationProviderBuildkite extends IntegrationProviderEntity {
    type: IntegrationProviderType.Buildkite;
    data: BuildkiteProviderData;
}

export interface BuildkiteIntegrationData extends BaseCIIntegrationData {
    org?: BuildkiteOrg;
}

export interface IntegrationBuildkite extends IntegrationEntity {
    id: IntegrationType.Buildkite;
    data: BuildkiteIntegrationData;
}

export interface AzureProject {
    id: string;
    name: string;
    url: string;
    orgId: string;
}

export interface AzurePipeline {
    id: string;
    name: string;
    url: string;
}

export interface AzureWorkArea {
    id: string;
    name?: string;
    path: string;
}

export interface AzureBoardColumn {
    name: string;
}

export interface AzureProjectAndResources {
    project: AzureProject;
    pipelines: AzurePipeline[];
    workAreas: AzureWorkArea[];
    boardColumns: AzureBoardColumn[];
    repos: AzureRepo[];
}

export interface AzureProviderData extends BaseITProviderData, BaseVCSProviderData {
    availableProjectsAndResources: AzureProjectAndResources[];
}

export interface IntegrationProviderAzure extends IntegrationProviderEntity {
    type: IntegrationProviderType.Azure;
    data: AzureProviderData;
}

export interface AzureCIIntegrationData extends BaseCIIntegrationData {
    selectedProject?: AzureProject;
}

export interface IntegrationAzureCI extends IntegrationEntity {
    id: IntegrationType.AzureCI;
    data: AzureCIIntegrationData;
}

export interface AzureITIntegrationData {
    selectedProject?: AzureProject;
    selectedWorkAreas: AzureWorkArea[];
    additionalDoneColumns: AzureBoardColumn[];
}

export interface IntegrationAzureIT extends IntegrationEntity {
    id: IntegrationType.AzureIT;
    data: AzureITIntegrationData;
}

export type BaseStabilityMonitoringIntegrationData =
    | BugsnagIntegrationData
    | SentryIntegrationData
    | FirebaseIntegrationData
    | DynatraceIntegrationData;

export interface BugsnagOrgAndProjects {
    orgId: string;
    orgName: string;
    projects: BugsnagProject[];
}

export interface BugsnagProject {
    id: string;
    orgId: string;
    name: string;
    releaseStages: string[];
}

export interface BugsnagProviderData {
    availableOrgsAndProjects: BugsnagOrgAndProjects[];
}

export interface BugsnagIntegrationData extends AppMonitoringHealthMetrics {
    selectedProject?: BugsnagProject;
    prodReleaseStage?: string;
    betaReleaseStage?: string;
}

export interface IntegrationProviderBugsnag extends IntegrationProviderEntity {
    type: IntegrationProviderType.Bugsnag;
    data: BugsnagProviderData;
}

export interface IntegrationBugsnag extends IntegrationEntity {
    id: IntegrationType.Bugsnag;
    data: BugsnagIntegrationData;
}

export interface SentryOrgAndProjects {
    orgId: string;
    orgName: string;
    projects: SentryProject[];
}

export interface SentryProject {
    id: string;
    orgId: string;
    displayableName: string;
    environments: string[];
}

export interface SentryProviderData {
    availableOrgsAndProjects: SentryOrgAndProjects[];
}

export interface SentryIntegrationData extends AppMonitoringHealthMetrics {
    selectedProject?: SentryProject;
    prodEnvironment?: string;
    betaEnvironment?: string;
}

export interface IntegrationProviderSentry extends IntegrationProviderEntity {
    type: IntegrationProviderType.Sentry;
    data: SentryProviderData;
}

export interface IntegrationSentry extends IntegrationEntity {
    id: IntegrationType.Sentry;
    data: SentryIntegrationData;
}

export interface TestRailProject {
    id: string;
    name: string;
    isSingleSuite: boolean;
}

export interface TestRailStatus {
    id: string;
    name: string;
    displayableName: string;
}

export interface TestRailProviderData {
    availableProjects: TestRailProject[];
    allStatuses: TestRailStatus[];
    username: string;
    baseUrl: string;
}

export interface TestRailIntegrationData {
    selectedProject?: TestRailProject;
    testRunNameKeywords: string[];
    testRunNameKeywordsMatchAny: boolean;
}

export interface IntegrationProviderTestRail extends IntegrationProviderEntity {
    type: IntegrationProviderType.TestRail;
    data: TestRailProviderData;
}

export interface IntegrationTestRail extends IntegrationEntity {
    id: IntegrationType.TestRail;
    data: TestRailIntegrationData;
}

export interface BrowserStackProject {
    name: string;
    identifier: string;
}

export interface BrowserStackProviderData {
    availableProjects: BrowserStackProject[];
    username: string;
}

export interface BrowserStackIntegrationData {
    selectedProject?: BrowserStackProject;
    testRunNameKeywords: string[];
    testRunNameKeywordsMatchAny: boolean;
}

export interface IntegrationProviderBrowserStack extends IntegrationProviderEntity {
    type: IntegrationProviderType.BrowserStack;
    data: BrowserStackProviderData;
}

export interface IntegrationBrowserStack extends IntegrationEntity {
    id: IntegrationType.BrowserStack;
    data: BrowserStackIntegrationData;
}

export interface QaseProject {
    code: string;
    title: string;
}

export interface QaseProviderData {
    availableProjects: QaseProject[];
}

export interface QaseIntegrationData {
    selectedProject?: QaseProject;
    testRunNameKeywords: string[];
    testRunNameKeywordsMatchAny: boolean;
}

export interface IntegrationProviderQase extends IntegrationProviderEntity {
    type: IntegrationProviderType.Qase;
    data: QaseProviderData;
}

export interface IntegrationQase extends IntegrationEntity {
    id: IntegrationType.Qase;
    data: QaseIntegrationData;
}

export interface XrayIntegrationData {
    testRunNameKeywords: string[];
    testRunNameKeywordsMatchAny: boolean;
}

export interface XrayProviderData {
    clientId: string;
}

export interface IntegrationProviderXray extends IntegrationProviderEntity {
    type: IntegrationProviderType.Xray;
    data: XrayProviderData;
}

export interface IntegrationXray extends IntegrationEntity {
    id: IntegrationType.Xray;
    data: XrayIntegrationData;
}

export interface AppCenterApp {
    id: string;
    ownerName: string;
    appSlug: string;
    distributionGroups: BetaTestingGroup[];
}

export interface AppCenterProviderData {
    availableApps: AppCenterApp[];
}

export interface IntegrationProviderAppCenter extends IntegrationProviderEntity {
    type: IntegrationProviderType.AppCenter;
    data: AppCenterProviderData;
}

export interface AppCenterCIIntegrationData extends BaseCIIntegrationData {
    selectedApp?: AppCenterApp;
}

export interface IntegrationAppCenterCI extends IntegrationEntity {
    id: IntegrationType.AppCenterCI;
    data: AppCenterCIIntegrationData;
}

export interface AppCenterBetaIntegrationData {
    selectedApp?: AppCenterApp;
    consoleUrl: string;
}

export interface IntegrationAppCenterBeta extends IntegrationEntity {
    id: IntegrationType.AppCenterBeta;
    data: AppCenterBetaIntegrationData;
}

export interface CommsChannel {
    id: string;
    name: string;
    isRedacted: boolean;
}

export interface SlackGroup {
    id: string;
    name: string;
    handle: string;
}

export interface SlackProviderData {
    availableChannels: CommsChannel[];
    availableGroups: SlackGroup[];
    scopes: string;
    slackTeam: string;
}

export interface SlackIntegrationData {
    primaryChannels: CommsChannel[];
    shouldAppendNotificationSettings: boolean;
}

export interface IntegrationProviderSlack extends IntegrationProviderEntity {
    type: IntegrationProviderType.Slack;
    data: SlackProviderData;
}

export interface IntegrationSlack extends IntegrationEntity {
    id: IntegrationType.Slack;
    data: SlackIntegrationData;
}

export interface CircleCICollaboration {
    id: string;
    orgName: string;
    projectType: string;
}

export interface CircleCIProviderData {
    availableCollaborations: CircleCICollaboration[];
}

export interface CircleCIIntegrationData extends BaseCIIntegrationData {
    collaboration?: CircleCICollaboration;
    repoName?: string;
    definitionId?: string;
}

export interface IntegrationProviderCircleCI extends IntegrationProviderEntity {
    type: IntegrationProviderType.CircleCI;
    // data: CircleCIProviderData;
    // TODO: including the above causes a strange type error to be thrown across several files. Leaving this provider be for now.
}

export interface IntegrationCircleCI extends IntegrationEntity {
    id: IntegrationType.CircleCI;
    data: CircleCIIntegrationData;
}

export interface AppMonitoringHealthMetrics {
    supportedMetricTypes: AppMonitoringHealthMetricType[];
}

export interface AmplitudeProviderData {
    allUserProperties: string[];
    allEventTypes: string[];
    shouldUseEUServer: boolean;
}

export interface AmplitudeIntegrationData extends AppMonitoringHealthMetrics {
    selectedEventsAndParams: EventAndParam[];
}

export interface EventAndParam {
    eventName: string;
    paramName?: string;
}

export interface GoogleAnalyticsIntegrationData extends AppMonitoringHealthMetrics {
    selectedEventsAndParams: EventAndParam[];
    dataStreamId: string;
}

export interface IntegrationGoogleAnalytics extends IntegrationEntity {
    id: IntegrationType.GoogleAnalytics;
    data: GoogleAnalyticsIntegrationData;
}

export interface AndroidVitalsIntegrationData extends AppMonitoringHealthMetrics {
    selectedEventsAndParams: EventAndParam[];
    appId: string;
}

export interface IntegrationAndroidVitals extends IntegrationEntity {
    id: IntegrationType.AndroidVitals;
    data: AndroidVitalsIntegrationData;
}

export interface ApplePowerPerformanceIntegrationData extends AppMonitoringHealthMetrics {
    selectedEventsAndParams: EventAndParam[];
}

export interface IntegrationApplePowerPerformanceMetrics extends IntegrationEntity {
    id: IntegrationType.ApplePowerPerformanceMetrics;
    data: ApplePowerPerformanceIntegrationData;
}

export interface IntegrationProviderAmplitude extends IntegrationProviderEntity {
    type: IntegrationProviderType.Amplitude;
    data: AmplitudeProviderData;
}

export interface IntegrationAmplitude extends IntegrationEntity {
    id: IntegrationType.Amplitude;
    data: AmplitudeIntegrationData;
}

export interface LokaliseProject {
    id: string;
    name: string;
    url: string;
}

export interface LokaliseProviderData {
    projects: LokaliseProject[];
}

export interface LokaliseIntegrationData {
    selectedProject?: LokaliseProject;
    selectedMetadataProject?: LokaliseProject;
}

export interface IntegrationProviderLokalise extends IntegrationProviderEntity {
    type: IntegrationProviderType.Lokalise;
    data: LokaliseProviderData;
}

export interface IntegrationLokalise extends IntegrationEntity {
    id: IntegrationType.Lokalise;
    data: LokaliseIntegrationData;
}

export interface CrowdinProviderData {
    projects: CrowdinProject[];
    onPremDomain: string;
}

export interface CrowdinIntegrationData {
    selectedProject?: CrowdinProject;
    selectedMetadataProject?: CrowdinProject;
}

export interface CrowdinProject {
    id: string;
    key: string;
    name: string;
    identifier: string;
    url: string;
}

export interface IntegrationProviderCrowdin extends IntegrationProviderEntity {
    type: IntegrationProviderType.Crowdin;
    data: CrowdinProviderData;
}

export interface IntegrationCrowdin extends IntegrationEntity {
    id: IntegrationType.Crowdin;
    data: CrowdinIntegrationData;
}

export interface PagerDutyUser {
    id: string;
    name: string;
    email: string;
}

export interface PagerDutyScheduleInfo {
    id: string;
    name: string;
}

export interface PagerDutyProviderData {
    schedules: PagerDutyScheduleInfo[];
}

export interface PagerDutyIntegrationData {
    selectedSchedule?: PagerDutyScheduleInfo;
}

export interface IntegrationProviderPagerDuty extends IntegrationProviderEntity {
    type: IntegrationProviderType.PagerDuty;
    data: PagerDutyProviderData;
}

export interface IntegrationPagerDuty extends IntegrationEntity {
    id: IntegrationType.PagerDutyScheduling;
    data: PagerDutyIntegrationData;
}

export interface AtlassianScheduleInfo {
    id: string;
    name: string;
}

export interface AtlassianRotationInfo {
    id: string;
    name: string;
    scheduleId: string;
}

export interface OpsgenieProviderData {
    schedules: AtlassianScheduleInfo[];
    rotations: AtlassianRotationInfo[];
    shouldUseEUServer: boolean;
}

export interface OpsgenieIntegrationData {
    selectedSchedule?: AtlassianScheduleInfo;
    selectedRotation?: AtlassianRotationInfo;
}

export interface IntegrationProviderOpsgenie extends IntegrationProviderEntity {
    type: IntegrationProviderType.Opsgenie;
    data: OpsgenieProviderData;
}

export interface IntegrationOpsgenie extends IntegrationEntity {
    id: IntegrationType.OpsgenieScheduling;
    data: OpsgenieIntegrationData;
}

export interface JiraServiceManagementProviderData {
    availableSchedules: AtlassianScheduleInfo[];
    availableRotations: AtlassianRotationInfo[];
    cloudUrl: string;
    onPremUrl: string;
}

export interface JiraServiceManagementIntegrationData {
    selectedSchedule?: AtlassianScheduleInfo;
    selectedRotation?: AtlassianRotationInfo;
}

export interface IntegrationJiraServiceManagement extends IntegrationEntity {
    id: IntegrationType.JiraServiceManagementScheduling;
    data: JiraServiceManagementIntegrationData;
}

export interface IntegrationProviderJiraServiceManagement extends IntegrationProviderEntity {
    type: IntegrationProviderType.JiraServiceManagement;
    data: JiraServiceManagementProviderData;
}

export interface LaunchDarklyProviderData {
    projects: LaunchDarklyProject[];
    environments: Record<string, LaunchDarklyEnvironment[]>;
    tags: string[];
}

export interface LaunchDarklyIntegrationData {
    selectedProject?: LaunchDarklyProject;
    selectedEnvironment?: LaunchDarklyEnvironment;
    selectedTags?: string[];
    selectedTagsMatchAny: boolean;
    selectedVersionAttributeName?: string;
}

export interface LaunchDarklyProject {
    id: string;
    key: string;
    name: string;
}

export interface LaunchDarklyEnvironment {
    id: string;
    key: string;
    name: string;
}

export interface IntegrationProviderLaunchDarkly extends IntegrationProviderEntity {
    type: IntegrationProviderType.LaunchDarkly;
    data: LaunchDarklyProviderData;
}

export interface IntegrationLaunchDarkly extends IntegrationEntity {
    id: IntegrationType.LaunchDarkly;
    data: LaunchDarklyIntegrationData;
}

export interface StatsigProject {
    id: string;
    name: string;
}

export interface StatsigEnvironment {
    id: string;
    name: string;
}

export interface StatsigProviderData {
    currentProject?: StatsigProject;
    environments: StatsigEnvironment[];
    tags: string[];
}

export interface StatsigIntegrationData {
    selectedProject?: StatsigProject;
    selectedEnvironment?: StatsigEnvironment;
    selectedTags?: string[];
    selectedTagsMatchAny: boolean;
    selectedVersionAttributeName?: string;
}

export interface IntegrationProviderStatsig extends IntegrationProviderEntity {
    type: IntegrationProviderType.Statsig;
    data: StatsigProviderData;
}

export interface IntegrationStatsig extends IntegrationEntity {
    id: IntegrationType.Statsig;
    data: StatsigIntegrationData;
}

export interface OptimizelyProviderData {
    projects: OptimizelyProject[];
    environments: Record<string, OptimizelyEnvironment[]>;
    versionAttributeName: string;
}

export interface OptimizelyIntegrationData {
    selectedProject?: OptimizelyProject;
    selectedEnvironment?: OptimizelyEnvironment;
    selectedVersionAttributeName?: string;
}

export interface OptimizelyProject {
    id: string;
    name: string;
    isUsingFlagsAPI: boolean;
    isClassic: boolean;
}

export interface OptimizelyEnvironment {
    id: string;
    name: string;
}

export interface IntegrationProviderOptimizely extends IntegrationProviderEntity {
    type: IntegrationProviderType.Optimizely;
    data: OptimizelyProviderData;
}

export interface IntegrationOptimizely extends IntegrationEntity {
    id: IntegrationType.Optimizely;
    data: OptimizelyIntegrationData;
}

export enum DatadogRumApplicationtype {
    Ios = 'ios',
    Android = 'android',
    ReactNative = 'react-native',
    Flutter = 'flutter',
    JavaScript = 'browser',
}

export interface DatadogRumApplication {
    applicationId: string;
    name: string;
    isActive: boolean;
    type: DatadogRumApplicationtype;
}

export interface DatadogProviderData {
    rumApplications: DatadogRumApplication[];
    applicationKey: string;
    dataCenter: string;
}

export interface DatadogAnalyticsIntegrationData extends AppMonitoringHealthMetrics {
    rumApplicationId: string;
    selectedEventsAndParams: EventAndParam[];
    shouldFilterByBundleId: boolean;
}

export interface IntegrationProviderDatadog extends IntegrationProviderEntity {
    type: IntegrationProviderType.Datadog;
    data: DatadogProviderData;
}

export interface IntegrationDatadogAnalytics extends IntegrationEntity {
    id: IntegrationType.DatadogAnalytics;
    data: DatadogAnalyticsIntegrationData;
}

export interface MixpanelAnalyticsProviderData {
    shouldUseEUServer: boolean;
}

export enum OBAAFilterOperator {
    Equal = '=',
    GreaterThan = '>',
    LessThan = '<',
}

export interface OBAAFilteredParam {
    name: string;
    filterValue: string;
    filterOperator: OBAAFilterOperator;
}

export interface MixPanelCustomSessionsConfiguration {
    eventName: string;
    filteredParameter: OBAAFilteredParam;
}

export interface MixpanelAnalyticsIntegrationData extends AppMonitoringHealthMetrics {
    projectId: number;
    selectedEventsAndParams: EventAndParam[];
    customSessionsConfiguration?: MixPanelCustomSessionsConfiguration;
}

export interface IntegrationProviderMixpanel extends IntegrationProviderEntity {
    type: IntegrationProviderType.Mixpanel;
    data: MixpanelAnalyticsProviderData;
}

export interface IntegrationMixpanelAnalytics extends IntegrationEntity {
    id: IntegrationType.MixpanelAnalytics;
    data: MixpanelAnalyticsIntegrationData;
}

export interface FirebaseBetaIntegrationData {
    projectId: string;
    firebaseAppId: string;
    appIdentifier: string;
    consoleUrl: string;
}

export interface IntegrationFirebaseBeta extends IntegrationEntity {
    id: IntegrationType.FirebaseBeta;
    data: FirebaseBetaIntegrationData;
}

export enum EmbraceEnvironment {
    Production = 'prod',
    Development = 'dev',
    Staging = 'staging',
    QA = 'qa',
    Debug = 'debug',
    Beta = 'beta',
}

export interface EmbraceApp {
    id: string;
    name: string;
    platform: AppPlatform;
    environment: EmbraceEnvironment;
}

export interface EmbraceProviderData {
    allApps: EmbraceApp[];
}

export interface EmbraceStabilityMonitoringIntegrationData extends AppMonitoringHealthMetrics {
    selectedProdApp?: EmbraceApp;
    selectedBetaApp?: EmbraceApp;
}

export interface IntegrationProviderEmbrace extends IntegrationProviderEntity {
    type: IntegrationProviderType.Embrace;
    data: EmbraceProviderData;
}

export interface IntegrationEmbraceStabilityMonitoring extends IntegrationEntity {
    id: IntegrationType.EmbraceStabilityMonitoring;
    data: EmbraceStabilityMonitoringIntegrationData;
}

export interface AzureRepo {
    id: string;
    projectId: string;
    name: string;
    defaultBranch: string;
}

export interface AzureVCSIntegrationData {
    selectedProject?: AzureProject;
    selectedRepo?: AzureRepo;
}

export interface IntegrationAzureVCS extends IntegrationEntity {
    id: IntegrationType.AzureVCS;
    data: AzureVCSIntegrationData;
}

export interface AzureActiveDirectoryTeam {
    id: string;
    name: string;
    description: string;
}

export interface MicrosoftTeamsChannel extends CommsChannel {
    displayName: string;
}

export interface AzureTeamTag {
    id: string;
    displayName: string;
    description: string;
}

export interface AzureActiveDirectoryProviderData {
    availableTeams: AzureActiveDirectoryTeam[];
    availableChannels: MicrosoftTeamsChannel[];
    availableTags: Record<string, AzureTeamTag[]>;
    accessibleTeams?: string[];
}

export interface MicrosoftTeamsIntegrationData {
    primaryChannels: MicrosoftTeamsChannel[];
    shouldAppendNotificationSettings: boolean;
}

export interface IntegrationProviderAzureActiveDirectory extends IntegrationProviderEntity {
    type: IntegrationProviderType.AzureActiveDirectory;
    data: AzureActiveDirectoryProviderData;
}

export interface IntegrationMicrosoftTeams extends IntegrationEntity {
    id: IntegrationType.MicrosoftTeams;
    data: MicrosoftTeamsIntegrationData;
}

export interface DatadogStabilityMonitoringIntegrationData extends AppMonitoringHealthMetrics {
    rumApplicationId: string;
    prodEnvironment: string;
    betaEnvironment?: string;
    shouldFilterByBundleId: boolean;
}

export interface IntegrationDatadogStabilityMonitoring extends IntegrationEntity {
    id: IntegrationType.DatadogStabilityMonitoring;
    data: DatadogStabilityMonitoringIntegrationData;
}

// ----------DYNATRACE---------------------------------------
export interface DynatraceRumApplication {
    id: string;
    name: string;
}

export interface DynatraceProviderData {
    rumApplications: DynatraceRumApplication[];
    accessToken: string;
    environmentId: string;
    updatedAt: number;
}

export interface DynatraceIntegrationData extends AppMonitoringHealthMetrics {
    prodRumApp?: DynatraceRumApplication;
    betaRumApp?: DynatraceRumApplication;
}

export interface IntegrationDynatraceStabilityMonitoring extends IntegrationEntity {
    id: IntegrationType.DynatraceStabilityMonitoring;
    data: DynatraceIntegrationData;
}

export interface IntegrationProviderDynatrace extends IntegrationProviderEntity {
    type: IntegrationProviderType.Dynatrace;
    data: DynatraceProviderData;
}

// ------------------------------------------------

// New Relic

export interface NewRelicMobileApplication {
    applicationGuid: string;
    appId: number;
    name: string;
    isActive: boolean;
}

export interface NewRelicProviderData {
    mobileApplications: NewRelicMobileApplication[];
}

export interface IntegrationProviderNewRelic extends IntegrationProviderEntity {
    type: IntegrationProviderType.NewRelic;
    data: NewRelicProviderData;
}

export interface NewRelicStabilityMonitoringIntegrationData extends AppMonitoringHealthMetrics {
    prodMobileApplication: NewRelicMobileApplication; // prod application guid
    betaMobileApplication?: NewRelicMobileApplication; // beta application guid
}

export interface IntegrationNewRelicStabilityMonitoring extends IntegrationEntity {
    id: IntegrationType.NewRelicStabilityMonitoring;
    data: NewRelicStabilityMonitoringIntegrationData;
}

export interface NewRelicAnalyticsIntegrationData extends AppMonitoringHealthMetrics {
    application: NewRelicMobileApplication;
    selectedEventsAndParams: EventAndParam[];
}

export interface IntegrationNewRelicAnalytics extends IntegrationEntity {
    id: IntegrationType.NewRelicAnalytics;
    data: NewRelicAnalyticsIntegrationData;
}

export interface CustomAnalyticsProviderData {
    displayableName: string;
}

export interface CustomAnalyticsIntegrationData extends AppMonitoringHealthMetrics {
    detectedEvents: string[];
    dataLastReceivedAt: Date;
    integrationId: string;
}

export interface IntegrationProviderCustomAnalytics extends IntegrationProviderEntity {
    type: IntegrationProviderType.CustomAnalytics;
    data: CustomAnalyticsProviderData;
}

export interface IntegrationCustomAnalytics extends IntegrationEntity {
    id: IntegrationType.CustomAnalytics;
    data: CustomAnalyticsIntegrationData;
}

export interface GoogleCalendarIntegrationData {
    calendarId: string;
    calendarName: string;
    appDisplayName?: string;
}

export interface IntegrationGoogleCalendar extends IntegrationEntity {
    id: IntegrationType.GoogleCalendar;
    data: GoogleCalendarIntegrationData;
}

export interface GoogleCalendarEntry {
    description: string;
    id: string;
    summary: string;
    timeZone: string;
}

export interface GoogleOAuthProviderData {
    availableCalendars: GoogleCalendarEntry[];
}

export interface IntegrationProviderGoogleOAuth extends IntegrationProviderEntity {
    type: IntegrationProviderType.GoogleOAuth;
    data: GoogleOAuthProviderData;
}

export interface IntegrationGooglePersonalCalendar extends IntegrationEntity {
    id: IntegrationType.GooglePersonalCalendar;
    data: GoogleCalendarIntegrationData;
}

export type IntegrationProvider =
    | IntegrationProviderGitHub
    | IntegrationProviderGitLab
    | IntegrationProviderJira
    | IntegrationProviderPivotal
    | IntegrationProviderApple
    | IntegrationProviderGoogle
    | IntegrationProviderBitbucket
    | IntegrationProviderCodemagic
    | IntegrationProviderBitrise
    | IntegrationProviderBuildkite
    | IntegrationProviderSlack
    | IntegrationProviderJenkins
    | IntegrationProviderCircleCI
    | IntegrationProviderLinear
    | IntegrationProviderBugsnag
    | IntegrationProviderSentry
    | IntegrationProviderTestRail
    | IntegrationProviderBrowserStack
    | IntegrationProviderTravisCI
    | IntegrationProviderXray
    | IntegrationProviderQase
    | IntegrationProviderAppCenter
    | IntegrationProviderAsana
    | IntegrationProviderShortcut
    | IntegrationProviderAmplitude
    | IntegrationProviderAzure
    | IntegrationProviderOptimizely
    | IntegrationProviderLaunchDarkly
    | IntegrationProviderStatsig
    | IntegrationProviderDatadog
    | IntegrationProviderGeneric
    | IntegrationProviderMixpanel
    | IntegrationProviderMonday
    | IntegrationProviderRunwayBuildDistro
    | IntegrationProviderEmbrace
    | IntegrationProviderAzureActiveDirectory
    | IntegrationProviderAmazon
    | IntegrationProviderCrowdin
    | IntegrationProviderPagerDuty
    | IntegrationProviderNewRelic
    | IntegrationProviderHuawei
    | IntegrationProviderSamsung
    | IntegrationProviderLokalise
    | IntegrationProviderOpsgenie
    | IntegrationProviderJiraServiceManagement
    | IntegrationProviderDynatrace
    | IntegrationProviderCustomAnalytics
    | IntegrationProviderGoogleOAuth
    | IntegrationProviderCustomCI;

export type Integration =
    | IntegrationBitbucket
    | IntegrationBitbucketCI
    | IntegrationGitHubVCS
    | IntegrationGitHubCI
    | IntegrationGitHubIT
    | IntegrationGitHubVCSDistribution
    | IntegrationGitLabVCS
    | IntegrationGitLabCI
    | IntegrationJira
    | IntegrationPivotal
    | IntegrationApple
    | IntegrationGoogle
    | IntegrationAmazon
    | IntegrationCodemagic
    | IntegrationBitrise
    | IntegrationBuildkite
    | IntegrationSlack
    | IntegrationJenkins
    | IntegrationCircleCI
    | IntegrationLinear
    | IntegrationTestFlight
    | IntegrationBugsnag
    | IntegrationSentry
    | IntegrationTestRail
    | IntegrationBrowserStack
    | IntegrationGoogleBeta
    | IntegrationTravisCI
    | IntegrationFirebase
    | IntegrationXray
    | IntegrationQase
    | IntegrationAppCenterCI
    | IntegrationAppCenterBeta
    | IntegrationAsana
    | IntegrationShortcut
    | IntegrationAmplitude
    | IntegrationAzureCI
    | IntegrationAzureIT
    | IntegrationOptimizely
    | IntegrationLaunchDarkly
    | IntegrationStatsig
    | IntegrationDatadogAnalytics
    | IntegrationGenericCI
    | IntegrationMixpanelAnalytics
    | IntegrationXcodeCloud
    | IntegrationMonday
    | IntegrationFirebaseBeta
    | IntegrationRunwayBuildDistroBeta
    | IntegrationEmbraceStabilityMonitoring
    | IntegrationGoogleAnalytics
    | IntegrationAndroidVitals
    | IntegrationApplePowerPerformanceMetrics
    | IntegrationAzureVCS
    | IntegrationMicrosoftTeams
    | IntegrationDatadogStabilityMonitoring
    | IntegrationCrowdin
    | IntegrationPagerDuty
    | IntegrationNewRelicStabilityMonitoring
    | IntegrationNewRelicAnalytics
    | IntegrationHuawei
    | IntegrationSamsung
    | IntegrationLokalise
    | IntegrationOpsgenie
    | IntegrationJiraServiceManagement
    | IntegrationDynatraceStabilityMonitoring
    | IntegrationCustomAnalytics
    | IntegrationCustomCI
    | IntegrationGoogleCalendar
    | IntegrationGooglePersonalCalendar;

export interface ReleaseBranchPattern {
    id: string;
    pattern: string;
    isHotfix: boolean;
    isDefault: boolean;
    isStatic: boolean;
}

export interface ReleaseTagPattern {
    id: string;
    pattern: string;
}

export enum AppPlatform {
    Ios = 'ios',
    Android = 'android',
    IosSDK = 'ios-sdk',
    AndroidSDK = 'android-sdk',
    ReactNativeOTA = 'react-native-ota',
    TvOS = 'tvos',
    VisionOS = 'visionos',
    WearOS = 'wearos',
    AndroidTV = 'android-tv',
    WatchOS = 'watchos',
    Samsung = 'samsung',
    Huawei = 'huawei',
    Amazon = 'amazon',
}

export enum AppStoreType {
    Apple = 'AppStoreConnect',
    Google = 'GooglePlay',
    Amazon = 'Amazon',
    Huawei = 'Huawei',
    Samsung = 'Samsung',
    None = '',
}

export interface StabilityHistory {
    averageSessionStability: number;
    averageUserStability: number;
    dataPointsCount: number;
}

export interface IntegrationRefreshData {
    lastRefreshedAt?: string;
    nextRefreshAt?: string;
    integrationId: IntegrationType;
    inProgress?: boolean;
    refreshStartedAt?: string;
    averageDurationMs?: number;
    rateLimitHit?: boolean;
    rateLimitResetAt?: string;
    canTriggerManualRefresh?: boolean;
}

export interface AppList {
    id: string;
    appId: string;
    appSettings: AppSettingsBase;
}

export interface AppEntity extends AppList {
    id: string; // database id
    appId: string; // customer-facing id
    appSettings: AppSettings;
    users: string[];
    appHome?: AppHome;
    workflowOverview?: WorkflowOverview;
    buildBuckets: BuildDistroBucketBase[];
    stabilityHistory?: StabilityHistory;
    totalAutomationsCount?: Statistic;
    totalTimeSavedFromAutomations?: Statistic;
    totalCostSavingFromAutomations?: Statistic;
    createdAt: string;
    isRestrictedApp: boolean;
    completedReleaseTotalCount: number;
    devices?: AppDevice[];
    devicesRegisteredInASCCount?: number;
    provisioningProfiles?: ProvisioningProfile[];
    releaseStepArchetypes: StringKeyedDictionary<ReleaseStepType[]>;
    liveReleaseId?: string;
    workflowId: Nullable<string>;
}

export interface App extends AppEntity {
    releases: ReleaseEntity[];
}

export interface AppleAppStoreDefaultSettings {
    resetSummaryRating: boolean;
    shouldApplyDefaultEncryption: boolean;
    exportComplianceInfoUsesEncryption: boolean;
    releaseMethod: ReleaseMethod;
    phasedRelease: boolean;
}

export interface GoogleAppStoreSettings {
    stagedRelease: boolean;
    inAppUpdatePriority: number;
    rolloutPercentage?: Percentage;
    // Deprecated in favor of rolloutPercentage
    initialStagedRolloutPercentage?: number;
}

export interface HuaweiAppGallerySettings {
    stagedRelease: boolean;
    rolloutPercentage?: Percentage;
    // Deprecated in favor of rolloutPercentage
    initialStagedRolloutPercentage?: number;
}

export interface AmazonAppStoreDefaultSettings {
    // Intentionally left blank for now
}

export type AppStoreDefaultSettings =
    | AppleAppStoreDefaultSettings
    | GoogleAppStoreSettings
    | HuaweiAppGallerySettings
    | AmazonAppStoreDefaultSettings;

export enum AutomationType {
    AutoSelectLatestBuild = 'AutoSelectLatestBuild',
    AutoCreateAppStoreVersions = 'AutoCreateAppStoreVersions',
    AutoCreateRunwayReleases = 'AutoCreateRunwayReleases',
    AutoTagReleases = 'AutoTagReleases',
    AutoMergeRunwayPRs = 'AutoMergeRunwayPRs',
    AutoAddMissingFAs = 'AutoAddMissingFAs',
    AutoDownloadArtifacts = 'AutoDownloadArtifacts',
    AutoUploadReleaseAssets = 'AutoUploadReleaseAssets',
    AutoApplyMetadata = 'AutoApplyMetadata',
    AutoKickoffRelease = 'AutoKickoff',
    AutoSubmitRelease = 'AutoSubmit',
    AutoRelease = 'AutoRelease',
    AutoBumpVersion = 'AutoBumpVersion',
    AutoAssignBetaBuilds = 'AutoAssignBetaBuilds',
    AutoSubmitBetaBuilds = 'AutoSubmitBetaBuilds',
    AutoPromoteBuildsToTestTrack = 'AutoPromoteBuildsToTestTrack',
    AutoPromoteStablePhasedReleases = 'AutoPromoteStablePhasedReleases',
    AutoHaltUnstablePhasedReleases = 'AutoHaltUnstablePhasedReleases',
    AutoCreateReleaseChannels = 'AutoCreateReleaseChannels',
    AutoCompleteIssueTrackingReleases = 'AutoCompleteIssueTrackingReleases',
    AutoApplyReviewAttachments = 'AutoApplyReviewAttachments',
    AutoBackmergeReleaseBranches = 'AutoBackmergeReleaseBranches',
    AutoApplyBetaBuildNotes = 'AutoApplyBetaBuildNotes',
    AutoKickoffTriggerWorkflow = 'AutoKickoffTriggerWorkflow',
    AutoSyncAppStoreUsers = 'AutoSyncAppStoreUsers',
    AutoDeleteReleaseBranch = 'AutoDeleteReleaseBranch',
    AutoUploadBinaryArtifactToAppStore = 'AutoUploadBinaryArtifactToAppStore',
    AutoUploadDsymsToStabilityMonitoring = 'AutoUploadDsymsToStabilityMonitoring',
    AutoCherryPickFromWorkingBranch = 'AutoCherryPickFromWorkingBranch',
    AutoTransitionIssueTrackingTickets = 'AutoTransitionIssueTrackingTickets',
    AutoStagedRollout = 'AutoStagedRollout',
    AutoDistributeReleaseSummary = 'AutoDistributeReleaseSummary',
    AutoSyncAppleDevices = 'AutoSyncAppleDevices',
    AutoSyncAppleProvisioningProfilesDevices = 'AutoSyncAppleProvisioningProfilesDevices',
    AutoResignRollbackBuild = 'AutoResignRollbackBuild',
    AutoPrepareRollbackRelease = 'AutoPrepareRollbackRelease',
    AutoSyncSlackGroups = 'AutoSyncSlackGroups',
    AutoSyncSlackPilotUserGroup = 'AutoSyncSlackPilotUserGroup',
    AutoSyncTeamsTags = 'AutoSyncTeamsTags',
    AutoCreateTestRuns = 'AutoCreateTestRuns',
    AutoCherryPickLocalizableStrings = 'AutoCherryPickLocalizableStrings',
    AutoSyncTranslations = 'AutoSyncTranslations',
    AutoSyncMetadataTranslations = 'AutoSyncMetadataTranslations',
    AutoSyncMetadataFromVCS = 'AutoSyncMetadataFromVCS',
    AutoTranslateUserReviews = 'AutoTranslateUserReviews',
    AutoPostBuildInfoOnPMTickets = 'AutoPostBuildInfoOnPMTickets',
    AutoTriggerCIWorkflow = 'AutoTriggerCIWorkflow',
    // Bucket automations
    BAAutoUpdatePRWithArtifactLink = 'AutoUpdatePRWithArtifactLink', // Deprecated naming convention, future Bucket automations should use prefix `BA_`
    BAAutoGenerateBuildTesterNotes = 'BA_AutoGenerateBuildTesterNotes',
    BAAutoTriggerBuildSystemWorkflow = 'BA_AutoTriggerBuildSystemWorkflow',
    BAAutoUploadBinaryArtifactToAppStore = 'BA_AutoUploadBinaryArtifactToAppStore',
    BAAutoPostBuildInfoOnPMTickets = 'BA_AutoPostBuildInfoOnPMTickets',
    BAAutoAssignBetaBuilds = 'BA_AutoAssignBetaBuilds',
}

export enum AutomationCategory {
    ReleaseCycle = 'Release cycle',
    AppStoreTasks = 'App store tasks',
    BetaTesting = 'Beta testing',
    ReleaseHygiene = 'Release hygiene',
    General = 'General',
    Unknown = 'Unknown',
}

export const AllAutomationCategories = allCases(AutomationCategory);

export enum AutomationStatus {
    Active = 'active', // actively happening more than once during a single release
    Inactive = 'inactive', // not actively happening
    Pending = 'pending', // will happen once in the future
    Success = 'success', // successfully occurred
    Failed = 'failed',
    Skipped = 'skipped', // didn't happen because the action occurred externally before Runway could automate
    Incomplete = 'incomplete', // needs resolution from the user before it can be considered successful
    Paused = 'paused', // automations are paused for the release
    Blocked = 'blocked', // when runway is unable to perform the automation
    Retrying = 'retrying',
    InProgress = 'inProgress',
}

export interface AutomationEntity {
    id: string;
    type: AutomationType;
    stepConfigId: Nullable<string>;
    legacyAppId?: string;
    name: string;
    description: string;
    additionalInfo?: string;
    enabled: boolean;
    isNew: boolean;
    isBeta: boolean;
    requiredCapabilities: IntegrationCapability[][];
    unsupportedIntegrations: IntegrationType[];
    supportedPlatforms: AppPlatform[];
    integrationInstallationIds: IntegrationInstallationId[];
    platform: AppPlatform;
    appStoreType: AppStoreType;
    automationRequirementsErrorMessage?: string;
    relativeTimeTrigger: RelativeTimeTrigger;
    completedStepsTrigger: CompletedStepsTrigger;
    automationReleaseTypes?: AutomationReleaseTypes;
}

export interface RelativeTimeTrigger {
    enabled: boolean;
    timing: RelativeTiming;
    relativeDays: number;
    relativeHours: number;
    relativeMinutes: number;
    lifecycleEventType: LifecycleEventType;
    waitForAllDestinations: boolean;
    allowTriggeringBasedOnTargetDate?: boolean;
}

export interface CompletedStepsTrigger {
    enabled: boolean;
    hasCustomRequiredSteps: boolean;
    requiredSteps: Nullable<ReleaseStepType[]>;
}

export interface AutomationReleaseTypeOverride {
    enabled?: boolean;
    data?: unknown;
    relativeTimeTrigger?: RelativeTimeTrigger;
    completedStepsTrigger?: CompletedStepsTrigger;
}

export interface Automation extends AutomationEntity {
    data?: unknown;
    configPerReleaseType?: Partial<Record<ReleaseTypesForNonBlockingSteps, AutomationReleaseTypeOverride>>;
}

export type BucketAutomation = Automation;

export interface AutomationRun {
    id: string;
    type: AutomationType;
    stepConfigId: Nullable<string>;
    description: string;
    status: AutomationStatus;
    name: string;
    isLifecycle: boolean;
    scheduledAt?: string;
    autoScheduledFromCadence?: boolean;
    statusMessage?: string;
    statusMessageLinkUrl?: string;
    isPartOfSequence: boolean;
    sequenceNumber?: number;
    hasEditableSettings?: boolean;
    relevantPRs: PullRequest[];
    isRetrying?: boolean;
}

export enum BumpVersionAutomationStrategy {
    PreKickoffToCurrentVersionOnWorkingBranch = 'AutoBumpPreKickoffToCurrentVersion',
    PostKickoffToCurrentVersionOnReleaseBranch = 'AutoBumpPostKickoffToCurrentVersion',
    PostKickoffToNextVersionOnWorkingBranch = 'AutoBumpPostKickoffToNextVersion',
    PostSubmitToNextVersionOnWorkingBranch = 'AutoBumpPostSubmitToNextVersion',
    PostReleaseToNextVersionOnWorkingBranch = 'AutoBumpPostReleaseToNextVersion',
}

export const AllBumpVersionStrategies = allCases(BumpVersionAutomationStrategy);

export enum AutomationReleaseTypes {
    All = 'all',
    HotfixOnly = 'hotfixOnly',
    AllExceptHotfix = 'allExceptHotfix',
}

export const AllAutomationReleaseTypes = allCases(AutomationReleaseTypes);

export enum AndroidAutoRolloutStartStrategy {
    Auto = 'Auto',
    Manual = 'Manual',
    AutoFromForwardedEmail = 'AutoFromForwardedEmail',
}

export const AllAndroidRolloutStartStrategies = allCases(AndroidAutoRolloutStartStrategy);

export interface DirectlyCommittableAutomation {
    shouldCommitAndPushDirectlyToTargetBranch?: boolean;
}

export interface AutomationBumpVersionData extends DirectlyCommittableAutomation {
    strategy: BumpVersionAutomationStrategy;
}

export interface AutomationBumpVersion extends Automation {
    type: AutomationType.AutoBumpVersion;
    data: AutomationBumpVersionData;
}

export interface AutomationHaltPromotePhasedReleaseData {
    phasedReleaseDayThreshold?: number;
    adoptionThreshold?: number;
    stagedRolloutPercentageThreshold?: number;
    monitoringConditions: string[];
    checklistItems: string[];
}

export interface AutoSyncAppStoreUsersData {
    allowDeleteUsers: boolean;
    rolesMap: RolesMap;
}

export enum ReleaseNotesDataSource {
    DefaultReleaseNotes = 'defaultReleaseNotes',
    PreviousRelease = 'previousRelease',
}

export const AllReleaseNotesDataSources = allCases(ReleaseNotesDataSource);

export interface AutoApplyMetadataData {
    releaseNotesSource: ReleaseNotesDataSource;
}

export enum AutoBackmergeStrategy {
    OnReleaseCompletion = 'onReleaseCompletion',
    Continuous = 'continuous',
    ContinuousCherryPick = 'continuousCherryPick',
}

export interface AutoBackmergeReleaseBranchesData {
    strategy: AutoBackmergeStrategy;
    availableStrategies: AutoBackmergeStrategy[];
    runwayPRTypesToSkip: PullRequestType[];
    shouldBackmergeOnlyMergeCommits: boolean;
}

export enum BuildNotesAutomationStrategy {
    CarryOver = 'BuildNotesFromPreviousBuild',
    BuildChangelog = 'BuildNotesFromBuildChangelog',
    ReleaseChangelog = 'BuildNotesFromReleaseChangelog',
}

export const AllBetaBuildNotesStrategies = allCases(BuildNotesAutomationStrategy);

export interface AutoApplyBetaBuildNotesData {
    strategy: BuildNotesAutomationStrategy;
}

export enum AppStoreArtifactUploadRule {
    UploadNewestBuildOnly = 'uploadNewestBuildOnly',
    ContinueOnFailure = 'continueOnFailure',
    StopOnFailure = 'stopOnFailure',
}

export const AllAppStoreArtifactUploadRules = allCases(AppStoreArtifactUploadRule);

export enum AppStoreArtifactUploadDestinationType {
    DestinationBetaTesting = 'betaTesting',
    DestinationAppStore = 'appStore',
}

export interface AppStoreArtifactUploadWorkflowSettings {
    workflowType: WorkflowType;
    fileNames: string[];
    destinationInstallationId: IntegrationInstallationId;
    trackName?: string;
    createNewTrackReleaseWithPatternedName?: string;
}

export interface AutoUploadBinaryArtifactToAppStoreData {
    uploadWorkflowSettings: AppStoreArtifactUploadWorkflowSettings[];
    uploadRule: AppStoreArtifactUploadRule;
}

export interface DsymsUploadWorkflowSettings {
    workflowType: WorkflowType;
    fileNames: string[];
}

export interface AutoUploadDsymsToStabilityMonitoringData {
    uploadWorkflowSettings: DsymsUploadWorkflowSettings[];
}

export interface AutoTriggerBuildDistroBuildData {
    timedTriggerEveryNHours?: number;
}

export interface AutomationAutoAddMissingFAs extends Automation {
    type: AutomationType.AutoAddMissingFAs;
    data: AutomationAutoAddMissingFAsData;
}

export interface AutomationAutoAddMissingFAsData {
    safeMode: boolean;
    withComponents: boolean;
    useWorkspaceLabels: boolean;
}

export interface AutomationAutoTagReleases extends Automation {
    type: AutomationType.AutoTagReleases;
    data: AutomationAutoTagReleasesData;
}

export interface AutomationAutoTagReleasesData {
    fallbackToHeadIfNeeded: boolean;
}

export interface AutomationKickoffReleaseData extends DirectlyCommittableAutomation {
    allowKickoffWhenPreviousNotComplete: boolean;
}

export interface AutomationKickoffRelease extends Automation {
    type: AutomationType.AutoKickoffRelease;
    data: AutomationKickoffReleaseData;
}

export interface AutomationPromoteStablePhasedRelease extends Automation {
    type: AutomationType.AutoPromoteStablePhasedReleases;
    data: AutomationHaltPromotePhasedReleaseData;
}

export interface AutomationHaltUnstableRelease extends Automation {
    type: AutomationType.AutoHaltUnstablePhasedReleases;
    data: AutomationHaltPromotePhasedReleaseData;
}

export interface AutomationPrepareRollbackRelease extends Automation {
    type: AutomationType.AutoPrepareRollbackRelease;
    data: AutomationPrepareRollbackReleaseData;
}

export interface AutomationApplyReviewAttachmentData {
    reviewAttachmentFile?: FileStats;
}

export interface AutomationApplyReviewAttachment extends Automation {
    type: AutomationType.AutoApplyReviewAttachments;
    data: AutomationApplyReviewAttachmentData;
}

export enum RollbackResigningStrategy {
    LocalResign = 'localResign',
    CIResign = 'ciResign',
}

export const AllRollbackResigningStrategies = allCases(RollbackResigningStrategy);

export interface AutomationPrepareRollbackReleaseData {
    strategy: RollbackResigningStrategy;
    // Auto submit
    shouldAutoSubmit: boolean;

    // Auto abandon
    abandonRollbackMonitoringConditions: string[];
    abandonRollbackAdoptionThreshold?: number;
    abandonRollbackPhasedReleaseDayThreshold?: number;
    abandonRollbackStagedRolloutPercentageThreshold?: number;
}

export interface AutomationBackmergeReleaseBranches extends Automation {
    type: AutomationType.AutoBackmergeReleaseBranches;
    data?: AutoBackmergeReleaseBranchesData;
}

export interface AutomationApplyBetaBuildNotes extends Automation {
    type: AutomationType.AutoApplyBetaBuildNotes;
    data?: AutoApplyBetaBuildNotesData;
}

export interface AutomationSyncAppStoreUsers extends Automation {
    type: AutomationType.AutoSyncAppStoreUsers;
    data?: AutoSyncAppStoreUsersData;
}

export interface AutomationApplyMetadata extends Automation {
    type: AutomationType.AutoApplyMetadata;
    data?: AutoApplyMetadataData;
}

export interface AutomationAutoUploadBinaryArtifactToAppStore extends Automation {
    type: AutomationType.AutoUploadBinaryArtifactToAppStore;
    data?: AutoUploadBinaryArtifactToAppStoreData;
}

export interface AutomationAutoUploadDsymsToStabilityMonitoring extends Automation {
    type: AutomationType.AutoUploadDsymsToStabilityMonitoring;
    data?: AutoUploadDsymsToStabilityMonitoringData;
}

export interface AutomationBAAutoTriggerBuildSystemWorkflow extends Automation {
    type: AutomationType.BAAutoTriggerBuildSystemWorkflow;
    data?: AutoTriggerBuildDistroBuildData;
}

export type AutoStagedRolloutBlackoutDay = {
    isBlackedOut: boolean;
    segments: AutoStagedRolloutBlackoutSegment[];
};

export type AutoStagedRolloutBlackoutSegment = {
    startTime?: string;
    endTime?: string;
};

export type AutoStagedRolloutBlackoutSegmentsByDay = Record<number, AutoStagedRolloutBlackoutDay>;

export interface AutoStagedRolloutData {
    daysToPercent?: number[];
    autoStagedRolloutBlackoutSegmentsByDay: AutoStagedRolloutBlackoutSegmentsByDay;
    autoStagedRolloutBlackoutTimezone: string;
    autoStagedRolloutStartStrategy: AndroidAutoRolloutStartStrategy;
    autoStagedRolloutCustomHour?: number;
}

export interface AutomationAutoStagedRollout extends Automation {
    type: AutomationType.AutoStagedRollout;
    data?: AutoStagedRolloutData;
}

export interface AutoAutoDistributeReleaseSummaryData {
    emailAddresses: string[];
}

export interface AutomationDistributeReleaseSummary extends Automation {
    type: AutomationType.AutoDistributeReleaseSummary;
    data?: AutoAutoDistributeReleaseSummaryData;
}

export enum AutoCherryPickStrategy {
    SingleInSequence = 'CherryPickSingleInSequence',
    SingleInParallel = 'CherryPickSingleInParallel',
    Cumulative = 'CherryPickCumulative',
}

export const AllAutoCherryPickStrategies = allCases(AutoCherryPickStrategy);

export enum PRCommentStrategy {
    CreateNew = 'createNew',
    UpdateExisting = 'updateExisting',
}

export interface AutoCherryPickFromWorkingBranchData {
    token?: string;
    strategy: AutoCherryPickStrategy;
    shouldNotifyPRAuthors: boolean;
    allowFixesWithToken: boolean;
    requiresFixApprovalToCherryPick: boolean;
}

export interface AutomationCherryPickFromWorkingBranch extends Automation {
    type: AutomationType.AutoCherryPickFromWorkingBranch;
    data?: AutoCherryPickFromWorkingBranchData;
}

export interface AutomationCherryPickLocalizableStrings extends Automation {
    type: AutomationType.AutoCherryPickLocalizableStrings;
    data: AutomationCherryPickLocalizableStringsData;
}

export type AutomationCherryPickLocalizableStringsData = DirectlyCommittableAutomation;

export interface AutomationSyncTranslations extends Automation {
    type: AutomationType.AutoSyncTranslations;
    data: AutomationSyncTranslationsData;
}

export interface AutomationSyncMetadataTranslations extends Automation {
    type: AutomationType.AutoSyncMetadataTranslations;
    data: AutomationSyncMetadataTranslationsData;
}

export enum AutoSyncTranslationsExportStrategy {
    ToWorkingBranch = 'toWorkingBranch',
    ToReleaseBranch = 'toReleaseBranch',
}

export const AllAutoSyncTranslationsExportStrategies = allCases(AutoSyncTranslationsExportStrategy);

export interface AutomationSyncTranslationsData {
    daysBeforeKickoffForUpload?: number;
    daysBeforeSubmissionForExport?: number;
    exportStrategy?: AutoSyncTranslationsExportStrategy;
}

export interface AutomationSyncMetadataTranslationsData {
    daysBeforeSubmissionForExport?: number;
}

export interface AutomationSyncMetadataFromVCS {
    type: AutomationType.AutoSyncMetadataFromVCS;
    data: AutomationSyncMetadataFromVCSData;
}

export interface AutomationSyncMetadataFromVCSData {
    autoSyncMetadataFromVCSFilePath?: string;
}

export interface AutomationBAAutoUpdatePRWithArtifactLink {
    type: AutomationType.BAAutoUpdatePRWithArtifactLink;
    data: AutomationBAAutoUpdatePRWithArtifactData;
}

export interface AutomationBAAutoUpdatePRWithArtifactData {
    prCommentStrategy?: PRCommentStrategy;
}

export enum AutoSelectLatestBuildStrategy {
    LatestAvailableBuild = 'AutoSelectLatestAvailableBuild',
    LatestPassedRegression = 'AutoSelectLatestPassedRegressionBuild',
}

export interface AutomationAutoSelectLatestBuildData {
    track: string;
    strategy: AutoSelectLatestBuildStrategy;
    regressionStepsToConsider: string[];
    updateTrackReleaseWithPatternedName?: string;
}

export interface AutomationAutoSelectLatestBuild extends Automation {
    type: AutomationType.AutoSelectLatestBuild;
    data: AutomationAutoSelectLatestBuildData;
}

export interface AutomationAssignBetaBuildsData {
    selectedBetaGroupIds?: string[];
}

export interface AutomationAssignBetaBuildGroups extends Automation {
    type: AutomationType.AutoAssignBetaBuilds;
    data: AutomationAssignBetaBuildsData;
}

export interface AutomationBAAssignBetaBuildGroups extends Automation {
    type: AutomationType.BAAutoAssignBetaBuilds;
    data: AutomationAssignBetaBuildsData;
}

export interface AutomationPromoteBuildsToTestTrackData {
    testingTrack: string;
}

export interface AutomationPromoteBuildsToTestTrack extends Automation {
    type: AutomationType.AutoPromoteBuildsToTestTrack;
    data: AutomationPromoteBuildsToTestTrackData;
}

export interface AutoSyncAppleDevicesData {
    autoSyncAppleDevicesPossibleUserGroups: UserGroupId[];
    autoSyncAppleDevicesShouldDisableDevices: boolean;
    autoSyncAppleDevicesShouldIncludeTestDevices: boolean;
}

export interface AutomationAutoSyncAppleDevices extends Automation {
    type: AutomationType.AutoSyncAppleDevices;
    data?: AutoSyncAppleDevicesData;
}

export interface AutoSyncAppleProvisioningProfilesDevicesData {
    autoSyncAppleProvisioningProfilesDevicesProfileTypes: ProvisioningProfileType[];
    autoSyncAppleProvisioningProfilesDevicesUserGroups: UserGroupId[];
    autoSyncAppleProvisioningProfilesDevicesShouldRemoveDevices: boolean;
}

export interface AutomationAutoSyncAppleProvisioningProfilesDevices extends Automation {
    type: AutomationType.AutoSyncAppleProvisioningProfilesDevices;
    data?: AutoSyncAppleProvisioningProfilesDevicesData;
}

export enum AutoSubmitStrategy {
    AsSoonAsPossible = 'asSoonAsPossible',
    AfterTargetDateEvenIfNotReady = 'afterTargetDateEvenIfNotReady',
    OnceStepsComplete = 'onceStepsComplete',
    OnTargetDate = 'onTargetDate',
    OnceStepsCompleteAfterTargetDate = 'onceStepsCompleteAfterTargetDate',
}

export const AutoSubmitStrategiesOnOrAfterTargetDate = [
    AutoSubmitStrategy.OnceStepsCompleteAfterTargetDate,
    AutoSubmitStrategy.AfterTargetDateEvenIfNotReady,
];
export const AllAutoSubmitStrategies = allCases(AutoSubmitStrategy);

export interface AutomationAutoSubmitReleaseData {
    submitStrategy: AutoSubmitStrategy;
    shouldReleaseBetaTrackToFullPercentagePriorToAutoSubmission?: boolean;
    shouldReleasePreviousSubmissionToAllUsersFirst?: boolean;
    shouldPullBlockingNonBinaryItems?: boolean;
    shouldUseCustomRequiredSteps: boolean;
    customRequiredSteps: Record<ReleaseTypesForNonBlockingSteps, ReleaseStepType[]>;
}

export enum AutoReleaseStrategy {
    OnDate = 'AutoReleaseOnDate',
    OnceStepsComplete = 'AutoReleaseOnceStepsComplete',
    OnceStepsCompleteAfterTargetDate = 'AutoReleaseOnceStepsCompleteAfterTargetDate',
}

export const AllAutoReleaseStrategies = allCases(AutoReleaseStrategy);

export interface AutomationAutoReleaseData {
    strategy: AutoReleaseStrategy;
    shouldReleasePreviousSubmissionToAllUsersFirst?: boolean;
    shouldUseCustomRequiredSteps: boolean;
    customRequiredSteps: Record<ReleaseTypesForNonBlockingSteps, ReleaseStepType[]>;
}

export interface AutomationAutoRelease extends Automation {
    type: AutomationType.AutoRelease;
    data: AutomationAutoReleaseData;
}

export interface AutomationAutoSubmitRelease extends Automation {
    type: AutomationType.AutoSubmitRelease;
    data: AutomationAutoSubmitReleaseData;
}

export interface AutomationAutoCreateReleaseChannelsData {
    teamId?: string;
    archiveChannelsWhenReleaseCompleted?: boolean;
    inviteGroups?: UserGroupId[];
    inviteUsers?: string[];
}

export interface AutomationAutoCreateReleaseChannels extends Automation {
    type: AutomationType.AutoCreateReleaseChannels;
    data: AutomationAutoCreateReleaseChannelsData;
}

export interface AutomationAutoSyncSlackPilotUserGroupData {
    userGroupHandle: string;
}

export interface AutomationAutoSyncSlackPilotUserGroup extends Automation {
    type: AutomationType.AutoSyncSlackPilotUserGroup;
    data: AutomationAutoSyncSlackPilotUserGroupData;
}

export interface AppStoreDefaultReleaseNotes {
    id: string;
    locale: string; // ex. en
    displayLocale: string; // ex. English
    whatsNew?: string; // localized whats new default
}

export interface AutoTransitionIssueTrackingTicketsData {
    statuses: string[];
}

export interface AutoTransitionIssueTrackingTickets extends Automation {
    type: AutomationType.AutoTransitionIssueTrackingTickets;
    data?: AutoTransitionIssueTrackingTicketsData;
}

export interface BAAutomationAutoUploadBinaryArtifactToAppStore extends Automation {
    type: AutomationType.BAAutoUploadBinaryArtifactToAppStore;
    data?: AutoUploadBinaryArtifactToAppStoreData;
}

export enum AutoCreateTestRunsStrategy {
    WithTestPlans = 'AutoCreateTestRunsWithTestPlans',
    WithoutTestPlans = 'AutoCreateTestRunsWithoutTestPlans',
}

export interface BrowserStackTestCaseFiltersCustomField {
    name: string;
    value: string[];
}

export interface BrowserStackTestCaseFilters {
    caseTypes: string[];
    customFields: BrowserStackTestCaseFiltersCustomField[];
}

export type TestCaseFilterSettings = BrowserStackTestCaseFilters;

export type TestRunFilterSettings = {
    pattern: string;
    shouldUseTestCaseFilters: boolean;
    testCaseFilters?: TestCaseFilterSettings;
};

export type TestPlanFilterSettings = {
    pattern: string;
    testRunFilters?: TestRunFilterSettings[];
};

export interface AutoCreateTestRunsData {
    strategy: AutoCreateTestRunsStrategy;
    testRunsFilterSettings: TestRunFilterSettings[];
    testPlansFilterSettings: TestPlanFilterSettings[];
}

export interface AutomationAutoCreateTestRuns extends Automation {
    type: AutomationType.AutoCreateTestRuns;
    data?: AutoCreateTestRunsData;
}

export type AutomationTypesWithSettings =
    | AutomationBumpVersion
    | AutomationAutoAddMissingFAs
    | AutomationAutoTagReleases
    | AutomationKickoffRelease
    | AutomationPromoteStablePhasedRelease
    | AutomationHaltUnstableRelease
    | AutomationPrepareRollbackRelease
    | AutomationApplyReviewAttachment
    | AutomationBackmergeReleaseBranches
    | AutomationApplyBetaBuildNotes
    | AutomationSyncAppStoreUsers
    | AutomationApplyMetadata
    | AutomationAutoUploadBinaryArtifactToAppStore
    | AutomationAutoUploadDsymsToStabilityMonitoring
    | AutomationBAAutoTriggerBuildSystemWorkflow
    | AutomationAutoStagedRollout
    | AutomationDistributeReleaseSummary
    | AutomationCherryPickFromWorkingBranch
    | AutomationCherryPickLocalizableStrings
    | AutomationSyncTranslations
    | AutomationSyncMetadataTranslations
    | AutomationAutoSelectLatestBuild
    | AutomationAssignBetaBuildGroups
    | AutomationPromoteBuildsToTestTrack
    | AutomationSyncMetadataFromVCS
    | AutomationAutoSyncAppleDevices
    | AutomationAutoSyncAppleProvisioningProfilesDevices
    | AutomationAutoRelease
    | AutomationAutoSubmitRelease
    | AutomationAutoCreateReleaseChannels
    | AutomationAutoSyncSlackPilotUserGroup
    | AutoTransitionIssueTrackingTickets
    | BAAutomationAutoUploadBinaryArtifactToAppStore
    | AutomationAutoCreateTestRuns;

export interface ReleaseScheduleCadenceSettings {
    scheduleCadence: ReleaseScheduleCadenceStyle;
    cadenceTargetDateSets?: CadenceTargetDateSet[];
    cadenceTimezone?: string;
    allowKickoffWhenPreviousNotComplete?: boolean;
}

export interface CadenceTargetDateSet {
    kickoffDayOfWeek?: DayOfTheWeek;
    kickoffTimeHour?: number;
    kickoffTimeMinute?: number;
    submitDayOfWeek?: DayOfTheWeek;
    submitTimeHour?: number;
    submitTimeMinute?: number;
    releaseDayOfWeek?: DayOfTheWeek;
    releaseTimeHour?: number;
    releaseTimeMinute?: number;
}

export interface LifecycleFreezeSet {
    startDate: string;
    endDate: string;
    timezone: string;
}

export interface FileStats {
    fileName: string;
    fileSize: number; // in bytes
}

export interface RoleSyncData {
    syncEnabled: boolean;
    roles: GooglePlayConsolePermission[] | ASCRole[];
}

export type RolesMap = Record<UserGroupId, Partial<Record<IntegrationCapability, RoleSyncData>>>;

export type UserGroupsToCommsGroupsMap = Record<UserGroupId, string[]>;

export type ProviderIdToUserGroupsToCommsGroupsMap = Record<string, UserGroupsToCommsGroupsMap>;

export interface BumpVersionFile {
    fileName: string;
    currentVersion?: string;
    branch: string;
}

export interface TranslationDirectory {
    name: string;
}

export type WorkflowEnabledSteps = Partial<Record<ReleaseStepType, boolean>>;

export interface AppSettingsBase {
    appName: string;
    platform: AppPlatform;
    appStoreType?: AppStoreType;
    destinations: Destination[];
    appImageUrl: string;
    shouldDefaultToBuildDistro: boolean;
    shouldHideRegisterDevicePrompt: boolean;
}

export interface AppSettings extends AppSettingsBase {
    betaTestingStyle: BetaTestingStyle;
    bumpVersionFiles: BumpVersionFile[];
    translationDirectories: TranslationDirectory[];
    prescribedBetaSoakDurationSeconds?: number; // only non-null for TimeLimitedSoak style
    releaseBranchPatterns: ReleaseBranchPattern[];
    releaseTagPatterns: ReleaseTagPattern[];
    featureAffiliations: FeatureAffiliation[];
    integrations: Integration[];
    stepChecklistItems: ChecklistItemTemplate[];
    groups: Record<UserGroupId, string[]>;
    workingBranch?: string;
    stagingBranch?: string;
    prodBranch?: string;
    usesStaticReleaseBranchForNonHotfixes: boolean;
    isPureTrunkBasedBranchingForNonHotfixes: boolean;
    appStoreUrl?: string;
    appStoreDefaults?: AppStoreDefaultSettings;
    automations: Automation[];
    appStoreDefaultReleaseNotes?: AppStoreDefaultReleaseNotes[];
    releaseScheduleCadenceSettings: ReleaseScheduleCadenceSettings;
    defaultReleaseType: ReleaseType;
    assumePointReleasesAreHotfixes: boolean;
    releasePilotIds: string[]; // deprecated.. use releasePilotRotation for info on the pilot rotation
    notifications: AppNotification[];
    allReleaseStepTypesForApp: ReleaseStepType[];
    requiredReleaseStepTypesForApp: ReleaseStepType[];
    nonBlockingReleaseStepTypes: ReleaseStepType[]; // DEPRECATED
    releaseStepsBlockingConfigForReleaseType: Record<
        ReleaseTypesForNonBlockingSteps,
        Record<ReleaseStepType, ReleaseStepBlockingConfig>
    >;
    shouldHideNonBlockingSteps: boolean;
    latestCompletedVersion?: string;
    latestReleaseTag?: ReleaseTag;
    filterPaths: string[];
    rolesMap: RolesMap;
    monitoringSettings: AppMonitoringSettings;
    customMessagesConfig: CustomMessagesConfig;
    buildDistributionEnabled: boolean;
    signingKeys: SigningKey[];
    releasePilotRotation: ReleasePilotRotation;
    hasCompletedQualitativeOnboardingAtLeastOnce: boolean;
    addFixRequestsToOpenPRsAgainstReleaseBranches: boolean;
    requiresFixRequestsApprovals: boolean;
    requiresFixRequestsDetails: boolean;
    fixRequestsDetailsTemplate: string;
    enableStatusChecksForFixRequests: boolean;
    threadFixNotifications: boolean;
    enableExperimentalAIReleaseNotesFeature: boolean; // DEPRECATED
    storeEmailForwardingAddress: string;
    mergeUserCreatedPRs: boolean;
    workingBranchUserCreatedPRMergeType: MergeType;
    releaseBranchUserCreatedPRMergeType: MergeType;

    bundleSizeDeviceModelId: string;
    ascDeviceModels: { id: string; displayName: string }[];

    // workflows properties
    workflowEnabledSteps: WorkflowEnabledSteps;
    isWorkflowsMigratedAndEnabled: boolean;
    stepGraph: WorkflowStepGraph;
    branchConfigsById: Record<string, BranchConfig>;
    tagConfigsById: Record<string, TagConfig>;
    featureAffiliationConfigsById: Record<string, FeatureAffiliationConfig>;
    destinationInfoByDestinationStepConfigId: Record<string, Destination>;
    use24HourTimeFormat: boolean;
    shouldShowBetaOverlayOnWorkflowsBetaOrgs: boolean;
    lifecycleFreezes?: LifecycleFreezeSet[];
    regressionTestingStrategy?: RegressionTestingStrategy;
}

export interface TagConfig {
    id: string;
    pattern: string;
}

export interface BranchPattern {
    pattern: string;
    isHotfix: boolean;
}

export interface BranchConfig {
    id: string;
    workingBranch: Nullable<string>;
    stagingBranch: Nullable<string>;
    prodBranch: Nullable<string>;
    releaseBranchPatterns: BranchPattern[];
}

export interface FeatureAffiliationConfig {
    id: string;
    pattern: string;
    integrationId: IntegrationType;
    type: FeatureAffiliationType;
}

export type AppNotification = CommonNotification;

export enum BuildDistroNotificationType {
    NewBuildsAvailable = 'NEW_BUILDS_AVAILABLE',
}

export enum NotificationDestination {
    Slack = 'SLACK',
    Email = 'EMAIL',
}

export enum AppNotificationType {
    TargetKickoffReminder = 'TARGET_KICKOFF_REMINDER',
    ReleaseKickoff = 'RELEASE_KICKOFF',
    ReleaseKickoffFailed = 'RELEASE_KICKOFF_FAILED',
    NextUpPilotReminder = 'NEXT_UP_PILOT_REMINDER',
    PauseResumeScheduleCadence = 'PAUSE_RESUME_SCHEDULE_CADENCE',
    VcsDistributionPublished = 'VCS_DISTRIBUTION_PUBLISHED',
    ManualSubmitToAppStore = 'MANUAL_SUBMIT_TO_APP_STORE',
    TargetSubmitReminder = 'TARGET_SUBMIT_REMINDER',
    AutoSubmitToAppStore = 'AUTO_SUBMIT_TO_APP_STORE',
    AutoSubmitFailedIncompleteSteps = 'AUTO_SUBMIT_FAILED_INCOMPLETE_STEPS',
    AutoSubmitFailed = 'AUTO_SUBMIT_FAILED',
    AppStoreStatusUpdate = 'APP_STORE_STATUS_UPDATE',
    TargetReleaseReminder = 'TARGET_RELEASE_REMINDER',
    ManualReleaseToAppStore = 'MANUAL_RELEASE_TO_APP_STORE',
    AutoReleaseToAppStore = 'AUTO_RELEASE_TO_APP_STORE',
    AutoReleaseFailedIncompleteSteps = 'AUTO_RELEASE_FAILED_INCOMPLETE_STEPS',
    AutoReleaseFailed = 'AUTO_RELEASE_FAILED',
    AppStorePhasedReleaseUpdate = 'APP_STORE_PHASED_RELEASE_UPDATE',
    AppStorePhasedReleaseComplete = 'APP_STORE_PHASED_RELEASE_COMPLETE',
    UnstablePhasedReleaseHalted = 'UNSTABLE_PHASED_RELEASE_HALTED',
    StablePhasedReleasePromoted = 'STABLE_PHASED_RELEASE_PROMOTED',
    ReleaseComplete = 'RELEASE_COMPLETE',
    RollbackReleaseManuallyCreated = 'ROLLBACK_RELEASE_MANUALLY_CREATED',
    DailyUserReviewsDigest = 'DAILY_USER_REVIEWS_DIGEST',
    IndividualReview = 'INDIVIDUAL_REVIEW',
    WeeklyUserReviewsIssuesDigest = 'WEEKLY_USER_REVIEWS_ISSUES_DIGEST',
    ReleaseAnnouncement = 'RELEASE_ANNOUNCEMENT',
    ChecklistItemIncomplete = 'CHECKLIST_ITEM_INCOMPLETE',
    ChecklistItemApproved = 'CHECKLIST_ITEM_APPROVED',
    ChecklistItemComment = 'CHECKLIST_ITEM_COMMENT',
    ChecklistItemsReminder = 'CHECKLIST_ITEMS_REMINDER',
    RegressionItemsReminder = 'REGRESSION_ITEMS_REMINDER',
    ApprovalItemsReminder = 'APPROVAL_ITEMS_REMINDER',
    ChecklistItemOwnerChanged = 'CHECKLIST_ITEM_OWNER_CHANGED',
    BetaReadyForInternalTesting = 'BETA_READY_FOR_INTERNAL_TESTING',
    BetaSubmittedForReview = 'BETA_SUBMITTED_FOR_REVIEW',
    BetaReadyForExternalTesting = 'BETA_READY_FOR_EXTERNAL_TESTING',
    BetaSoakStart = 'BETA_SOAK_START',
    BetaSoakEnd = 'BETA_SOAK_END',
    BetaSoakManualEnd = 'BETA_SOAK_MANUAL_END',
    BetaBuildAvail = 'BETA_BUILD_AVAIL',
    RegStatusUpdate = 'REG_STATUS_UPDATE',
    BuildStarted = 'BUILD_STARTED',
    BuildStopped = 'BUILD_STOPPED',
    BuildFailed = 'BUILD_FAILED',
    BuildAvail = 'BUILD_AVAIL',
    BuildAvailArtifacts = 'BUILD_AVAIL_ARTIFACTS',
    BuildInstallationLink = 'BUILD_INSTALLATION_LINK',
    ProductionWorkflowSucceeded = 'PRODUCTION_WORKFLOW_SUCCEEDED',
    StepApproval = 'STEP_APPROVAL',
    FrNotReady = 'FR_NOT_READY',
    FaApplied = 'FA_APPLIED',
    FrReady = 'FR_READY',
    MetadataEditable = 'METADATA_EDITABLE',
    MetaDataApplied = 'METADATA_APPLIED',
    ReleaseTagged = 'RELEASE_TAGGED',
    ReleaseAutotagFail = 'RELEASE_AUTOTAG_FAIL',
    SetSelectedBuildAppStore = 'SET_SELECTED_BUILD_APP_STORE',
    HealthMetricsWarning = 'HEALTH_METRICS_WARNING',
    AutoMergeFailure = 'AUTO_MERGE_FAILURE',
    AutoUploadBuildToAppStoreSucceeded = 'AUTO_UPLOAD_BUILD_TO_APPSTORE_SUCCEEDED',
    AutoUploadBuildToAppStoreFailed = 'AUTO_UPLOAD_BUILD_TO_APPSTORE_FAILED',
    FeatureFlagToggled = 'FEATURE_FLAG_TOGGLED',
    ReleaseWentLiveGooglePlayParsedEmail = 'RELEASE_WENT_LIVE_GOOGLE_PLAY_PARSED_EMAIL',
    ASCMissingOrExpiredAgreements = 'ASC_MISSING_EXPIRED_AGREEMENTS',
    ForwardedAppStoreEmail = 'FORWARDED_APP_STORE_EMAIL',
    NoteExpiringAppleProfilesCertificates = 'EXPIRING_APPLE_PROFILES_CERTIFICATES',
    ChecklistItemReminder = 'CHECKLIST_ITEM_REMINDER',
    RegressionBuildUpdated = 'REG_BUILD_UPDATE',
    RegressionStatusUpdated = 'REG_STATUS_UPDATE',
    BulkApproveChecklistItems = 'BULK_APPROVE_CHECKLIST_ITEMS',
    BulkChecklistItemsReminder = 'BULK_CHECKLIST_ITEMS_REMINDER',
    BundleSizeIncrease = 'BUNDLE_SIZE_INCREASE',
    CherryPickFixRequest = 'CHERRY_PICK_FIX_REQUEST',
}

export enum AppNotificationCategory {
    ReleaseCycle = 'Release cycle',
    AppReviews = 'App reviews',
    ReleaseAnnouncement = 'Release announcement',
    ChecklistItems = 'Checklist items',
    Testing = 'Testing',
    Builds = 'Builds',
    ReleaseProcessTasks = 'Release process tasks',
}

export interface AppGroup {
    id: string;
    name: string;
    appIds: string[];
    appGroupImageUrl: string;
    useAppImage: boolean;
}

export interface NewVersionValidationResult {
    validationError?: string;
    versionStringForPossibleBump: string;
    branchName: string;
    hotfixBase?: string;
    hotfixPreviousVersion?: string;
    isStaticBranch: boolean;
    releasePilotFromRotation?: string;
    previousTags?: ReleaseTag[];
    isReleasePilotFromRotationScheduleIntegration: boolean;
    latestCompletedReleaseVersion?: string;
}

export enum SSOConnectionState {
    Active = 'active',
    Inactive = 'inactive',
}

export interface SSOConnection {
    name?: string;
    state: SSOConnectionState;
    updatedAt?: string;
}

export type OrgUser = User;

export enum SubscriptionFeatureType {
    Webhooks = 'webhooks',
    PublicAPI = 'publicAPI',
    ApprovalsStep = 'approvalsStep',
    CLIOwnership = 'cliOwnership',
    ReleasePilots = 'releasePilots',
    Rollouts = 'rollouts',
    OrgLevelMetrics = 'orgLevelMetrics',
    DevOpsMetrics = 'devOpsMetrics',
    RoleBasedACL = 'roleBasedACL',
    SAML = 'saml',
    ChatSupport = 'chatSupport',
    Rollbacks = 'rollbacks',
    ReviewIssues = 'reviewsAnalysis',
    ReviewTranslation = 'reviewsTranslation',
    CustomGroups = 'customACLGroups',

    // capacity-controlled features
    AddApps = 'addApps',
    WriteUsers = 'writeUsers',
    ReleaseHistory = 'releaseHistory',
    FeatureCLI = 'cli',
    InsightsHistory = 'insightsHistory',
}

export enum SubscriptionStatus {
    Free = 'free',
    Paywall = 'paywall',
    CustomerTeam = 'customerTeam',
    CustomerBusiness = 'customerBusiness',
    CustomerEnterprise = 'customerEnterprise',
    CustomerTeamBD = 'customerTeamBD',
    CustomerBusinessBD = 'customerBusinessBD',
    CustomerEnterpriseBD = 'customerEnterpriseBD',
}

export interface DisabledOrLimitedSubscriptionFeature {
    feature: SubscriptionFeatureType;
    isDisabled: boolean;
    limit?: number;
    limitRemaining?: number;
}

export interface DisabledIntegration {
    integrationCapability: IntegrationCapability;
}

export interface DisabledAutomation {
    automationType: AutomationType;
}

export interface SubscriptionFeaturesAndCapabilities {
    disabledAutomationsMap: Partial<Record<AutomationType, DisabledAutomation>>;
    disabledIntegrationCapabilitiesMap: Partial<Record<IntegrationCapability, DisabledIntegration>>;
    disabledOrLimitedFeaturesMap: Partial<Record<SubscriptionFeatureType, DisabledOrLimitedSubscriptionFeature>>;
}

export interface OrgSubscriptionInfo {
    shouldShowTrialBanner: boolean;
    isTrialStarted: boolean;
    isTrialEnded: boolean;
    trialDaysRemaining?: number;
    trialType: SubscriptionStatus;
    subscriptionStatus: SubscriptionStatus;
    currentFeaturesAndCapabilities: SubscriptionFeaturesAndCapabilities;
    featuresAndCapabilitiesPerSubscriptionStatus: Record<SubscriptionStatus, SubscriptionFeaturesAndCapabilities>;
}

export interface SsoSamlConfig {
    ssoConnection?: SSOConnection;
    ssoSamlEnabled: boolean;
    ssoSamlEnforced: boolean;
    ssoDirectory?: SSODirectory;
    directorySyncUserGroupsForGroups: DirectorySyncUserGroupsForGroup[];
    isUsingDirectorySyncDefinedApps: boolean;
}

export interface UserGroup {
    id: UserGroupId;
    name: string;
    actions: RunwayUserAction[];
    members: string[];
    defaultActions?: RunwayUserAction[];
    grantAdminToGroupUsers: boolean;
    grantItAdminToGroupUsers: boolean;
}

export interface OrgSettings {
    webhookPortalUrl?: string;
    webhooks: WebhookEndpoint[]; // all existing webhooks endpoints for the app
    webhookEventTypes: WebhookEventType[]; // all possible webhook event types for the app
    isACLEnabled: boolean;
    groups: Record<UserGroupId, UserGroup>;
    providerIdToUserGroupsToCommsGroupsMap: ProviderIdToUserGroupsToCommsGroupsMap; // Migrated from app.Configs.userRolesToCommsGroupMap (latter is now defunct)
    isUsingAdHocGroups: boolean; // uses ad-hoc groups mapping to Runway group ids
}

export interface PublicAPIKey {
    id: string;
    keyValue: string;
    name: string;
    createdAt: string;
    createdBy: string;
    scopes: string[];
    expiration: string;
    deactivatedAt: string;
    appIds: string[];
    groups: UserGroupId[];
    isAdmin: boolean;
}

export interface Key {
    id: string;
    name: string;
    createdAt: string;
    createdBy: string;
    lastUsedAt?: string;
}

export interface SSODirectory {
    id: string;
    name: string;
    domains: string[];
    type: string;
    isActive: boolean;
}

export interface DirectorySyncGroup {
    id: string;
    name: string;
}

export interface DirectorySyncUserGroupsForGroup {
    group: DirectorySyncGroup;
    userGroups: UserGroupId[];
}

export interface UserGroupsForDirectoySyncGroup {
    group: DirectorySyncGroup;
    userGroups: UserGroup[];
}

export interface ClientScriptsConfig {
    logrocketDisabled?: boolean;
    userflowDisabled?: boolean;
    ga4Disabled?: boolean;
    gtmDisabled?: boolean;
    fbPixelDisabled?: boolean;
    redditDisabled?: boolean;
    hubspotDisabled?: boolean;
    linkedinDisabled?: boolean;
}

export interface OrganizationEntity {
    id: string; // database id
    orgId: string; // customer-facing id
    name: string;
    createdAt: string;
    orgImageUrl: string;
    users: OrgUser[];
    integrationProviders: IntegrationProvider[];
    allowedDomains: string[];
    mergeAccountsWithAllowedDomainEnabled: boolean;
    addNewUsersToAppsSettingEnabled: boolean;
    timezone: string;
    disableAIForAppStoreReviews: boolean;
    disableAIForReleaseNotes: boolean;
    disableAIForOrgInsights: boolean;
    isEnabledAIReleaseNotesFeature: boolean;
    subscriptionInfo: OrgSubscriptionInfo;
    orgSettings: OrgSettings;
    ssoSamlConfig: SsoSamlConfig;
    apiKeys: PublicAPIKey[];
    usePermissionsOnGroupLevel: boolean;
    clientScriptsConfig?: ClientScriptsConfig;
}

export interface Organization extends OrganizationEntity {
    apps: AppList[];
    appGroups: AppGroup[];
}

export type UserGroupId = string;

export enum DefaultUserGroupId {
    Pilot = 'pilot',
    Engineer = 'engineer',
    ProductManager = 'pm',
    QA = 'qa',
    Designer = 'design',
    EngManager = 'em',
    Marketing = 'marketing',
    CX = 'cx',
    Ops = 'ops',
    Approver = 'approver',
}

export const AllDefaultUserGroupIds = allCases(DefaultUserGroupId);

export enum GooglePlayConsolePermission {
    CanAccessApp = 'CAN_ACCESS_APP',
    ViewFinancial = 'CAN_VIEW_FINANCIAL_DATA',
    ManagePerms = 'CAN_MANAGE_PERMISSIONS',
    ReplyReviews = 'CAN_REPLY_TO_REVIEWS',
    ManageAPKs = 'CAN_MANAGE_PUBLIC_APKS',
    ManageTrackAPKs = 'CAN_MANAGE_TRACK_APKS',
    ManageTrackUsers = 'CAN_MANAGE_TRACK_USERS',
    ManagePublicListing = 'CAN_MANAGE_PUBLIC_LISTING',
    ManageDraftApps = 'CAN_MANAGE_DRAFT_APPS',
    ManageOrders = 'CAN_MANAGE_ORDERS',
    // Not relevant
    // Unspecified = 'APP_LEVEL_PERMISSION_UNSPECIFIED',
}

export enum ASCRole {
    AccountHolder = 'ACCOUNT_HOLDER',
    Admin = 'ADMIN',
    AppManager = 'APP_MANAGER',
    Developer = 'DEVELOPER',
    Finance = 'FINANCE',
    Marketing = 'MARKETING',
    Sales = 'SALES',
    CustomerSupport = 'CUSTOMER_SUPPORT',
    // Uncommon roles/perms
    // AccessToReports = 'ACCESS_TO_REPORTS',
    // CloudManagedAppDistribution = 'CLOUD_MANAGED_APP_DISTRIBUTION',
    // CloudManagedDeveloperID = 'CLOUD_MANAGED_DEVELOPER_ID',
    // CreateApps = 'CREATE_APPS',
    // ImageManager = 'IMAGE_MANAGER',
}

export enum ChecklistItemType {
    Binary = 'binary',
    Multi = 'multi',
}

export enum ChecklistItemStatusType {
    Approved = 'approved',
    Rejected = 'rejected',
    InProgress = 'inProgress',
    Blocked = 'blocked',
    Passed = 'passed',
    Failed = 'failed',
}

export enum ChecklistItemPlacement {
    StepChecklist = 'stepChecklist',
    Approvals = 'approvals',
    RegressionTesting = 'regressionTesting',
}

export enum ChecklistItemReleaseTypes {
    All = 'all',
    HotfixOnly = 'hotfixOnly',
    RollbackOnly = 'rollbackOnly',
    AllExceptHotfixAndRollback = 'allExceptHotfix',
}

export interface ChecklistItemTemplate {
    id: string;
    uid: string;
    title: string;
    description?: string;
    approverGroups: UserGroupId[];
    releaseTypes: ChecklistItemReleaseTypes;
    notifyEnabled: boolean;
    type: ChecklistItemType; // e.g. binary, multi
    releaseStepType?: ReleaseStepType;
    oneOffForVersion?: string;
    owners: string[];
    stepConfigId?: string;
    legacyAppId?: string;
    createdAt: string;
    targetCompletion?: RelativeTimeTrigger;
    incompleteReminderEnabled: boolean;
    autocompleteAtTargetCompletionEnabled: boolean;
    includeDescriptionInNotificationsEnabled: boolean;
    disableReminderAfterReleaseComplete: boolean;
    orgTemplateId: string;
    shouldApplyToAllAppsAndPlatforms?: boolean;
    apps?: string[];
    platforms?: AppPlatform[];
    channels?: string[];
}

export interface ChecklistItemStatus {
    status: ChecklistItemStatusType;
    createdBy: string; // id for user who added this status
    createdAt: string; // date string
}

export interface ChecklistItemComment {
    comment: string;
    createdBy: string; // id for user who added this comment
    createdAt: string; // date string
}

export interface ChecklistItem {
    id: string;
    uid: string;
    title: string;
    description?: string;
    approverGroups: UserGroupId[];
    releaseTypes: ChecklistItemReleaseTypes;
    notifyEnabled: boolean;
    type: ChecklistItemType; // e.g. binary, multi
    statuses: ChecklistItemStatusType[]; // e.g. Approved, Rejected, In Progress, Blocked etc.
    releaseStepType?: ReleaseStepType;
    oneOffForVersion?: string;
    owners: string[];
    createdAt: string;
    stepRunId?: string;
    templateId?: string;
    status?: ChecklistItemStatus;
    comment?: ChecklistItemComment;
    resolvedTargetCompletionDate?: string;
    targetCompletion?: RelativeTimeTrigger;
    incompleteReminderEnabled: boolean;
    autocompleteAtTargetCompletionEnabled: boolean;
    includeDescriptionInNotificationsEnabled: boolean;
    disableReminderAfterReleaseComplete: boolean;
    orgTemplateId: string;
    channels?: string[];
}

export type StepRunChecklistItemModel = {
    stepRun: StepRun;
    filteredChecklistItems: ChecklistItem[];
};

export interface SSORedirectResponse {
    authorizationURL: string;
}

export interface SSOAdminPortalResponse {
    portalURL: string;
}

export interface CandidateSelectableCommits {
    version: string;
    commits: SelectableCommit[];
}

export interface RepoContributors {
    contributors: VCSUserData[];
}

export type CreateUserResponse = {
    id: number;
    name: string;
};

export interface AuthCredentials {
    token: string;
    expiry: number; // as a unix timestamp
    userId: string;
    userEmail: string;
}

export interface AuthStore {
    userId: string;
    isLoggedIn: boolean;
    twoFactorAuthRequired: boolean;
    isEmailVerified: boolean;
    userflowIdentitySignature?: string;
    authCredentials?: AuthCredentials;
    authRedirectUrl?: string;
}

export enum Environment {
    Development = 'development',
    Production = 'production',
}

export enum WebsocketMessageType {
    StateUpdate = 'stateUpdate',
    Notification = 'notification',
    NewState = 'newState',
}

export enum ModelType {
    App = 'app',
    Org = 'org',
    Release = 'release',
    AppGroup = 'appGroup',
    RolloutMonitoring = 'rolloutMonitoring',
    FeatureFlags = 'featureFlags',
    BuildDistroBucket = 'buildDistroBucket',
    BuildDistroBuild = 'buildDistroBuild',
    ReleaseStep = 'releaseStep',
    StepRun = 'stepRun',
}

export const AllStateUpdateEventModelTypes = allCases(ModelType);

type WebsocketMessageModelType =
    | Release
    | AppEntity
    | OrganizationEntity
    | AppGroup
    | TimelineEvent[]
    | TimelineEvent
    | FeatureFlag[]
    | BuildDistroBucketBase
    | BuildDistroBuild
    | ReleaseStep
    | StepRun
    | RolloutMonitoring
    | TimelineEventsHistoryPaginatedResponse;

export interface WebsocketStateUpdateMessageData {
    modelType: ModelType;
    appIdParam?: string;
    orgIdParam: string;
    appId?: string;
    appGroupId?: string;
    orgId: string;
    prevReleaseId?: string;
    deletedReleaseId?: string;
    deletedAppId?: string;
    deletedAppGroupId?: string;
    releaseId?: string;
    model: WebsocketMessageModelType;
}

export interface Notification {
    title: string;
    message: string;
}

export interface WebsocketEvent {
    version: string; // the version of the server that sent the update
    messageType: WebsocketMessageType;
    data: Notification | WebsocketStateUpdateMessageData | DashboardState;
}

export interface WebsocketEventContainer {
    updates: WebsocketEvent[];
}

export interface WebsocketStateUpdateEvent extends WebsocketEvent {
    data: WebsocketStateUpdateMessageData;
}

export interface WebsocketNotificationEvent extends WebsocketEvent {
    data: Notification;
}

export interface WebsocketNewStateEvent extends WebsocketEvent {
    data: DashboardState;
}

// App Home Models

export interface ReleaseSummary {
    id: string;
    releaseId: string; // This is the user facing version (ex "3.990" not "tab:3.99.0")
    version: string;
    displayableVersion: string;
    releaseName?: string;
    releasePilotId?: string;
    targetKickoffDate?: string;
    targetSubmitDate?: string;
    targetReleaseDate?: string;
    lifecycleAutomations: AutomationRun[];
}

export interface ReviewDeviceData {
    productName?: string;
}

export interface RecentReview {
    author: string;
    version: string;
    rating: string;
    id: string;
    title: string;
    titleTranslated?: string;
    content: string;
    contentTranslated?: string;
    fetchedAt: string;
    submittedAt: string;
    androidUserOSVersion?: string;
    userDeviceData?: ReviewDeviceData;
}

export interface AppStoreReviewsIssue {
    description: string;
    examples: RecentReview[];
    isNew: boolean;
}

export interface AppStoreReviewData {
    averageRating: number;
    totalRatingsCount: number;
    recentReviews: RecentReview[];
    viewAllReviewsUrl: string;
    issues: AppStoreReviewsIssue[];
}

export enum OverviewSection {
    Highlights = 'Highlights',
    ProductSprint = 'ProductSprint',
    Code = 'Code',
}

export interface OverviewCodeContributor {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    avatar: string;
    itemOfWorkCount: number;
}

export interface OverviewContributingTeam {
    name: string;
    itemOfWorkCount: number;
}

export interface ChangedFile {
    fileName: string;
    linesChangedCount: number;
    isConfigFile: boolean;
}

export enum StatChangeStatus {
    Good = 'Good',
    Neutral = 'Neutral',
    Bad = 'Bad',
}

export enum StatisticType {
    TargetDateSuccessRate = 'TargetDateSuccessRate',
    TargetDateSuccessRateKickoff = 'TargetDateSuccessRateKickoff',
    TargetDateSuccessRateSubmission = 'TargetDateSuccessRateSubmission',
    TargetDateSuccessRateRelease = 'TargetDateSuccessRateRelease',
    CIWorkflowSuccessPercent = 'CIWorkflowSuccessPercent',
    CIAverageBuildTime = 'CIAverageBuildTime',
    CIBuildsCount = 'CIBuildsCount',
    CommitCount = 'CommitCount',
    LinesOfCodeChanged = 'LinesOfCodeChanged',
    LinesOfCodeAdded = 'LinesOfCodeAdded',
    LinesOfCodeDeleted = 'LinesOfCodeDeleted',
    LinesOfCodeAddedForHotfixes = 'LinesOfCodeAddedForHotfixes',
    LinesOfCodeChangedForHotfixes = 'LinesOfCodeChangedForHotfixes',
    LinesOfCodeDeletedForHotfixes = 'LinesOfCodeDeletedForHotfixes',
    FilesChangedCount = 'FilesChangedCount',
    ReleaseDuration = 'ReleaseDuration',
    WaitingForReviewDuration = 'WaitingForReviewDuration',
    InReviewDuration = 'InReviewDuration',
    ItemsCompleted = 'ItemsCompleted',
    FixesCount = 'FixesCount',
    FixRequestsCount = 'FixRequestsCount',
    FixRequestsApproved = 'FixRequestsAcceptedCount',
    FixRequestsRejected = 'FixRequestsRejectedCount',
    PercentWorkItemsInFixRequests = 'PercentCompletedWorkItemsThatAreFixes',
    AppStoreBuildCount = 'AppStoreBuildCount',
    CrashFreeUsers = 'AppStoreCrashFreeUsers',
    CrashFreeSessions = 'AppStoreCrashFreeSessions',
    BetaCrashFreeUsers = 'BetaCrashFreeUsers',
    BetaCrashFreeSessions = 'BetaCrashFreeSessions',
    AdoptionRate = 'AdoptionRate',
    StatTotalAutomationsCount = 'StatTotalAutomationsCount',
    StatTotalTimeSavedFromAutomations = 'StatTotalTimeSavedFromAutomations',
    StatAverageAutomationsCount = 'StatAverageAutomationsCount',
    AppStoreBuildBundleDownloadSize = 'AppStoreBuildBundleDownloadSize',
    AppStoreBuildBundleInstallSize = 'AppStoreBuildBundleInstallSize',
    StatAverageTimeSavedFromAutomations = 'StatAverageTimeSavedFromAutomations',
    StatAverageCustomerRating = 'StatAverageCustomerRating',
    TotalHotfixesCount = 'TotalHotfixesCount',
    HotfixReleaseDuration = 'HotfixReleaseDuration',
    StatAverageAppCustomerRating = 'StatAverageAppCustomerRating',

    // OBAA - Must match ObservabilityAndAnalyticsMetricType in observability_and_analytics.go
    AverageEventsPerDailyActiveUser = 'AverageEventsPerDailyActiveUser',
    AverageEventsPerSession = 'AverageEventsPerSession',

    AveragePropertyValues = 'AveragePropertyValues',
    MedianPropertyValues = 'MedianPropertyValues',
    P90PropertyValues = 'P90PropertyValues',
    P95PropertyValues = 'P95PropertyValues',
    P99PropertyValues = 'P99PropertyValues',
    RawValue = 'RawValue',
    Composed = 'Composed',

    // devops
    StatReleaseDurations = 'ReleaseDurations',
    StatAverageTimeToRecover = 'AverageTimeToRecover',
    StatFailureRate = 'FailureRate',
    StatAverageCLIApprovalTime = 'AverageCLIApprovalTime',
    StatAverageReleaseStepCompletionTime = 'AverageReleaseStepCompletionTime',
    AverageHotfixStepCompletionTime = 'AverageHotfixStepCompletionTime',
    StatReleaseFrequency = 'ReleaseFrequency',

    //deprecated stats
    StatTotalCostSavingsFromAutomations = 'StatTotalCostSavingFromAutomations',
    StatAverageCostSavingsFromAutomations = 'StatAverageCostSavingFromAutomations',
}

export type ExtendedStatisticType = StatisticType | `${StatisticType}.${string}`;

export enum StatisticUnit {
    Ratio = 'ratio',
    Count = 'count',
    Megabytes = 'megabytes',
    Percent = 'percent',
    Seconds = 'seconds',
    Cents = 'moneyCents',
    Cadence = 'cadence',
    Days = 'days',
    Rating = 'rating', // User rating, displayed with one decimal point
}

export enum StatisticScope {
    App = 'App',
    AppGroup = 'AppGroup',
    Org = 'Org',
    Release = 'Release',
}

export interface StatisticAdditionalData {
    eventName?: string;
    integrationId?: IntegrationType;
}

export interface Statistic {
    average: number;
    change: number;
    changeStatus: StatChangeStatus;
    category: StatisticCategory;
    type: StatisticType;
    trend?: number;
    unit: StatisticUnit;
    value: number;
    secondaryValue?: number; // used to calculate ratios
    additionalData?: StatisticAdditionalData;
}

export interface StatisticWithAssociatedInfo extends Statistic {
    releaseVersion?: string;
    completedAtOrReleasedAtTimestamp?: number;
    appName?: string;
}

export interface ReleaseAdoption {
    releaseId: string;
    adoptionRate: number;
    externalVersionString: string;
    buildIdentifier?: string;
}

export interface ReleaseConfidenceScore {
    value: number;
    displayString: string;
}

export interface ReleaseOverviewStats {
    codeContributors: OverviewCodeContributor[];
    contributingTeams: OverviewContributingTeam[];
    topChangedFiles: ChangedFile[];
    totalFilesChanged: number;
    previousReleasesAdoptionRate?: ReleaseAdoption[];
    averageAppStoreCrashFreeSessions: number;
    averageAppStoreCrashFreeUsers: number;
    averageBetaCrashFreeSessions: number;
    averageBetaCrashFreeUsers: number;
    totalAutomationsPerformedCount: number;
    totalAutomationsScheduledCount: number;
    estimatedAutomationsTimeSaved: number; // in seconds
    estimatedAutomationsCostSaving: number; // in cents;
    releaseConfidenceScore?: ReleaseConfidenceScore;
}

export interface OrgOverviewApprovalItemDurations {
    averageApprovalItemDurations: OverviewApprovalItemDuration[];
    numReleases: number;
}

export interface OverviewApprovalItemDuration {
    approvalItemId: string;
    approvalItemTitle: string;
    averageDuration: number;
}

export interface HotfixReleaseStatistics {
    id: string;
    completedAtOrReleasedAtTimestamp: number;
    hotfixReleaseDurationStat: Statistic;
    hotfixTimeToStartDurationStat: Statistic;
}

export interface ReleaseStatistics {
    id: string;
    completedAtOrReleasedAtTimestamp: number;
    statistics: Statistic[];
    allBuildsStabilityInfo: StabilityInfo[];
    allBetaBuildsStabilityInfo: StabilityInfo[];
    totalAutomationsPerformedCount: number;
    estimatedAutomationsCostSaving: number;
    estimatedAutomationsTimeSaved: number;
}

export interface ReleaseDurations {
    waitTimeInSeconds: number;
    workTimeInSeconds: number;
    deployTimeInSeconds: number;
    totalTimeInSeconds: number;
}

export interface ReleaseStepDuration {
    releaseStepType: ReleaseStepType;
    duration: number;
}

export interface ReleaseStepDurations {
    durations: ReleaseStepDuration[];
    totalDuration: number;
}

export enum TargetDateSuccessRateStepKey {
    KickoffSuccessRate = 'kickoffSuccessRate',
    SubmissionSuccessRate = 'submissionSuccessRate',
    ReleaseSuccessRate = 'releaseSuccessRate',
}

export type TargetDateSuccessRate = Record<TargetDateSuccessRateStepKey, number>;

export interface StatisticSampleBucketDataPoint {
    appId?: string;
    destinationId?: string;
    value: number | ReleaseDurations | ReleaseStepDurations | TargetDateSuccessRate;
    sampleStartedAt: string;
    sampleEndedAt: string;
}

export enum StatisticDataType {
    TimeSeries = 'timeSeries',
    SampleBuckets = 'sampleBuckets',
}

export type DataPointType = StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint;

export enum TrendStatus {
    Good = 'trendGood',
    Neutral = 'trendNeutral',
    Bad = 'trendBad',
}

export interface OrgOverviewStatistic {
    type: StatisticType;
    category: StatisticCategory;
    dataType: StatisticDataType;
    displayText: string;
    description: string;
    unit?: StatisticUnit;
    value?: number;
    benchmarkValue?: number;
    dataPoints: DataPointType[];
    trend?: number;
    trendStatus?: TrendStatus;
}

export interface OrgOverviewAvailableMetric {
    type: StatisticType;
    category: StatisticCategory;
    displayName: string;
    description: string;
}

export interface OrgOverviewAvailableMetricsResponse {
    metrics: OrgOverviewAvailableMetric[];
}

export interface OrgOverviewV2 {
    statistics: OrgOverviewStatistic[];
    liveRelease?: CompletedReleaseSummary;
    nextRelease?: NextReleaseSummary;
    averageApprovalItemDurations?: OrgOverviewApprovalItemDurations;
}

export interface CompletedReleaseSummary extends ReleaseSummary {
    type: ReleaseType;
    status: ReleaseStatus;
    releaseDate: string;
    releaseDuration?: number;
    appStoreReviewData?: AppStoreReviewData;
    appStoreState?: AppStoreState;
    phasedRelease?: PhasedRelease;
    appStoreBuilds: AppStoreAvailableBuild[];
    rolloutMonitoring: RolloutMonitoring;
    completedAt?: string;
    skippedAt?: string;
    submittedAt?: string;
    isKickedOff: boolean;
    featuresStep: CompactFeaturesStepData;
    steps: ReleaseStep[];
    releaseSummaryMessage?: string;
    stats: Statistic[];
}

export interface NextReleaseSummary extends ReleaseSummary {
    type: ReleaseType;
    events: TimelineEvent[];
    percentFeatureComplete?: number;
    planningSummary?: string;
    isKickedOff: boolean;
    isSubmitted: boolean;
    kickedOffAt?: string;
    submittedAt?: string;
    featuresStep: CompactFeaturesStepData;
    steps: ReleaseStep[];
    appStoreBuilds: AppStoreAvailableBuild[];
    phasedRelease?: PhasedRelease;
    stats: Statistic[];
}

export interface UpcomingReleaseSummary extends ReleaseSummary {
    type: ReleaseType;
    planningSummary?: string;
    isKickedOff: boolean;
    isSubmitted: boolean;
    events: TimelineEvent[];
    phasedRelease?: PhasedRelease;
    kickedOffAt?: string;
    featuresStep: CompactFeaturesStepData;
    steps: ReleaseStep[];
}

export interface AppHome {
    liveRelease?: CompletedReleaseSummary;
    nextRelease?: NextReleaseSummary;
    upcomingReleases?: UpcomingReleaseSummary[];
    pastReleases?: CompletedReleaseSummary[];
}

export enum ReleaseScheduleCadenceStyle {
    Weekly = 'weekly',
    Biweekly = 'biweekly',
    ThreeWeeks = 'threeWeeks',
    Monthly = 'monthly',
    SixWeeks = 'sixWeeks',
    Never = 'never', // disabled
}
export interface AppInfo {
    name: string;
    platform: AppPlatform;
    appStoreType: AppStoreType;
}

export const AllReleaseScheduleCadenceStyles = allCases(ReleaseScheduleCadenceStyle);

export enum DayOfTheWeek {
    Monday = 'monday',
    Tuesday = 'tuesday',
    Wednesday = 'wednesday',
    Thursday = 'thursday',
    Friday = 'friday',
    Saturday = 'saturday',
    Sunday = 'sunday',
}

export const AllDaysOfTheWeek = [
    DayOfTheWeek.Monday,
    DayOfTheWeek.Tuesday,
    DayOfTheWeek.Wednesday,
    DayOfTheWeek.Thursday,
    DayOfTheWeek.Friday,
    DayOfTheWeek.Saturday,
    DayOfTheWeek.Sunday,
];

export enum ReleaseMethod {
    Manual = 'releaseMethodManual',
    AutomaticOnApproval = 'releaseMethodAutomaticOnApproval',
    AutomaticOnDate = 'releaseMethodAutomaticOnDate',
}

export interface AppStoreMinimalApp {
    appName: string;
    appId: string;
    id?: number;
    appPlatform: AppPlatform;
    appIconUrl?: string;
}

export enum ForgotPasswordError {
    General = 'general',
    CodeNotVerified = 'codeUnverified',
    Unauthorized = 'unauthorized',
}

export enum SettingType {
    General = 'general',
    CustomStrings = 'customStrings',
    Integrations = 'integrations',
    Automations = 'automations',
    Schedule = 'schedule',
    AppStore = 'appStore',
    SetupTasks = 'setupTasks',
    Checklists = 'checklists',
    Notifications = 'notifications',
    HealthMetrics = 'healthMetrics',
    ProfilesAndDevices = 'profilesAndDevices', // ios only
    SigningKeys = 'signingKeys', // android only
    Team = 'team',
    DefaultReleaseNotes = 'defaultReleaseNotes',
    ScheduleCadence = 'scheduleCadence',
    ReleasePilotRotation = 'releasePilotRotation',
    WorkflowSteps = 'workflowSteps',
}

export interface WebhookEndpoint {
    id: string;
    url: string;
    description?: string;
    enabled: boolean;
    eventTypes: string[]; // empty means all event types
    appIds: string[]; // empty means all apps for the org
    customHeaders: Record<string, string>;
    redactedHeadersKeys: string[];
    createdAt: string;
}

export interface WebhookEventType {
    type: string; // e.g release.kickoff
    description: string;
}

export interface IntegrationsRecommendationBanner {
    title: string;
    description: string;
    actionText: string;
    integrationGroups: IntegrationType[][];
}

export interface OnboardingStep {
    title: string;
    description: string;
    icon: string;
    canSkip: boolean;
    integrationsRecommendationBanner: IntegrationsRecommendationBanner;
    stepSetupView: React.ReactElement;
    stepInfoView: React.ReactElement;
}

export enum MetricConditionOperator {
    LessThan = 'LessThan',
    GreaterThan = 'GreaterThan',
    // These are supported by the backend but not exposed in the UI
    LessThanOrEqualTo = 'LessThanOrEqualTo',
    GreaterThanOrEqualTo = 'GreaterThanOrEqualTo',
}

export const AllMetricConditionOperators = allCases(MetricConditionOperator).filter(
    (predicate) =>
        predicate !== MetricConditionOperator.LessThanOrEqualTo &&
        predicate !== MetricConditionOperator.GreaterThanOrEqualTo,
);

// Must match `MonitoringMetricType` in rollout_monitoring.go
export enum ObservabilityAndAnalyticsMetricType {
    EventsPerActiveUser = 'eventsPerActiveUser',
    EventsPerActiveUserDeltaFromPreviousPercentage = 'eventsPerActiveUserDeltaFromPreviousPercentage',
    EventsPerActiveUserDeltaFromPreviousValue = 'eventsPerActiveUserDeltaFromPreviousValue',

    EventsPerSession = 'eventsPerSession',
    EventsPerSessionDeltaFromPreviousPercentage = 'eventsPerSessionDeltaFromPreviousPercentage',
    EventsPerSessionDeltaFromPreviousValue = 'eventsPerSessionDeltaFromPreviousValue',

    AveragePropertyValues = 'avgPropertyValue',
    AveragePropertyValuesDeltaFromPreviousPercentage = 'avgPropertyValueDeltaFromPreviousPercentage',
    AveragePropertyValuesDeltaFromPreviousValue = 'avgPropertyValueDeltaFromPreviousValue',

    MedianPropertyValues = 'medianPropertyValue',
    MedianPropertyValuesDeltaFromPreviousPercentage = 'medianPropertyValueDeltaFromPreviousPercentage',
    MedianPropertyValuesDeltaFromPreviousValue = 'medianPropertyValueDeltaFromPreviousValue',

    P90PropertyValues = 'p90PropertyValue',
    P90PropertyValuesDeltaFromPreviousPercentage = 'p90PropertyValueDeltaFromPreviousPercentage',
    P90PropertyValuesDeltaFromPreviousValue = 'p90PropertyValueDeltaFromPreviousValue',

    P95PropertyValues = 'p95PropertyValue',
    P95PropertyValuesDeltaFromPreviousPercentage = 'p95PropertyValueDeltaFromPreviousPercentage',
    P95PropertyValuesDeltaFromPreviousValue = 'p95PropertyValueDeltaFromPreviousValue',

    P99PropertyValues = 'p99PropertyValue',
    P99PropertyValuesDeltaFromPreviousPercentage = 'p99PropertyValueDeltaFromPreviousPercentage',
    P99PropertyValuesDeltaFromPreviousValue = 'p99PropertyValueDeltaFromPreviousValue',

    GenericObaaValue = 'genericObaaValue',
    GenericObaaValueDeltaFromPreviousValue = 'genericObaaValueDeltaFromPreviousValue',
    GenericObaaValueDeltaFromPreviousPercentage = 'genericObaaValueDeltaFromPreviousPercentage',

    Composed = 'composed',
    ComposedDeltaFromPreviousValue = 'composedDeltaFromPreviousValue',
    ComposedDeltaFromPreviousPercentage = 'composedDeltaFromPreviousPercentage',
}

export const AllObservabilityAndAnalyticsMetricTypes = allCases(ObservabilityAndAnalyticsMetricType);

export enum StabilityMetricType {
    SessionStability = 'sessionStability',
    SessionStabilityDeltaFromPreviousPercentage = 'sessionStabilityDeltaFromPreviousPercentage',
    SessionStabilityDeltaFromPreviousValue = 'sessionStabilityDeltaFromPreviousValue',

    UserStability = 'userStability',
    UserStabilityDeltaFromPreviousPercentage = 'userStabilityDeltaFromPreviousPercentage',
    UserStabilityDeltaFromPreviousValue = 'userStabilityDeltaFromPreviousValue',

    // Special metric type used when crash free users/sessions is merged into the same card
    MergedSessionStability = 'mergedSessionStability',
    MergedUserStability = 'mergedUserStability',

    AdoptionRate = 'adoptionRate',
}

export const AllStabilityMetricTypes = allCases(StabilityMetricType);

export enum AppMonitoringHealthMetricCategory {
    Stability = 'stability',
    ObservabilityAndAnalytics = 'productAnalytics',
    UserReviews = 'userReviews',
}

export const AllMonitoringHealthMetricCategories = allCases(AppMonitoringHealthMetricCategory);

export enum UserRatingsMetricType {
    AvgUserRating = 'avgUserRating',
    AvgUserRatingDeltaFromPreviousPercentage = 'avgUserRatingDeltaFromPreviousPercentage',
    AvgUserRatingDeltaFromPreviousValue = 'avgUserRatingDeltaFromPreviousValue',
}

export const AllUserRatingsMetricType = allCases(UserRatingsMetricType);

export type AppMonitoringHealthMetricType =
    | ObservabilityAndAnalyticsMetricType
    | StabilityMetricType
    | UserRatingsMetricType;

export interface AppMonitoringHealthMetric {
    id: string;
    category: AppMonitoringHealthMetricCategory;
    integrationId: IntegrationType; // TODO - need to handle PseudoIntegrationForComposedMetric / "pseudo-obaa" ?
    integrationInstallationIds: IntegrationInstallationId[];
    thresholds: AppMonitoringHealthMetricThreshold[];
    type: AppMonitoringHealthMetricType;
    hideOnRolloutView: boolean;
    eventName?: string; // For OBAA types
    notifyOnApproachingThreshold: boolean;
}

export interface AppMonitoringObservabilityAndAnalyticsHealthMetric extends AppMonitoringHealthMetric {
    category: AppMonitoringHealthMetricCategory.ObservabilityAndAnalytics;
    type: ObservabilityAndAnalyticsMetricType;
    eventName: string;
    hideOnRolloutView: boolean;
    paramName?: string;

    // For composed metrics (Type == MonitoringMetricTypeComposed)
    composedMetricIds?: string[];
    composeMetricsUsingEquation?: string;
}

export interface AppMonitoringStabilityHealthMetric extends AppMonitoringHealthMetric {
    category: AppMonitoringHealthMetricCategory.Stability;
    type: StabilityMetricType;
}

export interface AppMonitoringSettings {
    metrics: AppMonitoringHealthMetric[];
    checklistItems: ChecklistItem[];
}

export enum AnalyticEventUnit {
    PerUser = 'PerUser',
    PerSession = 'PerSession',
}

export interface StatisticTimeSeriesDataPoint {
    appId?: string;
    destinationId?: string;
    destinationName?: string;
    platform?: string;
    releaseId?: string;
    timestamp: string;
    value: number;
    valuesByUser?: Record<string, number>;
}

export interface MonitoringStatistic {
    integrationId: IntegrationType;
    type: AppMonitoringHealthMetricType; // unit is inferred from type in any other non-analytic metric
    currentValue?: number; // This is just the most recent value
    recentDataPoints?: StatisticTimeSeriesDataPoint[]; // hourly data points for the last 24 hours
    pastDataPoints?: StatisticTimeSeriesDataPoint[]; // daily data points for the last X days
    additionalDataSeries?: Record<string, StatisticTimeSeriesDataPoint[]>; // used for multi-series charts
    monitoringMetricId?: string;
    monitoringMetricStatus?: MonitoringMetricHealthStatus;
    buildIdentifier?: string;

    //------  only used for analytic events
    eventName?: string;
    paramName?: string;

    //------ only used for 'Delta from previous version' cards
    delta?: number;
    previousVersion?: string;
    previousValue?: number;
}

export type PartialMonitoringStatistic = Omit<MonitoringStatistic, 'recentDataPoints' | 'pastDataPoints'>;

export interface RolloutMonitoring {
    releaseAdoptions: ReleaseAdoption[];
    monitoringStatistics: (MonitoringStatistic | PartialMonitoringStatistic)[];
    featureFlagTimeSeries: FeatureFlagTimeSeriesDatum[];
    monitoringAutomations: MonitoringAutomation[];
    checklistItems: ChecklistItem[];
    automationRuns: AutomationRun[];
    appStoreSelectedBuilds: Nullable<AppStoreAvailableBuild[]>;
    appStoreReviewData: Nullable<AppStoreReviewData>;
    rolloutStartedAt: Nullable<string>;
}

export interface ReleaseStepRolloutData extends RolloutMonitoring {
    appStoreType: AppStoreType;
    platform: AppPlatform;
    phasedRelease?: PhasedRelease;
    appStoreIntegrationType: IntegrationType;
    stabilityMonitoringIntegrationType?: IntegrationType;
    releasedAt?: string;
    arePhasedReleaseAutomationsPaused: boolean;
    internalVersionString: string;
}

export enum FeatureFlagChangeType {
    Create = 'create',
    Update = 'update',
    Delete = 'delete',
}

export interface FeatureFlagChange {
    type: FeatureFlagChangeType;
    path: string[];
    from: any;
    to: any;
}

export interface FeatureFlagTimeSeriesDatum {
    timestamp: string;
    flagBefore?: FeatureFlag;
    flagAfter?: FeatureFlag;
    diff: FeatureFlagChange[];
}

export enum MonitoringMetricHealthStatus {
    Healthy = 'healthy',
    Unhealthy = 'unhealthy',
}

export enum MonitoringAutomationConditionType {
    Adoption = 'adoption',
    PhasedReleaseDay = 'phasedReleaseDay',
    StagedRolloutPercentage = 'stagedRolloutPercentage',
}

export interface MonitoringAutomationMetric {
    id: string; // the identifier of the associated monitoring settings metric
    status?: MonitoringMetricHealthStatus; // the metric's computed health for the release based on the metric's defined threshold
}

export interface MonitoringAutomationCondition {
    type: MonitoringAutomationConditionType;
    isConditionMet: boolean; // whether or not the condition has been reached/met
}

export interface MonitoringAutomationStep {
    status: AutomationStatus;
    description: string;
}

export interface MonitoringAutomation {
    id: string;
    triggeredAt?: string;
    associatedMetrics: MonitoringAutomationMetric[];
    associatedConditions: MonitoringAutomationCondition[];
    steps: MonitoringAutomationStep[];
    discardedAt?: string;
    discardedBy?: string;
    discardedAutomaticallyByRunway?: boolean;
    checklistItems?: string[];
}

// Org Overview v2

export enum StatisticCategory {
    DevOps = 'devOps',
    Work = 'work',
    Build = 'build',
    StabilityAndHealth = 'stabilityAndHealth',
}

export const OrderedStatisticCategory = {
    [StatisticCategory.Work]: 4,
    [StatisticCategory.Build]: 2,
    [StatisticCategory.StabilityAndHealth]: 3,
    [StatisticCategory.DevOps]: 1,
};

export enum AndroidOutputType {
    APK = 'apk',
    AAB = 'aab',
}

export enum DependencyManagerType {
    CocoaPods = 'cocoapods',
    SwiftPackageManager = 'spm',
    Carthage = 'carthage',
}

// Quickstart CI/CD
export enum QuickStartCIScanAttribute {
    // common
    RubyVersion = 'rubyVersion',
    NodeVersion = 'nodeVersion',
    WorkspacePath = 'workspacePath',

    // ios specific
    DependencyManager = 'dependencyManager',
    TeamId = 'teamId',
    AppBundleIdentifier = 'appBundleIdentifier',
    BundleIdentifiers = 'bundleIdentifiers',
    Schemes = 'schemes',
    Targets = 'targets',
    BuildConfigurations = 'buildConfigurations',

    // android specific
    PackageName = 'packageName',
    ProjectDirectory = 'projectDirectory',
    BuildType = 'buildType',
    BuildFlavor = 'buildFlavor',

    OutputType = 'outputType',
}

export interface ScanResult {
    [QuickStartCIScanAttribute.RubyVersion]?: string;
    [QuickStartCIScanAttribute.NodeVersion]?: string;
    [QuickStartCIScanAttribute.WorkspacePath]?: string;
    [QuickStartCIScanAttribute.DependencyManager]?: string;
    [QuickStartCIScanAttribute.TeamId]?: string;
    [QuickStartCIScanAttribute.AppBundleIdentifier]?: string;
    [QuickStartCIScanAttribute.BundleIdentifiers]?: string[];
    [QuickStartCIScanAttribute.Schemes]?: string[];
    [QuickStartCIScanAttribute.Targets]?: string[];
    [QuickStartCIScanAttribute.BuildConfigurations]?: string[];
    [QuickStartCIScanAttribute.PackageName]?: string;
    [QuickStartCIScanAttribute.ProjectDirectory]?: string;
    [QuickStartCIScanAttribute.BuildType]?: string;
    [QuickStartCIScanAttribute.BuildFlavor]?: string;
    [QuickStartCIScanAttribute.OutputType]?: string;
    isReactNativeApp?: boolean;
}

export interface ScanConfirmedResult {
    ciProvider: string;
    rubyVersion?: string;
    nodeVersion?: string;
    appBundleIdentifier?: string;
    workspacePath?: string;
    projectDirectory?: string;
    teamId?: string;
    targets?: string[];
    scheme?: string;
    buildConfiguration?: string;
    provisioningProfileId?: string;
    packageName?: string;
    gradleTask?: string;
    buildType?: string;
    flavor?: string;
    profileId?: string;
}

export enum QuickStartCIFileType {
    Appfile = 'appfile',
    Fastfile = 'fastfile',
    Gemfile = 'gemfile',
    Pluginfile = 'pluginfile',
    GHWorkflowFile = 'ghWorkflowFile',
}

export interface QuickStartCIFile {
    fileType: QuickStartCIFileType;
    fileId: string;
    fileUrl: string;
}

export interface QuickStartCIFilesResult {
    resultFiles: QuickStartCIFile[];
}

export interface OpenPRQuickstartResult {
    prUrl: string;
}

export enum AppleCertificateType {
    IosDevelopment = 'IOS_DEVELOPMENT',
    IosDistribution = 'IOS_DISTRIBUTION',
    MacAppDistribution = 'MAC_APP_DISTRIBUTION',
    MacInstallerDistribution = 'MAC_INSTALLER_DISTRIBUTION',
    MacAppDevelopment = 'MAC_APP_DEVELOPMENT',
    DevKext = 'DEVELOPER_ID_KEXT',
    DevApplication = 'DEVELOPER_ID_APPLICATION',
    Development = 'DEVELOPMENT',
    Distribution = 'DISTRIBUTION',
    PassId = 'PASS_TYPE_ID',
    PassIdNfc = 'PASS_TYPE_ID_WITH_NFC',
}

export interface AppleCertificate {
    name: string;
    expirationDate: string;
    certificateType: AppleCertificateType;
}

export enum ProvisioningProfileType {
    IosDevelopment = 'IOS_APP_DEVELOPMENT',
    IosAppStore = 'IOS_APP_STORE',
    IosAdHoc = 'IOS_APP_ADHOC',
    IosInHouse = 'IOS_APP_INHOUSE',
    MacDevelopment = 'MAC_APP_DEVELOPMENT',
    MacAppStore = 'MAC_APP_STORE',
    MacDirect = 'MAC_APP_DIRECT',
    TvosDevelopment = 'TVOS_APP_DEVELOPMENT',
    TvosAppStore = 'TVOS_APP_STORE',
    TvosAdHoc = 'TVOS_APP_ADHOC',
    TvosInHouse = 'TVOS_APP_INHOUSE',
    CatalystDevelopment = 'MAC_CATALYST_APP_DEVELOPMENT',
    CatalyistAppStore = 'MAC_CATALYST_APP_STORE',
    CatalystDirect = 'MAC_CATALYST_APP_DIRECT',
}

export enum ProvisioningProfileState {
    active = 'ACTIVE',
    invalid = 'INVALID',
}

export interface ProvisioningProfile {
    id: string;
    name: string;
    profileType: ProvisioningProfileType;
    expirationDate: string;
    creationDate: string;
    profileState: ProvisioningProfileState;
    certificates: AppleCertificate[];
    devices: AppDevice[];
    bundleId: string;
    createdByRunwayAt?: string;
}

export interface GetProvisioningProfilesResult {
    profiles: ProvisioningProfile[];
}

export enum BinaryBuildType {
    Ipa = 'ipa',
    Apk = 'apk',
    Aab = 'aab',
}

export interface BinaryBuild {
    id: string;
    type: BinaryBuildType;
    buildNumber: string;
    buildVersion: string;
    uploadedAt?: string;
    provisioningProfile?: ProvisioningProfile;
    buildType?: BuildArtifactType;
    artifactFileName: string;
}

export interface BuildDetails {
    binaryBuild?: BinaryBuild;
    prData?: PullRequest;
    ciBuild?: BuildInfo;
    installationUrl: string;
    downloadUrl?: string;
    artifactInfo?: BuildDistroArtifactInfo;
    isMultiArtifact: boolean;
}

export type UsersAndGroups = {
    users: string[];
    groups: UserGroupId[];
};

export enum BucketStatus {
    Active = 'active',
    Archived = 'archived',
}

export enum BuildDistroMemberType {
    User = 'user',
    Group = 'role',
}

export enum BuildDistroMemberPermissionLevel {
    Admin = 'admin',
    Uploader = 'uploader',
    Tester = 'tester',
}

export interface BucketMember {
    type: BuildDistroMemberType;
    id: string | UserGroupId;
    permissionLevel: BuildDistroMemberPermissionLevel;
}

export interface StepNotificationConfigOverride {
    stepConfigId: string;
    enabled: boolean;
    settings?: Partial<CommonNotification>;
    channels?: CommsChannel[];
}

export interface CommonNotification {
    id: AppNotificationType | BuildDistroNotificationType;
    displayText: string;
    info: string;
    enabled: boolean;
    channels: CommsChannel[];
    mentionGroups?: UserGroupId[];
    mentionUsers?: string[];
    hourOfDayForNotification?: number;
    appleProfileAndCertificateReminderDaysBeforeExpiration?: number;
    selectedProfileAndCertificateTypes?: ProfileOrCertificateType[];
    includeNewIssuesIdentifiedByRunway?: boolean;
    threadWithRelatedNotifications?: boolean;
    phasedReleaseDayThreshold?: number;
    adoptionThreshold?: number;
    emailsToInclude?: string[];
    allowEmails?: boolean;
    notificationChannels?: NotificationDestination[];
    customCopy?: string;
    includeTicketListInSubmitNotifications?: boolean;
    bundleSizeIncreaseConditions?: BundleSizeIncreaseCondition[];
    bundleSizeCellularDownloadLimitThresholdMB?: number;
    bundleSizeAlertNotifyFor?: BundleSizeAlertNotifyFor;
    includeDirectDownloadLink?: boolean;
    directDownloadLinkAccessLimit?: number;
    directDownloadLinkExpiryHours?: number;
    includePROwnerInCherryPickNotifications?: boolean;
    includePRCommitInfo?: boolean;
    applicableStepArchetypes?: ReleaseStepType[];
    stepConfigOverrides?: StepNotificationConfigOverride[];
}

export enum BundleSizeAlertNotifyFor {
    FirstBuildOnly = 'firstBuildOnly',
    AllBuilds = 'allBuilds',
}

export enum BundleSizeConditionType {
    Download = 'download',
    Install = 'install',
}

export enum BundleSizeConditionUnit {
    MB = 'mb',
    Percent = 'percent',
}

export interface BundleSizeIncreaseCondition {
    type: BundleSizeConditionType;
    unit: BundleSizeConditionUnit;
    value: number;
}

export enum ProfileOrCertificateType {
    DevelopmentProfile = 'Development (Profile)',
    DistributionProfile = 'Distribution (Profile)',
    AdHocProfile = 'Ad Hoc (Profile)',
    DevelopmentCertificate = 'Development (Certificate)',
    DistributionCertificate = 'Distribution (Certificate)',
}

export interface BuildDistroBucketBaseProperties {
    id: string;
    name: string;
    type: BucketType;
}

export interface BuildDistroBucketBase extends BuildDistroBucketBaseProperties {
    explicitSortOrder?: number;
    rules: BucketRule[];
    members: BucketMember[];
    orgWideAccessEnabled: boolean;
    publicBucketLinkEnabled: boolean;
    publicHash?: string;
    status: BucketStatus;
    notificationsEnabled: boolean;
    notifications: CommonNotification[];
    createdAt: string;
    createdBy: string;
    automations: BucketAutomation[];
    archivedAt?: string;
    totalBuildsCount: number;
    artifactTypes?: BuildArtifactType[];
    disallowedActions?: RunwayUserAction[];
    integrationInstallationIds: string[];
}

export enum BucketType {
    Custom = 'custom',
    Personal = 'personal',
    RC = 'rc',
    Development = 'dev',
}

export interface BuildDistroWorkItemsByBuild {
    ciBuildIdentifier: string;
    ciIntegrationId: IntegrationType;
    binaryBuildIdentifier?: string;
    binaryIntegrationId?: IntegrationType;
    uploadedAt?: string;
    items: FeatureItem[];
}

export interface FetchBuildDistributionBucketBuildsWorkItemsTimelineResponse {
    total: number;
    items: BuildDistroWorkItemsByBuild[];
}

export interface ExternalBuildDistroBucket {
    id: string;
    name: string;
    type: BucketType;
    totalBuildsCount: number;
    artifactTypes: BuildArtifactType[];
    appName: string;
    platform: AppPlatform;
    appImageUrl: string;
}

export interface ExternalBuildDistroBuild {
    id: string;
    bucketId: string;
    bucketName: string;
    status: BuildDistroBuildStatus;
    artifactDownloadStatus?: BuildDistroArtifactDownloadStatus;
    binaryBuild?: ExternalBuildDistroBinaryBuild;
    testerNotes: string;
    installationUrl: string;
    downloadUrl?: string;

    // These should only be populated if BE config BuildDistroCanIncludeAdditionalArtifactsOnExternalBuckets is enabled
    additionalArtifacts: Nullable<BaseBuildArtifact[]>;
    ciBuild?: BuildInfo;
    uploadedAt: string;
}

export interface ExternalBuildDistroBinaryBuild {
    id: string;
    type: BinaryBuildType;
    buildNumber: string;
    buildVersion: string;
    buildType: BuildArtifactType;
    artifactFileName: string;
    hasInstallableProvisioningProfile: boolean;
    provisioningProfileHasDevices: boolean;
}

export interface ExternalBuildDistroBucketRequestBase {
    bucketHash: string;
}

export interface ExternalBuildDistroBuildRequestBase {
    buildHash: string;
}

export type FetchExternalBuildDistroBucketRequest = ExternalBuildDistroBucketRequestBase;
export type FetchExternalBuildDistroBucketBuildsRequest = ExternalBuildDistroBucketRequestBase;
export type FetchExternalBuildDistroBuildRequest = ExternalBuildDistroBuildRequestBase;

export type FetchExternalBuildDistroBucketResponse = ExternalBuildDistroBucket;
export type FetchExternalBuildDistroBuildResponse = ExternalBuildDistroBuild;
export type FetchExternalBuildDistroBucketBuildsResponse = ExternalBuildDistroBuild[];

export interface GetAllDevicesResponse {
    total: number;
    devices: AppDevice[];
    devicesRegisteredInASCCount: number;
}

export type FetchBuildDistributionBucketResponse = BuildDistroBucketBase;
export type FetchBuildDistributionBucketBuildsResponse = BuildDistroBuild[];
export type CreateBuildDistributionBucketResponse = BuildDistroBucketBase;
export type ArchiveBuildDistributionBucketResponse = BuildDistroBucketBase;
export type ToggleBuildDistroBucketNotificationsResponse = BuildDistroBucketBase;
export type UpdateBuildDistroBucketNotificationSettingsResponse = BuildDistroBucketBase;
export type UpdateBuildDistroBucketAutomationsSettingsResponse = BuildDistroBucketBase;
export type UpdateBuildDistributionBucketResponse = BuildDistroBucketBase;
export type UpdateBuildDistributionBuildTesterNotesResponse = BuildDistroBuild;
export type ShareBuildDistributionBuildWithTestersResponse = BuildDistroBuild;
export type FetchBuildDistributionBucketBuildResponse = BuildDistroBuild;
export type ArchiveBuildDistributionBuildResponse = BuildDistroBuild;
export type FetchBuildDistributionBucketDownloadMetricsResponse = BuildDistroBucketDownloadMetrics;

// export type RemoveBuildDistributionBuildTestersResponse = BuildDistroBuild;

export interface FetchBuildDistributionSharedWithMeBuildsResponse {
    builds: BuildDistroBuild[];
    totalCount: number;
}

export interface BuildDistributionBucket extends BuildDistroBucketBase {
    recentBuildIds?: string[];
    buildIds?: string[];
}

export interface BuildDistroBuildSharedWithMe extends BuildDistroBuild {
    sharedBy: string;
    sharedOn: string;
}

export enum BuildDistroArtifactDownloadStatus {
    Success = 'success',
    InProgress = 'inProgress',
    Failed = 'failed',
}

export interface BuildDistroArtifactInfo {
    downloadStatus: BuildDistroArtifactDownloadStatus;
    errorMessage?: string;
    appStoreInfos?: Record<IntegrationInstallationId, AppStoreArtifactUploadInfo>;
    artifactId: string;
    workflowRunId: string;
}

export interface BuildDistroBucketDownloadMetrics {
    numDownloadsPerBuild: Record<string, number>;
}

export interface BuildDistroBuild extends BuildDetails {
    id: string;
    bucketId: string;
    createdAt: string;
    uploadedAt: string;
    updatedAt: string;
    status: BuildDistroBuildStatus;
    individualTesters?: string[];
    testerNotes: string;
    workItemsByBuild?: BuildDistroWorkItemsByBuild[];
    additionalArtifacts: Nullable<BaseBuildArtifact[]>;
}

export enum BuildDistroBuildStatus {
    Active = 'active',
    Expired = 'expired',
    Deleted = 'deleted',
}

export interface BuildDistroBucketWithRecentBuilds extends BuildDistroBucketBase {
    recentBuilds: BuildDistroBuild[];
}

export interface FetchBuildDistributionBucketsWithRecentBuildsResponse {
    buckets: BuildDistroBucketWithRecentBuilds[];
    sharedWithMe: BuildDistroBuildSharedWithMe[];
}

export enum BuildDistroBucketRuleType {
    Branch = 'Branch',
    PR = 'PR',
}

export interface BuildDistroBucketBaseRule {
    workflowData?: WorkflowData;
    fileFilterPatterns: string[];
    isTokenizedBranch?: boolean;
    filterByVCSUsers: VCSUserData[];
    pullInAdditionalArtifacts: boolean;
    filterByVCSUsersBehavior: BuildDistroFilterByVCSUsersBehaviorType;
}

export interface BuildDistroBucketPRRule extends BuildDistroBucketBaseRule {
    type: BuildDistroBucketRuleType.PR;
    baseBranch: string;
}

export interface BuildDistroBucketBranchRule extends BuildDistroBucketBaseRule {
    type: BuildDistroBucketRuleType.Branch;
    branch: string;
}

export type BucketRule = BuildDistroBucketPRRule | BuildDistroBucketBranchRule;

export interface SigningKey {
    id: string;
    name: string;
    filename: string;
    updatedAt: string;
    uploadedBy: string;
}

export interface ReleasePilotRotation {
    items: ReleasePilotRotationItem[];
    syncInfo?: ReleasePilotRotationSyncInfo;
}

export interface ReleasePilotRotationSyncInfo {
    integrationId: IntegrationType;
    scheduleName: string;
}

export interface ReleasePilotRotationItem {
    id: string; // the user id or email of the predicted pilot if they're a Runway user
    pilotFromScheduleIntegrationEmail?: string; // the email of the pilot when it's been synced from a schedule integration but they're not a Runway user
    pilotRotationIndex?: number;
    releaseVersion: string;
    isReleaseVersionPredicted: boolean;
    releaseVersionKickoffDate?: string;
    releaseVersionTargetKickoffDate?: string;
    isReleaseVersionTargetKickoffDatePredicted: boolean;
    isReleaseVersionTargetKickoffDatePredictedFromScheduleIntegration: boolean;
    overridePilotId?: string;
    releaseType: ReleaseType;
    scheduleOnCallShiftStart?: string; // timestamp of the on call shift start
    scheduleOnCallShiftEnd?: string; // timestamp of the on call shift end
}

export interface EditViewPilot {
    id: string;
    overridePilotId: string;
    pilotRotationIndex: number;
}

export enum DeviceClass {
    iPhone = 'IPHONE',
    iPad = 'IPAD',
    iPod = 'IPOD',
    AppleTV = 'APPLE_TV',
    Mac = 'MAC',
    AppleWatch = 'APPLE_WATCH',
    AppleVisionPro = 'APPLE_VISION_PRO',
}

export interface Device {
    id: string;
    udid: string;
    deviceClass?: DeviceClass;
    displayName: string;
    isRegisteredInRunway: boolean;
    registeredInASCByAutomationAt?: string;
    updatedStatusInASCByAutomationAt?: string;
}

export enum ASCRegistrationStatus {
    Enabled = 'ENABLED',
    Disabled = 'DISABLED',
    Ineligible = 'INELIGIBLE',
}

export interface AppDevice extends Device {
    associatedUserEmail?: string;
    isTestDevice: boolean;
    ascRegistrationStatus?: ASCRegistrationStatus;
    appStoreId?: string;
}

export enum FixRequestStatusType {
    FixRequestApproved = 'Approved',
    FixRequestRejected = 'Rejected',
    FixRequestPending = 'Pending',
}

export interface FixRequestStatusInfo {
    status: FixRequestStatusType;
    updatedAt: string;
    authorId: string;
}

export interface FixRequest {
    id: string;
    createdBy: string;
    createdAt: string;
    details?: string;
    status?: FixRequestStatusInfo;
}

export interface GeneratedReleaseNotesResponse {
    content: string;
}

export enum GeneratedReleaseNotesType {
    ReleaseNotesTypeAIGeneratedExternal = 'ai-generated-external',
    ReleaseNotesTypeAIGeneratedInternal = 'ai-generated-internal',
    ReleaseNotesTypeCodeFocus = 'code-focus',
    ReleaseNotesTypeTicketFocus = 'ticket-focus',
    ReleaseNotesTypeTemplated = 'templated',
}

export enum GeneratedReleaseNotesMarkdownContext {
    MarkdownSupported = 'markdown-supported',
    MarkdownUnsupported = 'markdown-unsupported',
}

export enum GeneratedReleaseNotesStepContext {
    Kickoff = 'kickoff',
    BetaTesting = 'betaTesting',
    Metadata = 'metadata',
    Release = 'release',
    NoContext = 'noContext',
}

export interface RunwayModelsResponse {
    releases?: Release[];
    releaseSteps?: ReleaseStep[];
    stepRuns?: StepRun[];
    timelineEvents?: TimelineEvent[];
    stepConfigs?: StepConfig[];
    orgEntity?: Organization;
    apps?: AppEntity[];
    checklistItemTemplates?: ChecklistItemTemplate[];
    automations?: Automation[];
    workflowRunStepRunGraphs?: WorkflowRunStepRunGraph[];
    workflowStepGraphs?: WorkflowStepGraph[];
    workflowOverview?: WorkflowOverview;
    orgChecklistItemTemplates?: ChecklistItemTemplate[];

    // deleted entities
    deletedChecklistItemTemplateIds?: string[];
}

export interface AppIdentifiersAndPlatform {
    appId: string;
    appName: string;
    platform: AppPlatform;
}

export enum EmailNotificationCategory {
    OrgNotifications = 'EmailOrgNotification',
    AppNotifications = 'EmailAppNotification',
    CommsNotifications = 'EmailCommsNotification',
}

export interface AppOrCommsNotificationSettings {
    type: EmailNotificationCategory;
    apps?: Record<string, NotificationSettingAppDetail>;
}

export interface OrgNotificationSettings {
    type: EmailNotificationCategory;
    enabled: boolean;
}

export interface BuildDistroBucketNotificationDetails extends BuildDistroBucketBaseProperties {
    enabled: boolean;
}

export interface NotificationSettingAppDetail {
    enabled: boolean;
    additionalData?: any; // If present, it's a Record<string, BuildDistroBucketNotificationDetails> where the key is the bucket id
}

export interface NotificationBasicInfo {
    id: AppNotificationType | BuildDistroNotificationType;
    displayText: string;
    info: string;
}

export interface CommsNotificationDetails {
    type: EmailNotificationCategory;
    notification: NotificationBasicInfo;
    appIds: string[];
    additionalDataForApp?: Record<string, any>; // Value if present is a Record<string, BuildDistroBucketNotificationDetails[]> for now where the key is the app id
}

export interface UserEmailNotificationSettings {
    userOrgNotificationPreferences: Record<EmailNotificationType, OrgNotificationSettings>;
    userAppNotificationPreferences: Record<EmailNotificationType, AppOrCommsNotificationSettings>;
    userCommsNotificationPreferences: Record<
        AppNotificationType | BuildDistroNotificationType,
        AppOrCommsNotificationSettings
    >;
    notificationsDetails: Record<AllNotificationTypes, CommsNotificationDetails>;
}

export enum EmailNotificationType {
    OrgMetricsDigest = 'EmailNotificationOrgMetricsDigest',
    OrgReleasesDigest = 'EmailNotificationOrgReleasesDigest',
    OrgOverviewSummary = 'EmailNotificationOrgOverviewSummary',
    AppReleaseDigest = 'EmailNotificationAppReleasesDigest',
    ReleaseSummary = 'EmailNotificationReleaseSummary',
}

export interface ReleaseCalendarData {
    status: ReleaseStatus;
    displayVersion: string;
    stepLifecycleEvents: ReleaseCalendarLifecycleEvent[];
    color: string;
    isReleaseCompleted: boolean;
    canReleaseBeSubmitted: boolean;
    scheduledAutomationsPaused: boolean;
    isHotfix: boolean;
    releasePilotId?: string;
    releaseDescription?: string;
}

export interface ExternalCalendarEvent {
    id: string;
    startDate: string;
    endDate: string;
    externalCalendarEventId: string;
    title: string;
    description?: string;
    externalEventUrl: string;
    organizer?: string;
}

export interface AllReleaseCalendarData {
    releaseCalendar: Record<string, ReleaseCalendarData>;
    externalEvents: Record<string, ExternalCalendarEvent>;
    lifecycleFreezes: LifecycleFreezeSet[];
}

export enum ReleaseCalendarDateType {
    ReleaseCalendarDateTypeTarget = 'target',
    ReleaseCalendarDateTypeEstimated = 'estimated',
    ReleaseCalendarDateTypeActual = 'actual',
}

export interface ReleaseCalendarLifecycleEvent {
    id: string;
    startDate: string;
    endDate: string;
    stepId: ReleaseStepType;
    lifecycleEventType: LifecycleEventType;
    targetDateSetByCadence: boolean;
    incrementDayNumber?: number;
    stagedRolloutPercentage?: number;
    dateType: ReleaseCalendarEventType;
}

export enum LifecycleEventType {
    LifecycleEventTypeKickoff = 'kickoff',
    LifecycleEventTypeSubmit = 'submit',
    LifecycleEventTypeRelease = 'release',
    LifecycleEventTypeRolloutStart = 'rolloutStarted',
    LifecycleEventTypeRolloutCompletion = 'rollout',
    LifecycleEventTypeRolloutIncrement = 'rolloutIncrement',
    LifecycleEventTypeReleaseCycle = 'releaseCycle',
    LifecycleEventTypeExternalEvent = 'externalEvent',
    LifecycleEventTypeBlackoutPeriod = 'blackoutPeriod',
    LifecycleEventTypeArbitrary = 'arbitrary',
    LifecycleEventTypeLifecycleFreeze = 'lifecycleFreeze',
}

export enum ReleaseCalendarEventType {
    ReleaseCalendarEventTypeTargetDate = 'target',
    ReleaseCalendarEventTypeEstimatedDate = 'estimated',
    ReleaseCalendarEventTypeActualDate = 'actual',
    ReleaseCalendarEventTypeParent = 'parent',
    ReleaseCalendarExternalEvent = 'externalEvent',
    ReleaseCalendarLifecycleFreezeEvent = 'lifecycleFreeze',
}

export enum ReleaseCalendarViewType {
    ReleaseCalendarViewTypeMonth = 'dayGridMonth',
    ReleaseCalendarViewTypeWeek = 'timeGridWeek',
    ReleaseCalendarViewTypeDay = 'timeGridDay',
    ReleaseCalendarViewTypeList = 'listWeek',
}

export enum RelativeTiming {
    before = 'before',
    after = 'after',
    onTime = 'onTime',
}

export enum TokenizedTextModalType {
    CustomMessagesSettings = 'CustomMessagesSettings',
    ReleaseSummaryMessage = 'ReleaseSummaryMessage',
}

export type AppIdentifierForIntegration = {
    capability: IntegrationCapability;
    installationId: IntegrationInstallationId;
    appIdentifier: string;
};

export enum HotfixCheckboxTypes {
    ShouldCreateBranch = 'shouldCreateBranch',
    ShouldBumpVersionOnBranch = 'shouldBumpVersionOnBranch',
}

export enum AutomationTimelineEventRowKey {
    Conditions = 'Execution conditions',
    Description = 'Description',
    RelatedAutomations = 'Related automations',
    Status = 'Status',
}

export interface CustomCIProviderData {
    displayableName: string;
}

export interface CustomCIIntegrationData extends BaseCIIntegrationData {
    dataLastReceivedAt: Date;
    integrationId: string;
}

export interface IntegrationProviderCustomCI extends IntegrationProviderEntity {
    type: IntegrationProviderType.CustomCI;
    data: CustomCIProviderData;
}

export interface IntegrationCustomCI extends IntegrationEntity {
    id: IntegrationType.CustomCI;
    data: CustomCIIntegrationData;
}

export interface Percentage {
    fraction: number; // Ranges from 0.0 to 1.0
}

export enum MergeType {
    DoNotMerge = 'doNotMerge',
    MergeCommit = 'mergeCommit',
    Squash = 'squash',
}
